/**
 * Murphy Door Configurator - UNIFIED LINEAR DATA PIPELINE PROCESSOR
 *
 * ARCHITECTURAL ROLE: Complete data processing pipeline
 * that transforms raw DOM data into the perfectProductDictionary through 5 sequential layers.
 *
 * 🔄 LINEAR PROCESSING PIPELINE:
 * Raw Data → Layer 1: Variant Enhancement → Layer 2: Product Virtualization →
 * Layer 3: Material Relationships → Layer 4: Category Classification →
 * Layer 5: HTML Cache Scaffold → perfectProductDictionary
 *
 * PIPELINE LAYERS:
 * ================
 * Layer 1: Variant Enhancement
 * - Extracts wood_sku/finish_sku directly from woods/finishes arrays (no title parsing!)
 * - Detects material-agnostic variants (empty arrays = always show)
 * - Pre-computes material data structures for O(1) runtime access
 *
 * Layer 2: Product Virtualization (CompositeProducts)
 * - Groups products by display_name to create unified interfaces
 * - Eliminates confusing multi-product displays
 * - Creates virtual products with combined variant pools
 *
 * Layer 3: Material Relationships
 * - Builds material compatibility matrix from enhanced variants
 * - Creates wood_sku to finish_sku relationship mappings
 * - Enables intelligent material auto-selection
 *
 * Layer 4: Category Classification
 * - Analyzes product distribution per category
 * - Auto-classifies categories: LONELY_MULTI_VARIANT, MULTI_VARIANT_ONLY, MIX, SINGLE_VARIANT_ONLY
 * - Generates display strategies and routing intelligence
 *
 * Layer 5: HTML Cache Scaffold (Performance Optimization)
 * - Creates thumbnail HTML cache structure (scaffold only)
 * - Hydrated post-bootstrap by HTML cache hydrator module
 * - Eliminates 20+ O(n) HTML generation calls per page (~6 seconds total!)
 *
 * OUTPUT: perfectProductDictionary (PRIMARY)
 * ==========================================
 * Single Master Dictionary containing:
 * - products: Map<productId, enhancedProduct> - All products with material intelligence
 * - categories: Map<categoryId, categoryClassification> - Auto-classified display strategies
 * - materialRelationships: Map<relationshipKey, compatibilityData> - Material compatibility matrix
 * - tabStructure: Map<tabId, tabData> - Tab organization and requirements
 * - metadata: Object - Performance metrics and system configuration
 *
 * LEGACY OUTPUT: processedData (DEPRECATED - TASK #6)
 * ====================================================
 * The processExtractedData() method generates legacy processedData structure.
 * This method is deprecated and logs warnings when called. Use createPerfectProductDictionary() instead.
 *
 * Dependencies:
 * - md-config-core-conductor.js (receives final PPD)
 * - md-config-core-initializer.js (provides raw DOM data)
 */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CONSTANTS = window.MurphyDoorConfigurator?.CONSTANTS;

  // Fallback sort order for categories missing both displayOrder and loadOrder
  // Mirrors the same constant defined in md-config-core-conductor.js (line 139)
  const UNKNOWN_CATEGORY_SORT_ORDER = 999;

  // Display mode constants
  const DISPLAY_MODES = {
    STANDARD: 'STANDARD',
    DIRECT_VARIANT: 'DIRECT_VARIANT',
    WOOD_FINISH_FILTERED: 'WOOD_FINISH_FILTERED',
  };

  // Escapes HTML special characters to prevent attribute-breakout injection
  // (mirrors md-config-ui-navigation-manager.js's escapeHTML).
  const escapeHTML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // ⏱️ LEAD TIME (Layer 4): build baseProduct.lead_time from the piped
  // `lead_time_category` metaobject. Returns null when none is linked, so the
  // legacy production_time_* metafields stay in charge (fallback).
  //   { source: 'metaobject', handle, unfinished, finished|null,
  //     finishClassByVariantId: { [variantId]: 'finished'|'unfinished' } }
  // finishClassByVariantId covers MATERIAL_SOURCE_CATEGORIES ('Wood & Finish')
  // variants only; a plain object (not a Map) so it survives the IndexedDB PPD
  // cache and every structure pass-through exactly like baseProduct itself.
  function buildBaseLeadTime(rawProductData) {
    const utils = window.MurphyDoorConfigurator?.utils;
    if (!utils?.resolveLeadTimeCategory || !rawProductData) return null;
    const resolved = utils.resolveLeadTimeCategory(
      rawProductData.lead_time_category
    );
    if (!resolved) return null;

    const materialCategories = (window.MurphyDoorConfigurator?.CONSTANTS
      ?.MATERIAL_SOURCE_CATEGORIES || ['Wood & Finish']);
    const finishClassByVariantId = {};
    (rawProductData.add_on_products || []).forEach((product) => {
      const category = product?.configurator_tab_category?.category;
      if (!materialCategories.includes(category)) return;
      (product.variants || []).forEach((variant) => {
        const cls = utils.classifyFinishLeadTime(product, variant);
        if (cls) finishClassByVariantId[String(variant.id)] = cls;
      });
    });

    return Object.freeze({
      source: 'metaobject',
      handle: resolved.handle,
      unfinished: Object.freeze({ ...resolved.unfinished }),
      finished: resolved.finished
        ? Object.freeze({ ...resolved.finished })
        : null,
      finishClassByVariantId: Object.freeze(finishClassByVariantId),
    });
  }

  // Carrier transit window in BUSINESS days, from Theme settings > Shipping
  // estimates (settings.shipping_transit_days_min/max), piped by
  // md-configurator.liquid. The same setting feeds Product JSON-LD transitTime.
  function normalizeTransitDays(raw) {
    if (!raw || raw.low == null || raw.high == null) return null;
    const low = Number(raw.low);
    const high = Number(raw.high);
    if (!Number.isFinite(low) || !Number.isFinite(high)) return null;
    if (low < 0 || high < 0) return null;
    return { low: Math.min(low, high), high: Math.max(low, high) };
  }

  // Add transition state constants
  const TRANSITION_STATES = {
    NONE: 'NONE',
    WOOD_APPLIED: 'WOOD_APPLIED',
    BOTH_APPLIED: 'BOTH_APPLIED',
  };

  // Add navigation state constants
  const NAVIGATION_STATES = {
    INITIAL: 'INITIAL',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETE: 'COMPLETE',
  };

  // Expose constants to window object
  window.MurphyDoorConfigurator.DISPLAY_MODES = DISPLAY_MODES;
  window.MurphyDoorConfigurator.TRANSITION_STATES = TRANSITION_STATES;
  window.MurphyDoorConfigurator.NAVIGATION_STATES = NAVIGATION_STATES;

  // Phase 1 runtime protection flag - prevents bootstrap-only pipeline methods
  // from being called after bootstrap pipeline completes.
  let bootstrapComplete = false;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // UNIVERSAL PRODUCT SORTING - UX-OPTIMIZED ORDERING
  // ============================================================================
  // Applied ONCE during PPD creation (Phase 1) for zero runtime performance cost.
  //
  // SORTING PRINCIPLES:
  // 1. "No [Category]" / "None" options -> FIRST (quick opt-out)
  // 2. Within style tiers -> Sort by PRICE (affordable -> premium)
  // 3. Style hierarchy -> Simple -> Complex (Standard -> Ornate)
  // 4. Inset vs Non-Inset -> Non-Inset first (simpler/more common)
  //
  // ⚠️ CRITICAL DATA DEPENDENCY WARNING:
  // Sorting functions rely on KEYWORD MATCHING against product display_name/title
  // strings (e.g., "no ", "none", "standard", "inset", "shaker", "slab", etc.).
  // If Shopify product names are renamed or new naming conventions are introduced,
  // these sorting functions WILL BREAK SILENTLY (products will fall through to
  // price/alphabetical fallback instead of their correct tier).
  //
  // When adding/renaming products: verify sorting output in the configurator UI
  // or check sorted PPD category arrays in /logs/product-data.json.
  // ============================================================================

  /**
   * Extracts numeric price from product object.
   * Handles multiple price formats and variant prices.
   *
   * @param {Object} product - Product object
   * @returns {number} Numeric price value (defaults to 0 if no price found)
   */
  function getProductPrice(product) {
    // Try direct price field first
    if (product.price) {
      const priceStr = String(product.price).replace(/[$,]/g, '');
      const price = parseFloat(priceStr);
      if (!isNaN(price)) return price;
    }

    // Try first variant price
    if (
      product.variants &&
      product.variants.length > 0 &&
      product.variants[0].price
    ) {
      const priceStr = String(product.variants[0].price).replace(/[$,]/g, '');
      const price = parseFloat(priceStr);
      if (!isNaN(price)) return price;
    }

    // Default to 0 if no price found
    return 0;
  }

  /**
   * Determines if a product title represents a "No [Category]" / "None" opt-out option.
   * Centralizes the None-detection logic used across all sorting functions.
   *
   * Matches titles like: "No Top Cabinet", "No Shelf", "None", "no glass", etc.
   * Uses lowercased title for case-insensitive matching.
   *
   * @param {string} lowercaseTitle - Lowercased product title string
   * @returns {boolean} True if title represents a None/opt-out option
   */
  function isNoneOption(lowercaseTitle) {
    return lowercaseTitle.startsWith('no ') || lowercaseTitle.includes('none');
  }

  /**
   * Compares two products by their None/opt-out status.
   * Returns a sort comparator value: -1 if only a is None, 1 if only b is None, 0 if tied.
   * "None" products always sort FIRST (quick opt-out UX pattern).
   *
   * @param {string} aLowerTitle - Lowercased title of product A
   * @param {string} bLowerTitle - Lowercased title of product B
   * @returns {number} -1 (a first), 1 (b first), or 0 (both same status)
   */
  function compareNoneStatus(aLowerTitle, bLowerTitle) {
    const aIsNone = isNoneOption(aLowerTitle);
    const bIsNone = isNoneOption(bLowerTitle);

    if (aIsNone && !bIsNone) return -1;
    if (!aIsNone && bIsNone) return 1;
    return 0;
  }

  /**
   * CABINET DOORS SORTING
   * Order: "No Doors" FIRST -> Non-Inset styles (by price) -> Inset styles (by price)
   * Within each tier: Glass Panel -> Raised Panel -> Reeded Shaker -> Shaker -> Slab -> then by price
   */
  function sortCabinetDoors(a, b) {
    const aTitle = (a.display_name || a.title || '').trim();
    const bTitle = (b.display_name || b.title || '').trim();

    // "No Doors" FIRST (quick opt-out)
    const aIsNoDoors =
      aTitle.toLowerCase().includes('no top cabinet') ||
      aTitle.toLowerCase().includes('no bottom cabinet');
    const bIsNoDoors =
      bTitle.toLowerCase().includes('no top cabinet') ||
      bTitle.toLowerCase().includes('no bottom cabinet');

    if (aIsNoDoors && !bIsNoDoors) return -1;
    if (!aIsNoDoors && bIsNoDoors) return 1;

    // Non-inset before inset
    const aIsInset = aTitle.toLowerCase().includes('inset');
    const bIsInset = bTitle.toLowerCase().includes('inset');

    if (!aIsInset && bIsInset) return -1;
    if (aIsInset && !bIsInset) return 1;

    // Within same tier (both inset or both non-inset), sort by style hierarchy.
    //
    // MAINTENANCE: To add a new cabinet door style, insert it into the array below
    // at the desired sort position (index 0 = shown first, higher = shown later).
    // Styles are matched via case-insensitive substring against product display_name.
    // The order reflects visual complexity: simple/transparent -> ornate/solid.
    // After changes, verify sort output in configurator UI or /logs/product-data.json.
    const styleOrder = [
      'glass panel', // Most transparent / decorative
      'raised panel', // Traditional ornate
      'reeded shaker', // Textured shaker variant
      'shaker', // Classic simple frame
      'slab', // Simplest / most minimal
    ];

    const aStyle = styleOrder.findIndex((style) =>
      aTitle.toLowerCase().includes(style)
    );
    const bStyle = styleOrder.findIndex((style) =>
      bTitle.toLowerCase().includes(style)
    );

    if (aStyle !== -1 && bStyle !== -1 && aStyle !== bStyle) {
      return aStyle - bStyle;
    }

    // Within same style, sort by price (affordable -> premium)
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      // Avoid floating point equality issues
      return aPrice - bPrice;
    }

    // Final fallback: alphabetical
    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * ENTABLATURES SORTING
   * Order: "No [Category]" FIRST -> Standard -> Square -> Crown (with price tiebreaker)
   */
  function sortEntablatures(a, b) {
    const aTitle = (a.display_name || a.title || '').toLowerCase().trim();
    const bTitle = (b.display_name || b.title || '').toLowerCase().trim();

    // "No [Category]" FIRST (quick opt-out)
    const noneCompare = compareNoneStatus(aTitle, bTitle);
    if (noneCompare !== 0) return noneCompare;

    // Style hierarchy: Standard -> Square -> Crown
    const order = ['standard', 'square', 'crown'];

    const aIndex = order.findIndex((type) => aTitle.includes(type));
    const bIndex = order.findIndex((type) => bTitle.includes(type));

    if (aIndex !== -1 && bIndex !== -1 && aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    // Price tiebreaker within same style
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      return aPrice - bPrice;
    }

    // Alphabetical fallback
    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * POCKETS/SHELVES SORTING
   * Order: "No [Category]" FIRST -> Standard (by price) -> Specialty (by price)
   */
  function sortPocketsOrShelves(a, b) {
    const aTitle = (a.display_name || a.title || '').toLowerCase().trim();
    const bTitle = (b.display_name || b.title || '').toLowerCase().trim();

    // "No [Category]" FIRST (quick opt-out)
    const noneCompare = compareNoneStatus(aTitle, bTitle);
    if (noneCompare !== 0) return noneCompare;

    // Standard before specialty (lipped)
    const aIsStandard = aTitle.includes('standard');
    const bIsStandard = bTitle.includes('standard');

    if (aIsStandard && !bIsStandard) return -1;
    if (!aIsStandard && bIsStandard) return 1;

    // Price sorting within tier
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      return aPrice - bPrice;
    }

    // Alphabetical fallback
    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * DOOR BACKING SORTING
   * Order: "No [Category]" FIRST -> Standard -> Flat -> Mirror (with price verification)
   */
  function sortDoorBacking(a, b) {
    const aTitle = (a.display_name || a.title || '').toLowerCase().trim();
    const bTitle = (b.display_name || b.title || '').toLowerCase().trim();

    // "No [Category]" FIRST (quick opt-out)
    const noneCompare = compareNoneStatus(aTitle, bTitle);
    if (noneCompare !== 0) return noneCompare;

    // Type hierarchy: Standard -> Flat -> Mirror
    const order = ['standard', 'flat', 'mirror'];

    const aIndex = order.findIndex((type) => aTitle.includes(type));
    const bIndex = order.findIndex((type) => bTitle.includes(type));

    if (aIndex !== -1 && bIndex !== -1 && aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    // Price verification within tier
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      return aPrice - bPrice;
    }

    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * LOCKING SYSTEMS SORTING
   * Order: "No [Category]" FIRST -> Friction Catch -> Book Latch -> Mechanical -> Electromagnetic
   * Within each tier: sorted by price
   */
  function sortLockingSystems(a, b) {
    const aTitle = (a.display_name || a.title || '').toLowerCase().trim();
    const bTitle = (b.display_name || b.title || '').toLowerCase().trim();

    // "No [Category]" FIRST (quick opt-out)
    const noneCompare = compareNoneStatus(aTitle, bTitle);
    if (noneCompare !== 0) return noneCompare;

    // Security level hierarchy: Simple -> Advanced
    const order = [
      'friction catch',
      'book latch',
      'mechanical',
      'electromagnetic',
    ];

    const aIndex = order.findIndex((type) => aTitle.includes(type));
    const bIndex = order.findIndex((type) => bTitle.includes(type));

    if (aIndex !== -1 && bIndex !== -1 && aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    // Price sorting within same security level
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      return aPrice - bPrice;
    }

    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * BINARY CHOICE SORTING
   * Order: "No/None" option FIRST -> Active options (by price)
   */
  function sortBinaryChoice(a, b) {
    const aTitle = (a.display_name || a.title || '').toLowerCase().trim();
    const bTitle = (b.display_name || b.title || '').toLowerCase().trim();

    // "No/None" options FIRST (quick opt-out)
    const noneCompare = compareNoneStatus(aTitle, bTitle);
    if (noneCompare !== 0) return noneCompare;

    // Active options sorted by price
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      return aPrice - bPrice;
    }

    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * STANDARD FIRST SORTING
   * Order: "No [Category]" FIRST -> "Standard" -> Others (by price)
   */
  function sortStandardFirst(a, b) {
    const aTitle = (a.display_name || a.title || '').toLowerCase().trim();
    const bTitle = (b.display_name || b.title || '').toLowerCase().trim();

    // "No [Category]" FIRST (quick opt-out)
    const noneCompare = compareNoneStatus(aTitle, bTitle);
    if (noneCompare !== 0) return noneCompare;

    // Standard before specialty
    const aIsStandard = aTitle.includes('standard');
    const bIsStandard = bTitle.includes('standard');

    if (aIsStandard && !bIsStandard) return -1;
    if (!aIsStandard && bIsStandard) return 1;

    // Price sorting
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      return aPrice - bPrice;
    }

    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * PRICE-BASED ALPHABETICAL SORTING
   * Order: "No [Category]" FIRST -> By price (low -> high) -> Alphabetical tiebreaker
   */
  function sortByPriceThenAlphabetical(a, b) {
    const aTitle = (a.display_name || a.title || '').trim();
    const bTitle = (b.display_name || b.title || '').trim();
    const aLower = aTitle.toLowerCase();
    const bLower = bTitle.toLowerCase();

    // "No [Category]" FIRST (quick opt-out)
    const noneCompare = compareNoneStatus(aLower, bLower);
    if (noneCompare !== 0) return noneCompare;

    // Price sorting (low -> high)
    const aPrice = getProductPrice(a);
    const bPrice = getProductPrice(b);

    if (Math.abs(aPrice - bPrice) > 0.01) {
      return aPrice - bPrice;
    }

    // Alphabetical fallback
    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * NUMERIC DIMENSIONS SORTING
   * Order: Numeric ascending (smallest -> largest)
   * Extracts numbers from titles like "Width 24" or "5-1/4 Depth"
   */
  function sortNumericDimensions(a, b) {
    const aTitle = (a.display_name || a.title || '').trim();
    const bTitle = (b.display_name || b.title || '').trim();

    // Extract numeric values (handles fractions like 5-1/4)
    const aValue = parseNumericDimension(aTitle);
    const bValue = parseNumericDimension(bTitle);

    if (aValue !== null && bValue !== null) {
      return aValue - bValue;
    }

    // Fallback to alphabetical if no numbers found
    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * JAMB DEPTH SORTING
   * Order: stock sizes numeric ascending (4-9/16 -> 6-9/16) -> "Custom" LAST.
   *
   * This is the only comparator in this registry that pushes a bucket to the
   * END rather than the front -- it is compareNoneStatus() inverted. Custom is
   * the escape hatch: the two stock sizes cover the overwhelming majority of
   * orders, so they lead and Custom trails.
   *
   * 🚨 Do NOT fall back to sortNumericDimensions for this category. That
   * comparator returns a numeric difference when BOTH titles parse and a
   * localeCompare otherwise -- so the moment a non-numeric product ("Custom
   * Jamb Depth") joins the category, the two return paths mix and the result
   * is not a strict weak ordering. It happens to look right for three products
   * because ASCII puts digits before "C", but that is luck, not a rule.
   */
  function sortJambDepth(a, b) {
    const aTitle = (a.display_name || a.title || '').toLowerCase().trim();
    const bTitle = (b.display_name || b.title || '').toLowerCase().trim();

    const aIsCustom = aTitle.includes('custom');
    const bIsCustom = bTitle.includes('custom');

    if (aIsCustom && !bIsCustom) return 1;
    if (!aIsCustom && bIsCustom) return -1;
    if (aIsCustom && bIsCustom) {
      return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
    }

    // Both are real dimensions -> numeric ascending
    const aValue = parseNumericDimension(aTitle);
    const bValue = parseNumericDimension(bTitle);

    if (aValue !== null && bValue !== null) {
      return aValue - bValue;
    }

    return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
  }

  /**
   * Helper: Parse numeric dimension from title.
   * Handles formats like: "24", "5-1/4", "5 1/4", "24.5", "48 inches"
   *
   * @param {string} title - Product title containing dimension
   * @returns {number|null} Numeric dimension value or null if not found
   */
  function parseNumericDimension(title) {
    // Match patterns like "24", "5-1/4", "5 1/4", "24.5"
    // Note: Space OR hyphen separator supported for fractional dimensions
    const match = title.match(/(\d+)(?:[ -](\d+)\/(\d+))?(?:\.(\d+))?/);

    if (!match) return null;

    const whole = parseInt(match[1], 10);
    const numerator = match[2] ? parseInt(match[2], 10) : 0;
    const denominator = match[3] ? parseInt(match[3], 10) : 1;
    const decimal = match[4]
      ? parseInt(match[4], 10) / Math.pow(10, match[4].length)
      : 0;

    return whole + numerator / denominator + decimal;
  }

  /**
   * Sorts products within a category based on universal UX-optimized sorting rules.
   * Applies category-specific sorting strategy with price-based ordering within tiers.
   *
   * @param {string} categoryId - Category identifier (e.g., "Top Cabinet Doors")
   * @param {Array<Object>} products - Array of Product objects to sort (sorted in-place)
   * @returns {Array<Object>} Sorted products array (mutates original array for performance)
   */
  function sortProductsForCategory(categoryId, products) {
    if (!products || products.length === 0) return products;

    // Define category-specific sorting strategies
    const sortingStrategies = {
      // CABINET DOORS - "No Doors" first, then Non-Inset (by price), then Inset (by price)
      'Top Cabinet Doors': sortCabinetDoors,
      'Bottom Cabinet Doors': sortCabinetDoors,

      // ENTABLATURES - "No [Category]" first, then Simple -> Ornate with price tiebreaker
      'Front Entablature': sortEntablatures,
      'Back Entablature': sortEntablatures,
      'Crown Front Entablature': sortEntablatures,
      'Crown Back Entablature': sortEntablatures,

      // POCKETS/SHELVES - "No [Category]" first, then Standard -> Specialty with price
      'Top Pocket': sortPocketsOrShelves,
      'Bottom Pocket': sortPocketsOrShelves,
      // KNOWN GAP, deliberately NOT fixed here: the live category names on
      // MD-Configurator products are "Top Section"/"Bottom Section", not
      // "Top Pocket"/"Bottom Pocket" (confirmed 2026-08-17 by reading
      // categoryToProductsMap off the PPD on flush-mount-bookcase-door-2-0),
      // so shelf categories fall through to NO sorting at all -- they never
      // get "No shelves" first or Standard-before-Lipped.
      //
      // Adding them here was tried and reverted: this table is Phase 1, it
      // builds the PPD every experience consumes, and there is no mode
      // awareness at this layer to scope it to apo_style. Adding the two
      // names would silently reorder shelf tiles on live CLASSIC product
      // pages -- moving the opt-out tile to first position and shifting any
      // FigPii/Cypress selector keyed on tile order -- as a rider on an
      // apo_style PR. It is a real fix and worth making, but it needs its own
      // change and its own classic-mode UAT.
      //
      // 'Shelf Style' (2026-09-16) does NOT carry that risk: it's a brand-new
      // category name with no prior live behavior on any product/mode to
      // disrupt, so there's nothing to "silently reorder" -- this establishes
      // its first-ever tile order rather than changing an established one.
      // Without it, "Lipped Shelves"/"Standard Shelves" would sort
      // alphabetically (Lipped before Standard -- the $70 upcharge shown
      // before the $0 included option).
      'Shelf Style': sortPocketsOrShelves,

      // DOOR BACKING - "No [Category]" first, then Basic -> Premium (price-sorted)
      'Door Backing': sortDoorBacking,

      // LOCKING SYSTEMS - "No [Category]" first, then Simple -> Advanced with price
      'Locking Systems': sortLockingSystems,

      // SMART HOME - "None" first, then active options by price
      'Smart Home Automations': sortBinaryChoice,

      // CASING - "No [Category]" first, then Standard -> Specialty with price
      'Front Casing': sortStandardFirst,
      'Back Casing': sortStandardFirst,

      // KNOBS & PULLS - "No [Category]" first, then by price
      'Top Knobs & Pulls': sortByPriceThenAlphabetical,
      'Bottom Knobs & Pulls': sortByPriceThenAlphabetical,

      // DIMENSIONS - Numeric ascending (smallest -> largest)
      Width: sortNumericDimensions,
      Height: sortNumericDimensions,
      Depth: sortNumericDimensions,
      'Door Depth': sortNumericDimensions,
      // Jamb Depth is a MIX category (two single-variant stock sizes + one
      // multi-variant Custom product), so it needs Custom pinned last rather
      // than left to the numeric/localeCompare fallback. See sortJambDepth().
      'Jamb Depth': sortJambDepth,

      // APPEARANCE - "No [Category]" first, then by price
      'Appearance Upgrades': sortByPriceThenAlphabetical,

      // DOOR HANDING - Standard options first, then by price
      'Door Handing': sortStandardFirst,

      // WOOD & FINISH - By price (affordable -> premium)
      'Wood & Finish': sortByPriceThenAlphabetical,
    };

    // Get sorting strategy for this category (default to price-based alphabetical)
    const sortFn = sortingStrategies[categoryId] || sortByPriceThenAlphabetical;

    // Apply sorting strategy (in-place mutation for performance)
    return products.sort(sortFn);
  }

  // Create the config object
  const config = {
    // ============================================================================
    // UNIFIED LINEAR PIPELINE PROCESSOR
    // ============================================================================

    /**
     * 🎯 CENTRALIZED TWO-STEP DETECTION LOGIC
     * Determines if a product should use 'directVariant' or 'standard' displayMode
     * This is the SINGLE SOURCE OF TRUTH for two-step product classification
     *
     * A "two-step" product:
     * - Has multiple variants (product.variants.length > 1)
     * - Is the ONLY product in its category (productsInCategory === 1)
     *
     * Two-step products skip thumbnail selection and go straight to variant browsing
     *
     * @param {Object} product - Product object with variants array
     * @param {number} productsInCategory - Count of products in this product's category
     * @returns {string} 'directVariant' for two-step, 'standard' for normal products
     */
    _calculateDisplayMode: function (product, productsInCategory) {
      const hasMultipleVariants = (product.variants?.length || 0) > 1;
      const isOnlyProductInCategory = productsInCategory === 1;
      const isTwoStep = hasMultipleVariants && isOnlyProductInCategory;

      return isTwoStep ? 'directVariant' : 'standard';
    },

    /**
     * Creates the Perfect Product Dictionary from processed data (conductor compatibility method)
     * @param {Object} processedData - Already processed data from conductor
     * @param {Object} configData - Configuration data
     * @returns {Object} Perfect Product Dictionary
     */
    createPerfectProductDictionaryFromProcessedData: function (
      processedData,
      configData
    ) {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // ============================================================================
      if (bootstrapComplete) {
        const error =
          'CRITICAL ARCHITECTURAL VIOLATION: dataPipeline.createPerfectProductDictionaryFromProcessedData() called AFTER bootstrap complete! ' +
          'This is a Phase 1 Bootstrap method. The PPD is already built and frozen.';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      isDev() &&
        console.log(
          '🚀 [DataPipeline] Phase 1 execution: Pipeline processing started'
        );
      isDev() &&
        console.log('CONDUCTOR INTEGRATION: Creating PPD from processed data');

      // Convert processedData structure to extractedData format for pipeline
      const extractedData = {
        productData: processedData.groupedProducts || processedData.products,
        configData: configData,
        woodMetaobjects: processedData.woodMetaobjects || [],
        finishMetaobjects: processedData.finishMetaobjects || [],
      };

      return this.createPerfectProductDictionary(extractedData);
    },

    /**
     * MAIN PIPELINE: Transform raw DOM data into perfectProductDictionary
     * This replaces the complex multi-module approach with a single linear pipeline
     *
     * @param {Object} extractedData - Raw data from DOM extraction by initializer
     * @returns {Object} perfectProductDictionary - Complete processed data structure
     */
    createPerfectProductDictionary: function (extractedData) {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // ============================================================================
      if (bootstrapComplete) {
        const error =
          'CRITICAL ARCHITECTURAL VIOLATION: dataPipeline.createPerfectProductDictionary() called AFTER bootstrap complete! ' +
          'This is a Phase 1 Bootstrap method that runs ONCE during initialization. ' +
          'The PPD is already built and frozen. Runtime Phase 2 modules MUST use PPD via conductor delegation.';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      isDev() &&
        console.log(
          '🚀 [DataPipeline] Phase 1 execution: Pipeline processing started'
        );
      const pipelineStart = performance.now();
      isDev() && console.log('🔄 PIPELINE: Starting unified linear processing');

      try {
        // Layer 1: Variant Enhancement
        const enhancedData = this.layer1_enhanceVariants(extractedData);

        // Layer 2: Product Virtualization
        const virtualizedData = this.layer2_virtualizeProducts(enhancedData);

        // Layer 3: Material Relationships (SKU-based - no GID name maps needed!)
        const materialData =
          this.layer3_buildMaterialRelationships(virtualizedData);

        // Layer 4: Category Classification
        const ppd = this.layer4_classifyCategories(materialData);

        // Layer 4.5: Material Filtering Structures
        const materialFilteringData =
          window.MurphyDoorConfigurator.materialFilterBuilder.buildMaterialFilteringStructures(
            ppd.products,
            ppd.categories,
            {
              simplifiedUxEnabled: extractedData.configData.simplifiedUxEnabled,
            }
          );

        ppd.materialFilteredVariantMaps =
          materialFilteringData.materialFilteredVariantMaps;
        ppd.materialSortedVariantOrders =
          materialFilteringData.materialSortedVariantOrders;
        ppd.productMaterialCapabilities =
          materialFilteringData.productMaterialCapabilities;
        ppd.categoryMaterialAwareness =
          materialFilteringData.categoryMaterialAwareness;
        ppd.simplifiedUxConfig = materialFilteringData.simplifiedUxConfig;
        ppd.materialPreferenceToFilterStateMap =
          materialFilteringData.materialPreferenceToFilterStateMap;

        isDev() &&
          console.log(
            '✅ Layer 4.5: Material filtering structures added to PPD'
          );

        // Add performance metadata
        ppd.metadata = {
          processingTime: performance.now() - pipelineStart,
          pipelineLayers: 4.5,
          timestamp: Date.now(),
          productsProcessed: ppd.products.size,
          categoriesClassified: ppd.categories.size,
          imageLayersCreated:
            ppd.imageLayerStructure.front.size +
            ppd.imageLayerStructure.back.size,
        };

        isDev() &&
          console.log(
            `✅ PIPELINE: perfectProductDictionary created in ${ppd.metadata.processingTime.toFixed(2)}ms`
          );
        isDev() &&
          console.log(
            `📊 PPD: ${ppd.products.size} products, ${ppd.categories.size} categories, ${ppd.materialRelationships.size} material relationships`
          );

        // 📊 DEV LOGGING: Save PPD snapshot for analysis
        this.logPerfectProductDictionary(ppd);

        // 📊 DEV LOGGING: Save other API data for analysis
        this.updateApiLogs(
          extractedData.configData,
          extractedData.productData,
          extractedData.woodMetaobjects,
          extractedData.finishMetaobjects,
          extractedData.forbiddenConditionGroups,
          extractedData.forbiddenRulesUnified
        );

        // 🔒 BOOTSTRAP COMPLETE: Enable runtime protection on ALL Phase 1 utilities
        // Mark self (data pipeline) as bootstrap complete
        bootstrapComplete = true;
        isDev() &&
          console.log(
            '🎯 DataPipeline: Bootstrap complete - runtime protection enabled'
          );

        // Mark all Phase 1 utility modules as bootstrap complete
        if (window.MurphyDoorConfigurator?.compositeProductPreprocessor) {
          window.MurphyDoorConfigurator.compositeProductPreprocessor.markBootstrapComplete();
        }
        if (window.MurphyDoorConfigurator?.supportVariantEnhancer) {
          window.MurphyDoorConfigurator.supportVariantEnhancer.markBootstrapComplete();
        }
        if (window.MurphyDoorConfigurator?.materialFilterBuilder) {
          window.MurphyDoorConfigurator.materialFilterBuilder.markBootstrapComplete();
        }

        return ppd;
      } catch (error) {
        isDev() &&
          console.error(
            '❌ PIPELINE: Failed to create perfectProductDictionary:',
            error
          );
        throw error;
      }
    },

    /**
     * 🔄 Rebuild composite product scores when material preferences change
     *
     * This method is called when material preferences are updated, clearing
     * stale scores and re-computing best variants for all CompositeProducts.
     *
     * @param {Object} newPreferences - Updated material preferences
     */
    rebuildCompositeProductScores: function (newPreferences) {
      const METHOD_SIGNATURE = 'DataPipeline.rebuildCompositeProductScores';
      isDev() &&
        console.log(
          `🔄 ${METHOD_SIGNATURE}: Material preferences changed - rebuilding composite scores`
        );

      const rebuildStart = performance.now();
      let compositeCount = 0;

      try {
        const ppd =
          window.MurphyDoorConfigurator?.conductor?.perfectProductDictionary;
        if (!ppd || !ppd.products) {
          isDev() &&
            console.warn(
              `${METHOD_SIGNATURE}: PPD not available, cannot rebuild scores`
            );
          return;
        }

        // Iterate over all products and rebuild scores for CompositeProducts
        ppd.products.forEach((product) => {
          if (
            product.type === 'CompositeProduct' &&
            product.variants &&
            product.variants.length > 0
          ) {
            compositeCount++;
            this._computeAndSetBestVariant(product, newPreferences);
          }
        });

        const rebuildTime = performance.now() - rebuildStart;

        isDev() &&
          console.log(
            `✅ ${METHOD_SIGNATURE}: Rebuilt ${compositeCount} CompositeProduct scores in ${rebuildTime.toFixed(2)}ms`
          );

        // Publish event for other modules
        if (window.MurphyDoorConfigurator?.CoreEventBus) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.PPD_COMPOSITE_SCORES_UPDATED,
            {
              compositeCount,
              rebuildTime,
              reason: 'material_preferences_changed',
              preferences: newPreferences,
            }
          );
        }
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error rebuilding composite scores:`,
            error
          );
      }
    },

    /**
     * 🔧 PRIVATE HELPER: Compute and set best variant for a CompositeProduct
     *
     * Shared logic used by both Layer 4 initial computation and score rebuild.
     * Ensures consistent scoring algorithm across bootstrap and runtime updates.
     *
     * @private
     * @param {Object} product - CompositeProduct to score
     * @param {Object} preferences - Material preferences for scoring
     */
    _computeAndSetBestVariant: function (product, preferences) {
      if (!product.variants || product.variants.length === 0) {
        // Set a default for products without variants
        product.bestVariantByMaterialPreference = {
          variant: null,
          score: 0,
          scoredAt: Date.now(),
          preferencesSnapshot: null,
        };
        return;
      }

      let bestVariant = product.variants[0];
      let bestScore = 0;

      if (preferences && window.MurphyDoorConfigurator?.sortingManager) {
        // Create a temporary structure to hold variants and their scores without mutating original objects.
        const variantsWithScores = product.variants.map((variant) => {
          const score =
            window.MurphyDoorConfigurator.sortingManager.calculateVariantMaterialScore(
              variant,
              preferences
            );
          return { variant, score };
        });

        // Find the best variant from the scored list.
        const bestItem = variantsWithScores.reduce(
          (best, current) => {
            return current.score > best.score ? current : best;
          },
          { variant: bestVariant, score: bestScore }
        );

        bestVariant = bestItem.variant;
        bestScore = bestItem.score;

        // Sort the temporary structure.
        variantsWithScores.sort((a, b) => b.score - a.score);

        // Replace the variants array with a new sorted array. This avoids in-place mutation.
        product.variants = variantsWithScores.map((item) => item.variant);
      }

      product.bestVariantByMaterialPreference = {
        variant: bestVariant,
        score: bestScore,
        scoredAt: Date.now(),
        preferencesSnapshot: preferences
          ? {
              woodApplied: preferences.woodApplied || false,
              finishApplied: preferences.finishApplied || false,
              appliedWoodGid: preferences.appliedWoodGid || null,
              appliedFinishGid: preferences.appliedFinishGid || null,
            }
          : null,
      };
    },

    // ============================================================================
    // LAYER 1: VARIANT ENHANCEMENT
    // ============================================================================

    /**
     * Layer 1: Enhance variants with material intelligence
     * Extracts wood_sku/finish_sku directly from woods/finishes arrays (SKU-based filtering)
     */
    layer1_enhanceVariants: function (extractedData) {
      if (bootstrapComplete) {
        throw new Error(
          'CRITICAL: layer1_enhanceVariants() called AFTER bootstrap complete! Phase 1 only.'
        );
      }
      isDev() &&
        console.log(
          '🚀 [VariantEnhancer] Phase 1 execution: Material intelligence processing started'
        );
      isDev() &&
        console.log(
          '🔄 LAYER 1: Enhancing variants with SKU-based material extraction'
        );

      const { productData, woodMetaobjects, finishMetaobjects } = extractedData;

      // Initialize variant enhancer for title parsing fallback
      // NOTE: supportVariantEnhancer is an ACTIVE, FULLY IMPLEMENTED support module (Phase 1)
      // used for parsing variant titles when metafield arrays are null. This handles 127 products
      // with missing variant.woods/finishes data by extracting SKUs from title strings.
      // Status: NOT deprecated - this is a core Bootstrap Phase 1 support module.
      if (
        window.MurphyDoorConfigurator.supportVariantEnhancer &&
        !window.MurphyDoorConfigurator.supportVariantEnhancer.initialized
      ) {
        isDev() &&
          console.log(
            '🔧 LAYER 1: Initializing variant enhancer for title parsing fallback'
          );
        window.MurphyDoorConfigurator.supportVariantEnhancer.initialize(
          woodMetaobjects || [],
          finishMetaobjects || []
        );
      }

      // Collect variants with their parent product context for SKU extraction
      const variantProductPairs = [];

      // Add main product variants with context
      if (productData.variants && Array.isArray(productData.variants)) {
        productData.variants.forEach((variant) => {
          // Tag variant with parent product title for dimension extraction context
          variant._parentProductTitle = productData.title || '';
          variantProductPairs.push({
            variant: variant,
            productContext: {
              woods: productData.woods || [],
              finishes: productData.finishes || [],
              featured_image: productData.featured_image || null,
            },
          });
        });
      }

      // Add variants from add-on products with their specific context
      if (
        productData.add_on_products &&
        Array.isArray(productData.add_on_products)
      ) {
        productData.add_on_products.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              // Tag variant with parent product title for dimension extraction context
              variant._parentProductTitle = product.title || '';
              variantProductPairs.push({
                variant: variant,
                productContext: {
                  woods: product.woods || [],
                  finishes: product.finishes || [],
                  featured_image: product.featured_image || null,
                },
              });
            });
          }
        });
      }

      isDev() &&
        console.log(
          '📊 LAYER 1: Collected',
          variantProductPairs.length,
          'variant-product pairs for enhancement'
        );

      // 🎯 SKU-BASED MATERIAL EXTRACTION: Extract wood/finish SKUs directly from arrays
      // FALLBACK: Use variant enhancer title parsing when metafield arrays are missing
      isDev() &&
        console.log(
          '🎯 LAYER 1: Extracting material SKUs from structured arrays with variant enhancer fallback'
        );
      const enhancedVariants = variantProductPairs.map((pair) => {
        const variant = { ...pair.variant };

        // PRIORITY 1: Extract wood/finish SKUs from metafield arrays
        variant.wood_sku = variant.woods?.[0]?.sku || null;
        variant.wood_name = variant.woods?.[0]?.type || null;
        variant.finish_sku = variant.finishes?.[0]?.sku || null;
        variant.finish_name = variant.finishes?.[0]?.finish || null;

        // PRIORITY 2: FALLBACK to variant enhancer if metafield arrays are null
        // This handles 127 products with missing variant metafields by parsing titles
        if (
          (!variant.wood_sku || !variant.finish_sku) &&
          variant.title &&
          variant.title !== 'Default Title' &&
          window.MurphyDoorConfigurator.supportVariantEnhancer &&
          window.MurphyDoorConfigurator.supportVariantEnhancer.initialized
        ) {
          // Try wood lookup from title if not already set
          if (!variant.wood_sku) {
            const titleParts = variant.title.split('/').map((s) => s.trim());
            for (const part of titleParts) {
              const woodGid =
                window.MurphyDoorConfigurator.supportVariantEnhancer.lookupWoodGid(
                  part
                );
              if (woodGid) {
                variant.wood_sku =
                  window.MurphyDoorConfigurator.supportVariantEnhancer.extractSkuFromGid(
                    woodGid,
                    true
                  );
                variant.wood_name = part;
                isDev() &&
                  console.log(
                    `🔧 FALLBACK: Extracted wood SKU "${variant.wood_sku}" from title "${variant.title}"`
                  );
                break;
              }
            }
          }

          // Try finish lookup from title if not already set
          if (!variant.finish_sku) {
            const titleParts = variant.title.split('/').map((s) => s.trim());
            for (const part of titleParts) {
              const finishGid =
                window.MurphyDoorConfigurator.supportVariantEnhancer.lookupFinishGid(
                  part
                );
              if (finishGid) {
                variant.finish_sku =
                  window.MurphyDoorConfigurator.supportVariantEnhancer.extractSkuFromGid(
                    finishGid,
                    false
                  );
                variant.finish_name = part;
                isDev() &&
                  console.log(
                    `🔧 FALLBACK: Extracted finish SKU "${variant.finish_sku}" from title "${variant.title}"`
                  );
                break;
              }
            }
          }
        }

        // Empty arrays AND no title match = agnostic (always show, never filter/sort)
        variant.isMaterialAgnostic = !variant.wood_sku && !variant.finish_sku;

        return variant;
      });

      const materialVariants = enhancedVariants.filter(
        (v) => v.wood_sku || v.finish_sku
      );
      const agnosticVariants = enhancedVariants.filter(
        (v) => v.isMaterialAgnostic
      );
      isDev() &&
        console.log(
          `✅ LAYER 1: Extracted SKUs - ${materialVariants.length} material variants, ${agnosticVariants.length} agnostic variants`
        );

      // ⚡ PPD-FIRST OPTIMIZATION: Pre-extract material data structure
      // SKU-based material data for O(1) filtering and sorting
      /**
       * @typedef {object} MaterialData
       * @property {boolean} hasCompatibleMaterials - True if the variant has material SKUs
       * @property {string|null} woodSku - The wood SKU (e.g., "PG", "WN")
       * @property {string|null} finishSku - The finish SKU (e.g., "UN", "MS")
       * @property {string|null} woodName - The wood display name
       * @property {string|null} finishName - The finish display name
       * @property {boolean} isMaterialAgnostic - True if variant has no material preferences (empty arrays)
       * @property {Array<string>} compatibilityTags - Any compatibility tags
       */
      const materialDataStart = performance.now();
      isDev() &&
        console.log(
          '🚀 LAYER 1: Pre-extracting SKU-based material data for O(1) access'
        );
      enhancedVariants.forEach((variant) => {
        /** @type {MaterialData} */
        variant.materialData = {
          hasCompatibleMaterials: !!(variant.wood_sku || variant.finish_sku),
          woodSku: variant.wood_sku || null,
          finishSku: variant.finish_sku || null,
          woodName: variant.wood_name || null,
          finishName: variant.finish_name || null,
          isMaterialAgnostic: variant.isMaterialAgnostic || false,
          compatibilityTags: variant.compatibility_tags || [],
        };
      });
      const materialDataTime = performance.now() - materialDataStart;
      isDev() &&
        console.log(
          `⚡ LAYER 1: SKU-based material data complete in ${materialDataTime.toFixed(2)}ms for ${enhancedVariants.length} variants`
        );

      // 🚀 LAYER 1: Pre-parse variant dimensions (width, height, depth, handling) - ONCE at bootstrap
      isDev() &&
        console.log(
          '🔄 LAYER 1: Pre-parsing variant dimensions for O(1) runtime access'
        );
      const dimensionExtractionStart = performance.now();

      let dimensionsExtracted = 0;

      enhancedVariants.forEach((variant) => {
        // Extract all dimensions from variant title ONCE
        const titleLower = (variant.title || '').toLowerCase();

        let width = this._extractWidth(variant.title);
        let height = this._extractHeight(variant.title);
        let depth = this._extractDepth(variant.title);
        const handling = this._extractHandling(titleLower);

        // FALLBACK: For variants like "30 in. (Rough Opening 32 in.)" or "6-1/2 in. (Shelf Depth = 4-3/4 in.)"
        // that use "in." without a dimension-type suffix (w/d/h), extract the standalone dimension
        // and assign it based on the parent product's title context.
        // ⚠️ MAINTENANCE WARNING: This logic is brittle and depends on product titles containing
        // keywords like "width", "depth", or "height". If naming conventions change in Shopify admin,
        // this will silently fail. Long-term fix: add structured dimension metafields to all variants.
        if (!width && !height && !depth && !handling && variant.title) {
          const standaloneDim = this._extractStandaloneDimension(variant.title);
          if (standaloneDim !== null) {
            // Use parent product title to determine dimension type
            const parentTitle = (
              variant._parentProductTitle || ''
            ).toLowerCase();
            if (
              parentTitle.includes('width') ||
              parentTitle.includes('widths')
            ) {
              width = standaloneDim;
            } else if (
              parentTitle.includes('depth') ||
              parentTitle.includes('depths')
            ) {
              depth = standaloneDim;
            } else if (
              parentTitle.includes('height') ||
              parentTitle.includes('heights')
            ) {
              height = standaloneDim;
            }
          }
        }

        variant.extractedDimensions = Object.freeze({
          width,
          height,
          depth,
          handling,
        });

        if (
          Object.values(variant.extractedDimensions).some((v) => v !== null)
        ) {
          dimensionsExtracted++;
        }
      });

      const dimensionExtractionTime =
        performance.now() - dimensionExtractionStart;
      isDev() &&
        console.log(
          `✅ LAYER 1: Pre-extracted dimensions for ${dimensionsExtracted}/${enhancedVariants.length} variants in ${dimensionExtractionTime.toFixed(2)}ms (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Dimension extraction reduced from O(m) string ops to O(1) property reads`
        );

      // 🚀 LAYER 1: Build variantMap for O(1) variant lookups (PPD-First Architecture)
      isDev() &&
        console.log('🔄 LAYER 1: Building variantMap for O(1) variant lookups');
      const variantMapStart = performance.now();
      const variantMap = new Map();

      // Add base product variants with context
      if (productData.variants && Array.isArray(productData.variants)) {
        productData.variants.forEach((variant) => {
          const variantId = parseInt(variant.id);
          variantMap.set(variantId, {
            ...variant,
            productId: productData.id,
            productHandle: productData.handle,
            productTitle: productData.title,
            isBaseProduct: true,
          });
        });
      }

      // Add add-on product variants with context
      if (
        productData.add_on_products &&
        Array.isArray(productData.add_on_products)
      ) {
        productData.add_on_products.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              const variantId = parseInt(variant.id);
              variantMap.set(variantId, {
                ...variant,
                productId: product.id,
                productHandle: product.handle,
                productTitle: product.title,
                // Product-level metafield: variants inherit it so consumers can read
                // inset-ness off a resolved variant without a second product lookup.
                productIsInset: product.is_inset === true,
                isBaseProduct: false,
              });
            });
          }
        });
      }

      Object.freeze(variantMap);

      const variantMapTime = performance.now() - variantMapStart;
      isDev() &&
        console.log(
          `✅ LAYER 1: Built variantMap - ${variantMap.size} variants in ${variantMapTime.toFixed(2)}ms (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Variant lookup complexity reduced from O(n) to O(1)`
        );

      // 🚀 LAYER 1: Pre-extract display formatting for all variants (PPD-First Architecture)
      isDev() &&
        console.log(
          '🔄 LAYER 1: Pre-extracting display formatting for UI consistency'
        );
      const displayFormattingStart = performance.now();

      // Build product reference map for O(1) lookups
      const productMap = new Map();
      productMap.set(productData.id, productData);
      if (
        productData.add_on_products &&
        Array.isArray(productData.add_on_products)
      ) {
        productData.add_on_products.forEach((product) => {
          productMap.set(product.id, product);
        });
      }

      // ============================================================================
      // 🔥 DISPLAY NAME NORMALIZATION (PPD-First Pattern) - MUST happen BEFORE displayFormatting
      // ============================================================================
      isDev() &&
        console.log(
          '🔄 LAYER 1: Normalizing display_name for ALL products (PPD-First pattern)'
        );
      let productsNormalized = 0;

      // Normalize base product display_name
      if (!productData.display_name) {
        productData.display_name = productData.title;
        isDev() &&
          console.log(
            `  ✅ Base product: Normalized display_name from title "${productData.title}"`
          );
      }
      Object.freeze(productData.display_name);
      productsNormalized++;

      // Normalize all add-on products display_name
      if (
        productData.add_on_products &&
        Array.isArray(productData.add_on_products)
      ) {
        productData.add_on_products.forEach((product, index) => {
          if (!product.display_name) {
            product.display_name = product.title;
            isDev() &&
              console.log(
                `  ✅ Add-on product ${index + 1}: Normalized display_name from title "${product.title}"`
              );
          }
          Object.freeze(product.display_name);
          productsNormalized++;
        });
      }

      isDev() &&
        console.log(
          `✅ LAYER 1: Normalized display_name for ${productsNormalized} products (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: All Phase 2 modules can now use product.display_name without fallback logic`
        );

      enhancedVariants.forEach((variant) => {
        // Get parent product from variantMap context
        const variantContext = variantMap.get(parseInt(variant.id));
        const product = variantContext
          ? productMap.get(variantContext.productId)
          : null;

        if (!product) {
          isDev() &&
            console.warn(
              `⚠️ LAYER 1: Could not find product for variant ${variant.id}`
            );
          return;
        }

        // ✅ STEP 1: Extract clean variant name (remove redundant product name)
        let cleanVariantName = variant.title;

        // Remove redundant product patterns
        // ⚠️ CRITICAL DATA DEPENDENCY ⚠️
        // The logic below assumes variant titles follow a "Product Name / Variant Option" format.
        // If this naming convention changes in Shopify, this parser MUST be updated.
        // 🧙‍♂️ INTELLIGENT PARSING: Use product metadata to detect which part is the variant option
        if (cleanVariantName && cleanVariantName.includes(' / ')) {
          const parts = cleanVariantName.split(' / ');

          // Smart detection: Use product's woods/finishes metadata to identify the variant option
          const productWoods = product.woods || [];
          const productFinishes = product.finishes || [];

          // Build lookup arrays for intelligent matching
          const woodNames = productWoods.map((w) => w.type).filter(Boolean);
          const finishNames = productFinishes
            .map((f) => f.finish || f.type)
            .filter(Boolean);

          let variantOptionPart = null;

          // Strategy 1: Find which part matches a finish (most common case - finish IS the variant option)
          for (let i = 0; i < parts.length; i++) {
            const part = parts[i].trim();
            // Case-insensitive match
            if (
              finishNames.some(
                (finish) => finish.toLowerCase() === part.toLowerCase()
              )
            ) {
              variantOptionPart = part;
              break;
            }
          }

          // Strategy 2: If no finish match, check if a part matches a wood
          // In this case, the OTHER part is the variant option (e.g., Paint Grade products)
          if (!variantOptionPart && woodNames.length > 0) {
            for (let i = 0; i < parts.length; i++) {
              const part = parts[i].trim();
              if (
                woodNames.some(
                  (wood) => wood.toLowerCase() === part.toLowerCase()
                )
              ) {
                // Found the wood part, so the OTHER part is the variant option
                variantOptionPart = parts[i === 0 ? 1 : 0]?.trim();
                break;
              }
            }
          }

          // Fallback: If metadata matching failed, use LAST part (legacy behavior)
          // This preserves backward compatibility with existing products
          cleanVariantName = variantOptionPart || parts[parts.length - 1];
        }

        // ✅ STEP 2: Extract clean product name (guaranteed to exist from Layer 1 normalization)
        let cleanProductName = product.display_name;

        // Remove size info from product title if present (e.g., "(Single Door)")
        if (cleanProductName) {
          cleanProductName = cleanProductName
            .replace(/\s*\([^)]*\)\s*/g, '')
            .trim();
        }

        // ✅ STEP 3: Build context-specific display formats (PPD-FIRST)
        // Pre-compute ALL formats to eliminate runtime string manipulation in Phase 2
        const isDefaultTitle = variant.title === 'Default Title';

        // Context 1: Thumbnail Display (product name only, hide variant)
        const thumbnailDisplay = cleanProductName;

        // Context 2: Variant Browsing Display (variant option only, hide product name)
        // Special case: Hide "Default Title" variants entirely
        const variantBrowsingDisplay = isDefaultTitle ? null : cleanVariantName;

        // Context 3: Composite Variant Display (MaterialType | FinishName)
        // Pre-extract composite display format with "|" separator for CompositeProducts
        // ✅ FIX: Use cleanVariantName (product name already stripped) to prevent duplication
        // in selectionDisplay which prepends cleanProductName. Using variant.title here
        // caused "Maple Veneer | Maple Veneer | Clear Stain" because variant.title still
        // contains the product name prefix that cleanVariantName has already removed.
        let compositeVariantDisplay = cleanVariantName;

        // Check if we have material info to build composite format from metadata
        const hasWoodInfo =
          variant.wood_sku || variant.wood_name || variant.material_type;
        const hasFinishInfo = variant.finish_sku || variant.finish_name;

        if (hasWoodInfo && hasFinishInfo) {
          const materialName =
            variant.material_type || variant.wood_name || 'Unknown Material';
          const finishName = variant.finish_name || 'Unknown Finish';
          compositeVariantDisplay = `${materialName} | ${finishName}`;
        }

        // Context 4: Full Display (ProductName | VariantName) - Fallback
        const fullDisplay = isDefaultTitle
          ? cleanProductName
          : `${cleanProductName} | ${cleanVariantName}`;

        // Context 5: Category Selection Display (full context for ALL product types)
        // Single-variant Default Title: Just product name (no redundant "| Default Title")
        // Multi-variant / named-variant: Full format "Product | Material | Finish"
        // ✅ FIX: Detect and prevent duplication when compositeVariantDisplay already starts
        // with the product name (e.g., composite products like "Maple Veneer" where the
        // metadata-built display "Maple Veneer | Clear Stain" already includes the product name)
        let selectionDisplay;
        if (isDefaultTitle) {
          selectionDisplay = cleanProductName;
        } else if (compositeVariantDisplay.startsWith(cleanProductName)) {
          // compositeVariantDisplay already includes product name - use it directly
          selectionDisplay = compositeVariantDisplay;
        } else {
          selectionDisplay = `${cleanProductName} | ${compositeVariantDisplay}`;
        }

        /**
         * Pre-computed, context-aware display strings for a variant.
         * @property {string} cleanVariantName - The variant option name, stripped of the product title.
         * @property {string} cleanProductName - The product's display name, stripped of any parenthetical size info.
         * @property {string} thumbnailDisplay - For product thumbnails. Displays product name ONLY.
         * @property {string|null} variantBrowsingDisplay - For variant selection UI. Displays variant option ONLY. Can be `null` for "Default Title" variants.
         * @property {string} compositeVariantDisplay - For composite material variants, e.g., "Material | Finish".
         * @property {string} selectionDisplay - For category selection UI (all product types). Single-variant: "Product". Multi-variant: "Product | Material | Finish".
         * @property {string} fullDisplay - The full, backward-compatible display name, e.g., "Product | Variant".
         * @property {boolean} isDefaultTitle - Flag indicating if the variant is a "Default Title" variant.
         * @property {boolean} shouldHideVariantName - Alias for isDefaultTitle (for readability in UI code).
         * @property {string} baseDisplay - Legacy alias for fullDisplay (backward compatibility).
         * @property {string} originalVariantTitle - Original Shopify variant title (for debugging).
         * @property {string} originalProductTitle - Original Shopify product title (for debugging).
         * @property {boolean} hasDisplayName - Whether the product has a custom display_name metafield.
         */
        // ✅ STEP 4: Detect Delivery category for shipping time display feature
        const isDeliveryCategory =
          product.configurator_tab_category?.category ===
          CONSTANTS.CATEGORY_NAMES.DELIVERY;

        // ✅ STEP 5: Freeze enhanced displayFormatting with ALL context-aware formats
        variant.displayFormatting = Object.freeze({
          // Raw clean names (for building custom formats if needed)
          cleanVariantName: cleanVariantName,
          cleanProductName: cleanProductName,

          // ⚡ NEW: Context-specific pre-built formats (ZERO runtime manipulation needed)
          thumbnailDisplay: thumbnailDisplay, // For product thumbnails in category buttons
          variantBrowsingDisplay: variantBrowsingDisplay, // For variant selection UI
          compositeVariantDisplay: compositeVariantDisplay, // For composite product variants
          selectionDisplay: selectionDisplay, // For category selection UI (all product types)
          fullDisplay: fullDisplay, // Fallback for backward compatibility

          // Special case flags
          isDefaultTitle: isDefaultTitle,
          shouldHideVariantName: isDefaultTitle,
          isDeliveryCategory: isDeliveryCategory, // For Delivery tab shipping time display
          isRushFeeProduct:
            isDeliveryCategory &&
            product.handle === CONSTANTS.PRODUCT_HANDLES.RUSH_FEE_UPGRADE, // For Rush Fee dynamic production time display

          // Legacy format (keep for backward compatibility during transition)
          baseDisplay: fullDisplay,

          // Metadata for debugging
          originalVariantTitle: variant.title,
          originalProductTitle: product.title,
          hasDisplayName: !!product.display_name,
        });
      });

      const displayFormattingTime = performance.now() - displayFormattingStart;
      isDev() &&
        console.log(
          `✅ LAYER 1: Pre-extracted context-aware display formats for ${enhancedVariants.length} variants in ${displayFormattingTime.toFixed(2)}ms (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          '✅ LAYER 1: Pre-extracted compositeVariantDisplay with | separator'
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Context-aware formatting eliminates runtime string manipulation (thumbnails, variant browsing, composite variants)`
        );

      // Store enhanced variants globally for other layers to access
      this.enhancedVariants = enhancedVariants;

      isDev() &&
        console.log(
          `✅ LAYER 1: Variant enhancement complete - ${enhancedVariants.length} variants processed, enhanced, and material data pre-extracted`
        );

      // Return enhanced data structure with enhanced variants and variantMap included
      return {
        ...extractedData,
        enhancedVariants: enhancedVariants,
        variantMap: variantMap,
      };
    },

    // ============================================================================
    // LAYER 2: PRODUCT VIRTUALIZATION
    // ============================================================================

    /**
     * Layer 2: Create CompositeProducts by grouping products with same display_name
     * Eliminates confusing multi-product displays
     */
    layer2_virtualizeProducts: function (enhancedData) {
      if (bootstrapComplete) {
        throw new Error(
          'CRITICAL: layer2_virtualizeProducts() called AFTER bootstrap complete! Phase 1 only.'
        );
      }
      isDev() &&
        console.log(
          '🚀 [CompositeProductPreprocessor] Phase 1 execution: Product virtualization started'
        );
      isDev() &&
        console.log('🔄 LAYER 2: Virtualizing products (CompositeProducts)');

      const { productData } = enhancedData;
      let totalProductsReceived = 0;
      let enhancedProductsCount = 0;
      let nonEnhancedProductsCount = 0;

      // Track composite product groups
      const compositeGroups = new Map();
      const standaloneProducts = [];
      let trueCompositeGroups = []; // SCOPE FIX: Declare at function level
      let compositeGroupsCreated = 0;
      let totalVariantsClustered = 0;

      if (
        productData.add_on_products &&
        Array.isArray(productData.add_on_products)
      ) {
        productData.add_on_products.forEach((product) => {
          totalProductsReceived++;

          // Check if this product has any enhanced variants
          let hasEnhancedVariants = false;
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              if (variant.wood_sku || variant.finish_sku) {
                hasEnhancedVariants = true;
              }
            });
          }

          if (hasEnhancedVariants) {
            enhancedProductsCount++;
          } else {
            nonEnhancedProductsCount++;
          }

          // 🔥 DEFENSIVE FIX: Normalize display_name if Layer 1 normalization was bypassed
          // This ensures ALL products have display_name set, preventing null display_name from causing
          // products to be incorrectly classified as standalone and breaking thumbnail generation
          if (!product.display_name && product.title) {
            product.display_name = product.title;
            isDev() &&
              console.log(
                `🛡️ LAYER 2 DEFENSIVE: Normalized display_name for "${product.title}" (Layer 1 normalization was bypassed)`
              );
          }

          // Group products by display_name + category (exact match)
          // WHITESPACE FIX: Trim both fields to handle human typos in Shopify admin
          const displayName = product.display_name?.trim();
          // Align with CompositeProductPreprocessor: include fallback to product.category
          const rawCategory =
            product.configurator_tab_category?.category || product.category;
          const category = rawCategory?.trim();

          // Skip products with null display_name or missing category
          if (!displayName || !category) {
            standaloneProducts.push(product);
            isDev() &&
              console.log(
                `🔍 LAYER 2: Standalone product "${product.title}" (display_name: ${displayName}, category: ${category})`
              );
            return;
          }

          // Create composite group key
          const groupKey = `${displayName}|${category}`;

          if (!compositeGroups.has(groupKey)) {
            compositeGroups.set(groupKey, {
              display_name: displayName,
              category: category,
              products: [],
              clustered_variants: [],
            });
          }

          // Add product to composite group
          const group = compositeGroups.get(groupKey);
          group.products.push(product);

          // Cluster enhanced variants from Layer 1 (exclude "Default Title" variants)
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              if (variant.title !== 'Default Title') {
                // Find the enhanced version of this variant from Layer 1
                let enhancedVariant = null;
                if (
                  enhancedData.enhancedVariants &&
                  Array.isArray(enhancedData.enhancedVariants)
                ) {
                  enhancedVariant = enhancedData.enhancedVariants.find(
                    (ev) => ev.id === variant.id
                  );
                }

                // Use enhanced variant if available, otherwise use original variant
                const variantToCluster = enhancedVariant || variant;

                group.clustered_variants.push({
                  ...variantToCluster,
                  source_product_id: product.id,
                  source_product_title: product.title,
                  // The member product's 3D model. A composite (Standard Front
                  // Casing, Shelf Style...) merges Paint Grade / Veneers / White
                  // Oak / TFL members that each carry their OWN glb_url, so the
                  // composite parent has none; the renderer reads it here. This is
                  // the stage the RUNTIME composites come from (not the
                  // composite-product preprocessor), measured on staging
                  // 2026-09-24: 0 of 30 casing variants carried a GLB.
                  source_product_glb_url: product.glb_url || null,
                  // Same reason for inset: "Inset Shaker" etc. are members of a
                  // composite whose parent has no is_inset, so the knob logic reads it here.
                  source_product_is_inset: product.is_inset === true,
                });
                totalVariantsClustered++;

                // Log when material variant is clustered
                if (
                  enhancedVariant &&
                  (enhancedVariant.wood_sku || enhancedVariant.finish_sku)
                ) {
                  isDev() &&
                    console.log(
                      `🧬 LAYER 2: Material variant ${variant.id} (wood:${enhancedVariant.wood_sku || 'none'}, finish:${enhancedVariant.finish_sku || 'none'}) added to "${group.display_name}"`
                    );
                }
              }
            });
          }
        });

        // Log composite group creation
        compositeGroupsCreated = compositeGroups.size;
        compositeGroups.forEach((group) => {
          isDev() &&
            console.log(
              `🎯 LAYER 2: Created composite "${group.display_name}" with ${group.products.length} products, ${group.clustered_variants.length} variants`
            );
        });

        // ARCHITECTURAL FIX: Filter single-product groups
        // Only groups with 2+ products should become CompositeProducts
        // Single-product groups should remain as standalone products
        trueCompositeGroups = []; // Reset array for filtering
        compositeGroups.forEach((group) => {
          if (group.products.length > 1) {
            // True composite: multiple products with same display_name
            trueCompositeGroups.push(group);
            isDev() &&
              console.log(
                `🧩 LAYER 2: Composite group "${group.display_name}" has ${group.products.length} products - creating CompositeProduct`
              );
          } else if (group.products.length === 1) {
            // Single-product group: keep as standalone, NOT a CompositeProduct
            standaloneProducts.push(group.products[0]);
            isDev() &&
              console.log(
                `🏠 LAYER 2: Single-product group "${group.display_name}" - keeping as standalone product`
              );
          }
        });

        // TASK 2: Tag variants in TRUE CompositeProducts with isCompositeProductVariant flag
        // This flag distinguishes composite variants from standalone multi-variant product variants
        // CRITICAL: Only tag variants in true composites (2+ products), NOT single-product groups
        let totalCompositeVariantsTagged = 0;
        trueCompositeGroups.forEach((group) => {
          group.clustered_variants.forEach((variant) => {
            variant.isCompositeProductVariant = true;
            totalCompositeVariantsTagged++;
          });
        });

        isDev() &&
          console.log(
            `✅ LAYER 2: Tagged ${totalCompositeVariantsTagged} variants with isCompositeProductVariant = true`
          );
        isDev() &&
          console.log(
            `📊 LAYER 2: ${trueCompositeGroups.length} true composites, ${standaloneProducts.length} total standalone products`
          );
      }

      isDev() &&
        console.log(
          `🎯 LAYER 2: Received ${totalProductsReceived} products from Layer 1`
        );
      isDev() &&
        console.log(
          `📊 LAYER 2: Enhanced products: ${enhancedProductsCount}, Non-enhanced products: ${nonEnhancedProductsCount}`
        );
      isDev() &&
        console.log(
          `🔄 LAYER 2: Created ${compositeGroupsCreated} composite groups with ${totalVariantsClustered} clustered variants`
        );
      isDev() &&
        console.log(
          `📦 LAYER 2: ${standaloneProducts.length} standalone products (null display_name or missing category)`
        );

      // Create virtualized product data structure
      const virtualizedData = {
        ...enhancedData,
        productData: {
          ...productData,
          composite_groups: trueCompositeGroups,
          standalone_products: standaloneProducts,
          original_products: productData.add_on_products, // Keep original for reference
        },
      };

      // Return virtualized data to Layer 3
      return virtualizedData;
    },

    // ============================================================================
    // MATERIAL NAME MAPS SETUP (for Layer 3)
    // ============================================================================

    /**
     * Setup material name mapping tables for Layer 3 processing
     * Moved from legacy processExtractedData to support unified pipeline
     * @param {Object} extractedData - Raw data from DOM extraction
     */
    setupMaterialNameMaps: function (extractedData) {
      isDev() &&
        console.log('🔧 PIPELINE: Setting up material name maps for Layer 3');

      const { woodMetaobjects, finishMetaobjects } = extractedData;

      // Create slug-to-name Maps for Woods and Finishes
      // P3.1.4 FIX: Map SKU to slug format to match variant enhancer GID extraction
      this.woodGidToNameMap = new Map();
      const woodSkuToSlugMap = {
        WN: 'walnut-veneer',
        MP: 'maple-veneer',
        RO: 'red-oak-veneer',
        KA: 'knotty-alder-veneer',
        'EU-O': 'european-oak',
        PG: 'paint-grade',
        TFL: 'tfl',
        WO: 'white-oak-veneer',
      };

      isDev() &&
        console.log(
          `🔍 DEBUG: woodMetaobjects.length = ${woodMetaobjects?.length || 0}`
        );
      woodMetaobjects.forEach((meta) => {
        if (meta.sku && meta.type && woodSkuToSlugMap[meta.sku]) {
          const slug = woodSkuToSlugMap[meta.sku];
          this.woodGidToNameMap.set(slug, meta.type);
          isDev() &&
            console.log(
              `🔍 DEBUG: Wood mapping added - ${meta.sku} → ${slug} → ${meta.type}`
            );
        }
      });

      this.finishGidToNameMap = new Map();
      const finishSkuToSlugMap = {
        CHS: 'cherry-stain',
        MS: 'medium-stain',
        DS: 'dark-stain',
        CS: 'clear-stain',
        BS: 'burnt-coal-stain',
        UN: 'unfinished',
        BL: 'black',
        DD: 'dew-drop',
        DG: 'dorian-gray',
        HN: 'hale-navy',
        MN: 'monaco',
        NS: 'natural-stain',
        WW: 'white-washed-white-oak',
        PO: 'primer-only',
        SC: 'suman-clay',
        SS: 'suman-sand',
        WH: 'white',
        WG: 'willow-grey',
      };

      isDev() &&
        console.log(
          `🔍 DEBUG: finishMetaobjects.length = ${finishMetaobjects?.length || 0}`
        );
      finishMetaobjects.forEach((meta) => {
        const finishName = meta.finish || meta.type;
        if (meta.sku && finishName && finishSkuToSlugMap[meta.sku]) {
          const slug = finishSkuToSlugMap[meta.sku];
          this.finishGidToNameMap.set(slug, finishName);
          isDev() &&
            console.log(
              `🔍 DEBUG: Finish mapping added - ${meta.sku} → ${slug} → ${finishName}`
            );
        }
      });

      isDev() &&
        console.log(
          `🔍 DEBUG: Final map sizes - woodGidToNameMap: ${this.woodGidToNameMap.size}, finishGidToNameMap: ${this.finishGidToNameMap.size}`
        );
      isDev() &&
        console.log(`✅ PIPELINE: Material name maps ready for Layer 3`);
    },

    // ============================================================================
    // LAYER 3: MATERIAL RELATIONSHIPS
    // ============================================================================

    /**
     * Layer 3: Build material compatibility matrix from enhanced variants
     * Creates wood_sku to finish_sku relationship mappings (SKU-based filtering)
     */
    layer3_buildMaterialRelationships: function (virtualizedData) {
      if (bootstrapComplete) {
        throw new Error(
          'CRITICAL: layer3_buildMaterialRelationships() called AFTER bootstrap complete! Phase 1 only.'
        );
      }
      isDev() &&
        console.log(
          '🔄 LAYER 3: Building SKU-based material relationships from Layer 2 virtualized data'
        );

      // Extract enhanced variants from Layer 2 virtualized product structure
      let enhancedVariants = [];

      // PRIMARY SOURCE: Composite groups from Layer 2 (the actual display structure)
      if (
        virtualizedData.productData &&
        virtualizedData.productData.composite_groups
      ) {
        virtualizedData.productData.composite_groups.forEach((group) => {
          if (
            group.clustered_variants &&
            Array.isArray(group.clustered_variants)
          ) {
            const groupVariants = group.clustered_variants.filter(
              (v) => v.wood_sku || v.finish_sku
            );
            enhancedVariants.push(...groupVariants);
            isDev() &&
              console.log(
                `🧬 LAYER 3: Found ${groupVariants.length} material variants in composite group "${group.display_name}"`
              );
          }
        });
      }

      // SECONDARY SOURCE: Standalone products from Layer 2 (single-variant products)
      if (
        virtualizedData.productData &&
        virtualizedData.productData.standalone_products
      ) {
        virtualizedData.productData.standalone_products.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            const productVariants = product.variants.filter(
              (v) => v.wood_sku || v.finish_sku
            );
            enhancedVariants.push(...productVariants);
            isDev() &&
              console.log(
                `🧬 LAYER 3: Found ${productVariants.length} material variants in standalone product "${product.title}"`
              );
          }
        });
      }

      // FALLBACK: Use Layer 1 enhanced variants only if Layer 2 data is missing/empty
      if (enhancedVariants.length === 0) {
        isDev() &&
          console.log(
            '⚠️ LAYER 3: No variants found in Layer 2 data, falling back to Layer 1 enhanced variants'
          );
        if (
          virtualizedData.enhancedVariants &&
          Array.isArray(virtualizedData.enhancedVariants)
        ) {
          enhancedVariants = virtualizedData.enhancedVariants.filter(
            (v) => v.wood_sku || v.finish_sku
          );
          isDev() &&
            console.log(
              `🧬 LAYER 3: Found ${enhancedVariants.length} enhanced variants with material properties from Layer 1 fallback`
            );
        }
      }

      // Remove duplicates based on variant ID
      const uniqueVariants = [];
      const seenIds = new Set();
      enhancedVariants.forEach((variant) => {
        if (variant.id && !seenIds.has(variant.id)) {
          uniqueVariants.push(variant);
          seenIds.add(variant.id);
        }
      });

      isDev() &&
        console.log(
          `🧬 LAYER 3: Processing ${uniqueVariants.length} unique variants with material properties from Layer 2 virtualized data`
        );

      // Build SKU-based material relationships
      const materialRelationships =
        this.buildMaterialRelationshipsFromEnhancedVariants(uniqueVariants);

      // ============================================================================
      // LAYER 3 ENHANCEMENT: Pre-filter and tag material variants for O(1) access
      // ============================================================================

      // Tag material variants on composite groups
      if (
        virtualizedData.productData &&
        virtualizedData.productData.composite_groups
      ) {
        virtualizedData.productData.composite_groups.forEach((group) => {
          if (
            group.clustered_variants &&
            Array.isArray(group.clustered_variants)
          ) {
            group.materialVariants = group.clustered_variants.filter(
              (variant) => variant.wood_sku || variant.finish_sku
            );

            // Tag for quick identification
            group.materialVariants.forEach((v) => (v.isMaterialVariant = true));
          }
        });
      }

      // Tag material variants on standalone products
      if (
        virtualizedData.productData &&
        virtualizedData.productData.standalone_products
      ) {
        virtualizedData.productData.standalone_products.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.materialVariants = product.variants.filter(
              (variant) => variant.wood_sku || variant.finish_sku
            );

            // Tag for quick identification
            product.materialVariants.forEach(
              (v) => (v.isMaterialVariant = true)
            );
          }
        });
      }

      isDev() &&
        console.log('🏷️ PIPELINE Layer 3: Material variants tagged', {
          compositeGroupsWithMaterialVariants:
            virtualizedData.productData?.composite_groups?.filter(
              (g) => g.materialVariants?.length > 0
            ).length || 0,
          standaloneProductsWithMaterialVariants:
            virtualizedData.productData?.standalone_products?.filter(
              (p) => p.materialVariants?.length > 0
            ).length || 0,
        });

      // Add material relationships to the virtualized data structure
      const materialData = {
        ...virtualizedData,
        materialRelationships: materialRelationships,
      };

      isDev() &&
        console.log(
          `🎯 LAYER 3 COMPLETE: Built ${materialRelationships.size} material relationships from Layer 2 virtualized product structure`
        );

      return materialData;
    },

    // ============================================================================
    // LAYER 4: CATEGORY CLASSIFICATION
    // ============================================================================

    /**
     * Layer 4: Classify categories and generate display strategies
     * Auto-determines: LONELY_MULTI_VARIANT, MULTI_VARIANT_ONLY, MIX, SINGLE_VARIANT_ONLY
     */
    layer4_classifyCategories: function (materialData) {
      if (bootstrapComplete) {
        throw new Error(
          'CRITICAL: layer4_classifyCategories() called AFTER bootstrap complete! Phase 1 only.'
        );
      }
      isDev() &&
        console.log(
          '🔄 LAYER 4: Classifying categories and building final PPD'
        );

      // Extract material relationships from Layer 3
      const materialRelationships =
        materialData.materialRelationships || new Map();
      isDev() &&
        console.log(
          `🔄 LAYER 4: Received ${materialRelationships.size} material relationships from Layer 3`
        );

      // Initialize PPD structure
      const products = new Map();
      const categories = new Map();
      const tabStructure = new Map();
      const imageLayerStructure = {
        front: new Map(),
        back: new Map(),
      };

      // Extract products from Layer 3 virtualized data
      const allProducts = [];
      let compositeGroupCount = 0;
      let standaloneProductCount = 0;

      // Process composite groups (multi-variant products from Layer 2)
      if (
        materialData.productData &&
        materialData.productData.composite_groups
      ) {
        materialData.productData.composite_groups.forEach((group) => {
          compositeGroupCount++;

          // Create a unified product entry for the composite group
          const firstVariant =
            group.clustered_variants && group.clustered_variants.length > 0
              ? group.clustered_variants[0]
              : null;

          // CRITICAL FIX: Match composite-product-preprocessor.js generateGroupId() logic
          // Ensures consistent composite product IDs across both Phase 1 modules (preprocessor + data-pipeline)
          // This prevents category collisions where "Standard Shelves" in Top Pocket overwrites Bottom Pocket
          const categoryContext = group.category || 'Unknown';

          // Slugify category (same logic as preprocessor.slugify())
          const slugifyCategory = (text) =>
            text
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim();

          const categoryPrefix = categoryContext
            ? `${slugifyCategory(categoryContext)}-`
            : '';

          // Slugify display name (same logic as preprocessor.slugify())
          const slugifyDisplayName = group.display_name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();

          // Build composite ID with category prefix to prevent collisions
          const compositeId = `composite-${categoryPrefix}${slugifyDisplayName}`;

          const compositeProduct = {
            id: compositeId,
            title: group.display_name,
            display_name: group.display_name,
            type: 'CompositeProduct',
            'data-group-id': `group_${group.category.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${group.display_name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
            variants: group.clustered_variants || [],
            category: group.category,
            configurator_tab_category: group.configurator_tab_category || {
              category: group.category,
            },
            isMultiVariant:
              group.clustered_variants && group.clustered_variants.length > 1,
            // Add image from first variant for thumbnail display
            image: firstVariant?.featured_image?.src || null,
            // 🔥 OPTIMIZATION: Pre-compute primary IDs to eliminate runtime traversal in navigation manager
            // CRITICAL FIX: Reuse compositeId variable to prevent dual ID formats for same composite product
            primaryProductId: firstVariant?.source_product_id || compositeId,
            primaryVariantId: firstVariant?.id || null,
          };

          // 🔥 LAYER 2: Normalize and freeze composite product display_name (defensive pattern)
          if (!compositeProduct.display_name) {
            compositeProduct.display_name = compositeProduct.title;
          }
          Object.freeze(compositeProduct.display_name);

          // 🚀 OPTIMIZATION: Pre-build variant ID Set for O(1) sorting compatibility checks
          compositeProduct.variantIdSet = compositeProduct.variants
            ? new Set(compositeProduct.variants.map((v) => v.id))
            : new Set();
          compositeProduct.variantCount = compositeProduct.variantIdSet.size;

          allProducts.push(compositeProduct);
          products.set(compositeProduct.id, compositeProduct);

          // 🚀 LAYER 4: Pre-compute best variant for grouped product handler (O(1) lookup)
          const materialPreferences =
            window.MurphyDoorConfigurator?.stateManager?.getState?.()
              ?.materialPreferences;
          this._computeAndSetBestVariant(compositeProduct, materialPreferences);

          isDev() &&
            console.log(
              `🎯 LAYER 4: Pre-computed best variant for "${compositeProduct.display_name}" - score: ${compositeProduct.bestVariantByMaterialPreference.score} (variant: ${compositeProduct.bestVariantByMaterialPreference.variant?.id || 'none'})`
            );

          // 🔥 PPD VALIDATION: Ensure bestVariantByMaterialPreference was computed
          if (!compositeProduct.bestVariantByMaterialPreference) {
            throw new Error(
              `CRITICAL PPD VIOLATION: CompositeProduct "${compositeProduct.display_name}" (ID: ${compositeProduct.id}) ` +
                `missing bestVariantByMaterialPreference after _computeAndSetBestVariant() call. ` +
                `PPD Layer 4 MUST pre-compute material scores for ALL CompositeProducts during bootstrap. ` +
                `Check that materialPreferences is passed correctly to _computeAndSetBestVariant().`
            );
          }

          // Validate structure completeness
          if (
            !compositeProduct.bestVariantByMaterialPreference.variant ||
            typeof compositeProduct.bestVariantByMaterialPreference.score !==
              'number' ||
            !compositeProduct.bestVariantByMaterialPreference.scoredAt
          ) {
            // Helper to safely format the object for the error message (avoids circular reference crashes)
            const formatObjectForError = (obj) => {
              if (!obj) return 'null or undefined';
              return `score: ${obj.score}, scoredAt: ${obj.scoredAt}, variant.id: ${obj.variant?.id ?? 'undefined'}`;
            };
            throw new Error(
              `CRITICAL PPD VIOLATION: CompositeProduct "${compositeProduct.display_name}" (ID: ${compositeProduct.id}) ` +
                `has incomplete bestVariantByMaterialPreference structure. ` +
                `Expected: { variant: Object, score: number, scoredAt: number }, ` +
                `Got: { ${formatObjectForError(compositeProduct.bestVariantByMaterialPreference)} }`
            );
          }

          isDev() &&
            console.log(
              `🧩 LAYER 4: Processed composite group "${group.display_name}" with ${compositeProduct.variants.length} variants (category: ${compositeProduct.category})`
            );
          isDev() &&
            console.log(
              `🚀 LAYER 4 OPTIMIZATION: Pre-built variantIdSet for "${group.display_name}" with ${compositeProduct.variantCount} variant IDs`
            );
        });
      }

      // Process standalone products (single-variant products from Layer 2)
      if (
        materialData.productData &&
        materialData.productData.standalone_products
      ) {
        materialData.productData.standalone_products.forEach((product) => {
          standaloneProductCount++;

          // 🔥 PPD-FIRST FIX: Replace variants with their enhanced versions from Layer 1
          // This ensures standalone products have the same displayFormatting.cleanVariantName
          // pre-computed data as composite products (fixes zen precommit HIGH severity issue)
          const enhancedProductVariants = materialData.enhancedVariants.filter(
            (ev) => materialData.variantMap.get(ev.id)?.productId === product.id
          );

          // Use original product structure but normalize for PPD with enhanced variants
          const standaloneProduct = {
            ...product,
            // Overwrite variants if enhanced versions were found
            variants:
              enhancedProductVariants.length > 0
                ? enhancedProductVariants
                : product.variants,
            id: product.id.toString(), // 🔥 BUG FIX: Normalize ID to string to match Map key format
            type:
              (enhancedProductVariants.length > 0
                ? enhancedProductVariants
                : product.variants
              ).length > 1
                ? 'Multi-variant'
                : 'Single-variant',
            isMultiVariant:
              (enhancedProductVariants.length > 0
                ? enhancedProductVariants
                : product.variants
              ).length > 1,
            category: product.configurator_tab_category?.category || 'Unknown',
          };

          // 🚀 OPTIMIZATION: Pre-build variant ID Set for O(1) sorting compatibility checks
          standaloneProduct.variantIdSet = standaloneProduct.variants
            ? new Set(standaloneProduct.variants.map((v) => v.id))
            : new Set();
          standaloneProduct.variantCount = standaloneProduct.variantIdSet.size;

          allProducts.push(standaloneProduct);
          products.set(product.id.toString(), standaloneProduct);

          isDev() &&
            console.log(
              `🏠 LAYER 4: Processed standalone product "${product.title}" with ${standaloneProduct.variants?.length || 0} variants (category: ${standaloneProduct.category})`
            );
          isDev() &&
            console.log(
              `🚀 LAYER 4 OPTIMIZATION: Pre-built variantIdSet for "${product.title}" with ${standaloneProduct.variantCount} variant IDs`
            );
        });
      }

      isDev() &&
        console.log(
          `📊 LAYER 4: Total products processed: ${allProducts.length} (${compositeGroupCount} composite groups + ${standaloneProductCount} standalone products)`
        );

      // Group products by category for classification
      const productsByCategory = new Map();
      allProducts.forEach((product) => {
        const category = product.category || 'Unknown';
        if (!productsByCategory.has(category)) {
          productsByCategory.set(category, []);
        }
        productsByCategory.get(category).push(product);
      });

      isDev() &&
        console.log(
          `🗂️ LAYER 4: Found ${productsByCategory.size} categories to classify`
        );

      // 🚀 LAYER 4: Build Product-to-Category Map
      isDev() && console.log('🔄 LAYER 4: Building product-to-category map');

      const productIdToCategoryMap = new Map();

      allProducts.forEach((product) => {
        productIdToCategoryMap.set(product.id.toString(), product.category);
      });

      Object.freeze(productIdToCategoryMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built productIdToCategoryMap - ${productIdToCategoryMap.size} entries (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Product category lookup reduced from object traversal to O(1) map access`
        );

      // 🔥 CRITICAL FIX: Calculate displayMode for CompositeProducts based on category product count
      // This must happen AFTER productsByCategory is built but BEFORE classification
      // Uses centralized _calculateDisplayMode helper for consistent logic across pipelines
      allProducts.forEach((product) => {
        if (product.type === 'CompositeProduct') {
          const categoryProducts =
            productsByCategory.get(product.category) || [];

          // Use centralized helper for consistent two-step detection
          product.displayMode = this._calculateDisplayMode(
            product,
            categoryProducts.length
          );

          isDev() &&
            console.log(
              `🎯 LAYER 4: CompositeProduct "${product.title}" in category "${product.category}" has displayMode: ${product.displayMode} (${product.variants.length} variants, ${categoryProducts.length} products in category)`
            );
        }
      });

      // Classify each category
      productsByCategory.forEach((categoryProducts, categoryName) => {
        const classification = this.classifyCategory(
          categoryProducts,
          categoryName
        );

        // Find the category config to get its load order and media z-index
        const categoryConfig = materialData.configData.tabCategories.find(
          (cat) => cat.category === categoryName
        );
        classification.loadOrder = categoryConfig?.load_order || 0;
        classification.mediaZIndex = categoryConfig?.media_z_index || 0;
        // Measurement-mode classification (configurator_tab_categories.layer_role).
        // Null for every record until the metaobject is populated, which is why
        // md-config-bl-dimension-calculator.js falls back to a literal list --
        // shipping the reader first means populating the field needs no deploy.
        classification.layerRole = categoryConfig?.layer_role || null;
        // Admin-owned layer shift rule (configurator_tab_categories.layer_shift_rule).
        // Shifts THIS category's image layer when a parent category's selection changes
        // — e.g. the Book Latch book moves to the far side of the shelf on a left-hand
        // door. Read by md-config-bl-image-layer-coordinator.js (computeAdminLayerShift).
        // Null when unpopulated, which is a no-op, so a category without a rule costs
        // nothing and no deploy is needed to add one later.
        classification.layerShiftRule = categoryConfig?.layer_shift_rule || null;

        // 🚀 BUILD displayElements with thumbnails for state manager
        const thumbnails = [];
        categoryProducts.forEach((product) => {
          thumbnails.push({
            id: product.id,
            category: categoryName,
            image: {
              src: product.featured_image?.src || product.image || '',
              alt: product.featured_image?.alt || product.title,
            },
            title: product.display_name,
            price: product.price,
          });
        });

        // Build thumbnailMap for O(1) lookups
        const thumbnailMap = new Map();
        thumbnails.forEach((thumbnail) => {
          thumbnailMap.set(thumbnail.id, thumbnail);
        });
        Object.freeze(thumbnailMap);

        classification.displayElements = {
          thumbnails: thumbnails,
          thumbnailMap: thumbnailMap,
        };

        categories.set(categoryName, classification);

        isDev() &&
          console.log(
            `🎯 LAYER 4: Category "${categoryName}" classified as ${classification.type} with loadOrder ${classification.loadOrder} (${categoryProducts.length} products: ${classification.multiVariantCount} multi-variant, ${classification.singleVariantCount} single-variant)`
          );
      });

      // ⚡ PRIORITY 3: Build categoryTypeClassificationMap for O(1) type lookups
      // Purpose: Fast lookup of category classification types (MVC vs SVC)
      // Use: When code needs just the classification type, not full classification object
      const categoryTypeClassificationMap = new Map();
      categories.forEach((classification, categoryName) => {
        categoryTypeClassificationMap.set(categoryName, classification.type);
      });
      isDev() &&
        console.log(
          `⚡ LAYER 4 (Priority 3): Built categoryTypeClassificationMap with ${categoryTypeClassificationMap.size} category type lookups`
        );

      // 🚀 ANTI-PATTERN FIX #3: Build dimensionCategoryIndex for O(1) dimension category lookups
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building dimensionCategoryIndex for O(1) dimension category classification'
        );

      // ⚠️ CRITICAL CONFIGURATION DEPENDENCY ⚠️
      // The string values below ('Width', 'Door Depth', 'Door Handing') MUST EXACTLY
      // match the category names configured in the Shopify backend (allRequiredCategoryIdsArray).
      // If these names are changed in Shopify, this index will break silently, causing
      // dimension calculations to fail. Consider moving this to a configuration file
      // or metaobject in the future to decouple it from the codebase.
      //
      // VERIFIED AGAINST: data-pipeline sorting config (lines ~538-545):
      //   'Door Depth': sortNumericDimensions
      //   'Door Handing': sortStandardFirst
      const dimensionCategoryIndex = Object.freeze({
        // Direct lookup helpers (O(1) direct access)
        widthCategory: 'Width',
        depthCategory: 'Door Depth',
        handlingCategory: 'Door Handing',
        // Jamb depth drives in-swing wall clearance (engineering sheet B23,
        // rev. 2026-09-25). Additive: not one of the space calculator's three
        // readiness categories -- an absent jamb falls back to the standard
        // 4-9/16in rather than blocking the calculation.
        jambCategory: 'Jamb Depth',
      });

      isDev() &&
        console.log(
          '✅ LAYER 4: Built dimensionCategoryIndex with pre-classified dimension categories (IMMUTABLE)'
        );
      isDev() &&
        console.log(
          '🚀 PERFORMANCE BOOST: Eliminated O(n×m) category iteration - now O(1) direct access!'
        );

      // Build tab structure from Layer 3 data with proper GID resolution
      if (
        materialData.productData &&
        materialData.productData.original_products
      ) {
        // Extract config data for tab metaobject resolution
        const configData = JSON.parse(
          document.getElementById('murphy-door-config-data').textContent
        );

        // Build category → load_order map for sorting categories within tabs
        const categoryLoadOrderMap = new Map();
        if (configData.tabCategories) {
          configData.tabCategories.forEach((catData) => {
            categoryLoadOrderMap.set(catData.category, catData.load_order);
          });
        }

        const tabMap = new Map(); // Temporary map to collect categories per tab

        // First pass: collect all categories for each tab
        materialData.productData.original_products.forEach((product) => {
          if (product.configurator_tab_category) {
            const tabGid = product.configurator_tab_category.configurator_tab;
            const category = product.configurator_tab_category.category;

            if (!tabMap.has(tabGid)) {
              tabMap.set(tabGid, {
                gid: tabGid,
                categories: [],
                categoryData: [],
              });
            }

            const tabData = tabMap.get(tabGid);
            if (!tabData.categories.includes(category)) {
              tabData.categories.push(category);
              tabData.categoryData.push({
                category: category,
                media_z_index:
                  product.configurator_tab_category.media_z_index || 1,
              });
            }
          }
        });

        // Second pass: resolve GIDs to full tab metaobjects and build final structure
        tabMap.forEach((tempTabData, tabGid) => {
          // Extract numeric ID from GID (e.g., "gid://shopify/Metaobject/94796480557" -> "94796480557")
          const numericIdMatch = tabGid.match(/\/(\d+)$/);
          if (numericIdMatch) {
            const numericId = parseInt(numericIdMatch[1], 10);

            // Find the tab metaobject in configData
            const tabMetaobject = configData.configuratorTabs.find(
              (t) => t.id === numericId
            );

            if (tabMetaobject) {
              // Sort categories by load_order within this tab
              const sortedCategories = [...tempTabData.categories].sort(
                (a, b) => {
                  const orderA = categoryLoadOrderMap.get(a) ?? 999;
                  const orderB = categoryLoadOrderMap.get(b) ?? 999;
                  return orderA - orderB;
                }
              );

              tabStructure.set(tabGid, {
                id: tabGid, // Use GID as ID
                name: tabMetaobject.tab_name, // ✅ Changed from tab_name to name
                loadOrder: tabMetaobject.load_order, // ✅ Changed from load_order to loadOrder
                handle: tabMetaobject.handle,
                categories: sortedCategories, // Use sorted categories
                categoryData: tempTabData.categoryData,
              });
            }
          }
        });

        isDev() &&
          console.log(
            `📑 LAYER 4: Built tab structure with ${tabStructure.size} tabs`
          );
        if (tabStructure.size > 0) {
          isDev() &&
            console.log(
              '  📋 Tabs:',
              Array.from(tabStructure.values())
                .map((t) => t.name)
                .join(', ')
            );
        }
      }

      // 🔥 PPD LAYER 4: Category → Parent Tab Mapping (O(1) lookups)
      const categoryToParentTabMap = new Map();
      tabStructure.forEach((tabData, tabId) => {
        if (tabData.categories && Array.isArray(tabData.categories)) {
          tabData.categories.forEach((categoryId) => {
            categoryToParentTabMap.set(categoryId, tabId);
          });
        }
      });

      isDev() &&
        console.log(
          `✅ PPD LAYER 4: Built categoryToParentTabMap with ${categoryToParentTabMap.size} mappings`
        );

      // 🚀 ANTI-PATTERN #2 FIX: Pre-sort tabs and categories by loadOrder (Phase 1 heavy lifting)
      isDev() &&
        console.log(
          '🔄 PPD LAYER 4: Pre-sorting tabs and categories by loadOrder for O(1) navigation'
        );

      // Sort tabs by loadOrder
      const sortedTabsByLoadOrder = Array.from(tabStructure.entries()).sort(
        (a, b) => (a[1].loadOrder || 0) - (b[1].loadOrder || 0)
      );

      // Build sorted category lists per tab
      const tabToSortedCategoryIds = new Map();
      tabStructure.forEach((tabData, tabId) => {
        if (tabData.categories && Array.isArray(tabData.categories)) {
          const sortedCategories = [...tabData.categories].sort((a, b) => {
            const catA = categories.get(a);
            const catB = categories.get(b);
            return (catA?.loadOrder || 0) - (catB?.loadOrder || 0);
          });
          tabToSortedCategoryIds.set(tabId, sortedCategories);
        }
      });

      isDev() &&
        console.log(
          `✅ PPD LAYER 4: Pre-sorted ${sortedTabsByLoadOrder.length} tabs, ${tabToSortedCategoryIds.size} category lists (Anti-Pattern #2 fix)`
        );

      // 🚀 LAYER 4: Pre-sort categories by displayOrder for navigation rendering
      isDev() &&
        console.log(
          '🔄 LAYER 4: Pre-sorting categories by displayOrder for navigation'
        );

      // First, normalize displayOrder for all categories.
      // Score = tabLoadOrder * 100 + categoryLoadOrder so cross-tab ordering is correct
      // when multiple tabs' categories are shown simultaneously (e.g. simplified mode).
      // Within a single tab all categories share the same tabLoadOrder, so relative
      // within-tab ordering is unchanged for normal tab views.
      categories.forEach((categoryData, categoryId) => {
        if (
          categoryData.displayOrder === undefined ||
          categoryData.displayOrder === null
        ) {
          if (
            categoryData.loadOrder !== undefined &&
            categoryData.loadOrder !== null
          ) {
            const tabGid = categoryToParentTabMap.get(categoryId);
            const tabLoadOrder =
              tabGid != null
                ? (tabStructure.get(tabGid)?.loadOrder ?? 999)
                : 999;
            categoryData.displayOrder =
              tabLoadOrder * 100 + categoryData.loadOrder;
          } else {
            isDev() &&
              console.warn(
                `⚠️ LAYER 4: Category "${categoryId}" missing displayOrder and loadOrder, assigning fallback ${UNKNOWN_CATEGORY_SORT_ORDER}`
              );
            categoryData.displayOrder = UNKNOWN_CATEGORY_SORT_ORDER;
          }
        }
      });

      // Then, create sorted array for navigation manager
      const sortedCategoriesArray = Array.from(categories.entries()).sort(
        ([, catA], [, catB]) => {
          return catA.displayOrder - catB.displayOrder;
        }
      );

      Object.freeze(sortedCategoriesArray);

      isDev() &&
        console.log(
          `✅ LAYER 4: Pre-sorted ${sortedCategoriesArray.length} categories by displayOrder for navigation rendering (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Navigation rendering eliminated O(n log n) sorting, using O(1) array access`
        );

      // 🔥 PPD LAYER 4: Build category display order map for fast sorting
      // This eliminates O(n) Map.get() lookups during O(n log n) sort comparisons in showCategoriesForTab()
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryDisplayOrder map for O(1) sort comparisons'
        );
      const categoryDisplayOrder = new Map();
      Array.from(categories.entries())
        .sort((a, b) => a[1].displayOrder - b[1].displayOrder)
        .forEach(([categoryId], index) => {
          categoryDisplayOrder.set(categoryId, index);
        });

      Object.freeze(categoryDisplayOrder);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryDisplayOrder - ${categoryDisplayOrder.size} categories mapped to display order (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: showCategoriesForTab() eliminated Map.get() calls during sorting (~20 calls/page × 20 categories)`
        );

      // 🚀 LAYER 4: Build progressMaps structure for progress-tracker
      // Pre-built, immutable lookup maps that eliminate runtime iterations and enable O(1) lookups
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building progressMaps structure for O(1) progress tracking'
        );

      // Initialize progressMaps structure - pre-built, immutable lookup maps
      const progressMaps = {
        categoryToTabMap: new Map(), // Map<categoryId, tabId> - Reverse lookup to find which tab a category belongs to
        tabCategoriesMap: new Map(), // Map<tabId, categoryId[]> - Pre-extracted category arrays per tab (frozen)
        tabCategoryCounts: new Map(), // Map<tabId, count> - Pre-computed category counts per tab
        totalCategoryCount: 0, // Total category count for progress percentage calculations
      };

      // Build all maps in a single iteration for efficiency
      tabStructure.forEach((tabData, tabId) => {
        const categories = tabData.categories || [];
        const categoryCount = categories.length;

        // Store frozen category array for this tab (immutable)
        progressMaps.tabCategoriesMap.set(
          tabId,
          Object.freeze([...categories])
        );

        // Store category count for this tab
        progressMaps.tabCategoryCounts.set(tabId, categoryCount);
        progressMaps.totalCategoryCount += categoryCount;

        // Build reverse lookup: category → tab
        categories.forEach((categoryId) => {
          progressMaps.categoryToTabMap.set(categoryId, tabId);
        });
      });

      // Freeze the progressMaps structure to prevent mutations.
      // Note: This is a shallow freeze. The Maps themselves can still be mutated,
      // but the PPD contract assumes consumers will treat them as read-only.
      Object.freeze(progressMaps);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built progressMaps structure - ${progressMaps.categoryToTabMap.size} category-to-tab mappings, ` +
            `${progressMaps.tabCategoryCounts.size} tab category counts, ${progressMaps.tabCategoriesMap.size} tab category arrays, ` +
            `${progressMaps.totalCategoryCount} total categories (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Progress tracking complexity reduced from O(n) iterations to O(1) lookups`
        );

      // 🔄 LAYER 4: Extract base product for composite product shipping fallback
      // Must be extracted BEFORE productMaxShippingTimeMap building
      // The base product is the root-level productData object itself (has id, title, production_time, etc.)
      // CRITICAL: Include .variant property for backward compatibility with price-calculator.js
      const rawProductData = materialData.productData || {};
      const baseProduct = rawProductData
        ? {
            ...rawProductData,
            variant: (rawProductData.variants || [])[0] || null,
          }
        : null;

      // ⏱️ LEAD TIME SINGLE SOURCE OF TRUTH: when the base product links a
      // `lead_time_category` metaobject, its UNFINISHED range replaces the legacy
      // production_time_* metafields as the base door time, and `lead_time`
      // carries the finished range plus a per-variant classification of every
      // Wood & Finish variant (raw Shopify variant ids, which Layer 2 clustering
      // preserves). The shipping calculator swaps to the finished range when a
      // paint/stain variant is selected and ignores those variants' own legacy
      // production_time. No metaobject -> nothing changes (legacy fallback).
      if (baseProduct) {
        const leadTime = buildBaseLeadTime(rawProductData);
        if (leadTime) {
          baseProduct.production_time = { ...leadTime.unfinished };
          baseProduct.lead_time = leadTime;
        }
        baseProduct.shipping_transit_days = normalizeTransitDays(
          rawProductData.shipping_transit_days
        );
      }

      if (baseProduct && baseProduct.production_time) {
        isDev() &&
          console.log(
            `🏠 LAYER 4: Base product extracted: "${baseProduct.title}" (production time: ${baseProduct.production_time.low || 0}-${baseProduct.production_time.high || 0} weeks)`
          );
      } else {
        isDev() &&
          console.warn(
            '⚠️ LAYER 4: No base product found - composite product shipping fallback unavailable'
          );
      }

      // 🚀 LAYER 4: Build variantShippingTimeMap for O(1) variant shipping lookups
      // Eliminates O(n×m) search in shipping calculator (~200ms → ~0.5ms per lookup)
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building variantShippingTimeMap for O(1) variant→shippingTime lookups'
        );

      const variantShippingTimeMap = new Map();

      allProducts.forEach((product) => {
        const productTime = product.production_time || { low: 0, high: 0 };

        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach((variant) => {
            // Check both productionTime and production_time to handle inconsistencies in the source data model
            const variantTime = variant.productionTime ||
              variant.production_time || { low: 0, high: 0 };

            // Pre-calculate the MAX of product + variant times
            const maxTime = {
              low: Math.max(productTime.low || 0, variantTime.low || 0),
              high: Math.max(productTime.high || 0, variantTime.high || 0),
            };

            variantShippingTimeMap.set(variant.id, maxTime);
          });
        }
      });

      Object.freeze(variantShippingTimeMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built variantShippingTimeMap - ${variantShippingTimeMap.size} variant→shippingTime mappings (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Variant shipping lookups reduced from O(n×m) search to O(1) Map.get() (~99% faster)`
        );

      // 🚀 LAYER 4: Build productMaxShippingTimeMap for O(1) product shipping lookups
      // Eliminates O(v) variant iteration (~10ms → ~0.1ms per lookup)
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building productMaxShippingTimeMap for O(1) product→maxShippingTime lookups'
        );

      const productMaxShippingTimeMap = new Map();

      allProducts.forEach((product) => {
        const times = [];

        // Base product time
        if (product.production_time) {
          times.push({
            low: product.production_time.low || 0,
            high: product.production_time.high || 0,
          });
        }

        // All variant times
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach((variant) => {
            const variantTime =
              variant.productionTime || variant.production_time;
            if (variantTime) {
              times.push({
                low: variantTime.low || 0,
                high: variantTime.high || 0,
              });
            }
          });
        }

        // Pre-calculate MAX across product + all variants
        const maxTime = {
          low: Math.max(...times.map((t) => t.low), 0),
          high: Math.max(...times.map((t) => t.high), 0),
        };

        // 🚀 PPD-FIRST FIX: Handle composite product fallback in Phase 1
        // If composite product has no shipping time (0,0), use base product's time
        if (
          product.type === 'CompositeProduct' &&
          maxTime.low === 0 &&
          maxTime.high === 0
        ) {
          if (baseProduct?.production_time) {
            maxTime.low = baseProduct.production_time.low || 0;
            maxTime.high = baseProduct.production_time.high || 0;
            isDev() &&
              console.log(
                `🔄 LAYER 4: Composite product "${product.title}" using baseProduct shipping time fallback`
              );
          }
        }

        // Enforce consistent STRING key type for reliable lookups (handles both numeric and composite IDs)
        productMaxShippingTimeMap.set(String(product.id), maxTime);
      });

      Object.freeze(productMaxShippingTimeMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built productMaxShippingTimeMap - ${productMaxShippingTimeMap.size} product→maxShippingTime mappings (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Product shipping lookups reduced from O(v) iteration to O(1) Map.get() (~99% faster)`
        );

      // 🚀 LAYER 4 PRIORITY 1: Build grouping maps for O(1) product group lookups
      // Pre-built maps eliminate runtime iterations for display_name grouping
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building Priority 1 grouping maps for O(1) product group lookups'
        );

      const displayNameToProductIdsMap = new Map(); // Map<displayName, productId[]>
      const categoryToGroupsMap = new Map(); // Map<categoryId, Set<displayName>>

      allProducts.forEach((product) => {
        if (product.display_name) {
          const groupName = product.display_name.trim();

          // Build displayName → productIds mapping
          if (!displayNameToProductIdsMap.has(groupName)) {
            displayNameToProductIdsMap.set(groupName, []);
          }
          displayNameToProductIdsMap.get(groupName).push(product.id);

          // Build category → groups mapping
          if (product.category) {
            if (!categoryToGroupsMap.has(product.category)) {
              categoryToGroupsMap.set(product.category, new Set());
            }
            categoryToGroupsMap.get(product.category).add(groupName);
          }
        }
      });

      isDev() &&
        console.log(
          `✅ LAYER 4 PRIORITY 1: Built grouping maps - ${displayNameToProductIdsMap.size} display name groups, ` +
            `${categoryToGroupsMap.size} categories with groups`
        );

      // 🚀 LAYER 4: Build categoryToProductsMap for O(1) category lookups
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryToProductsMap for O(1) category→products lookups'
        );

      const categoryToProductsMap = new Map();

      for (const [, product] of products.entries()) {
        const categoryId = product.category;
        if (!categoryId) continue;

        if (!categoryToProductsMap.has(categoryId)) {
          categoryToProductsMap.set(categoryId, []);
        }
        categoryToProductsMap.get(categoryId).push(product);
      }

      // UX OPTIMIZATION: Sort products within each category for optimal user experience
      // Sorting happens in-place BEFORE freezing for zero runtime cost
      // Principles: "No [Category]" first, price-based within tiers, simple -> complex
      isDev() &&
        console.log(
          '🔄 LAYER 4: Applying universal product sorting (price-optimized) to all categories'
        );
      for (const [categoryId, productList] of categoryToProductsMap.entries()) {
        sortProductsForCategory(categoryId, productList);
        isDev() &&
          console.log(
            `  ✅ Sorted ${productList.length} products in "${categoryId}"`
          );
      }

      // Freeze arrays to prevent mutation (NOW SORTED!)
      for (const [categoryId, productList] of categoryToProductsMap.entries()) {
        categoryToProductsMap.set(categoryId, Object.freeze(productList));
      }

      Object.freeze(categoryToProductsMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryToProductsMap - ${categoryToProductsMap.size} categories with pre-built product lists (IMMUTABLE)`
        );

      // 🎨 LAYER 4: Build material-aware products maps for O(1) lookups
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building material-aware products maps for image optimization'
        );

      const categoryToMaterialAwareProductsMap = new Map();
      const categoryToProductsWithImagesMap = new Map();

      categories.forEach((categoryData, categoryName) => {
        const categoryProducts = categoryToProductsMap.get(categoryName) || [];

        // Material-aware products with specialized images
        const materialAwareProducts = categoryProducts.filter((product) => {
          const hasMaterialData =
            product.materialPreferences || product.materialRelationships;
          return hasMaterialData;
        });

        categoryToMaterialAwareProductsMap.set(
          categoryName,
          Object.freeze({
            front: Object.freeze(
              materialAwareProducts.filter(
                (p) =>
                  p.configuratorFrontImage ||
                  p.variants?.some((v) => v.configuratorFrontImage)
              )
            ),
            back: Object.freeze(
              materialAwareProducts.filter(
                (p) =>
                  p.configuratorBackImage ||
                  p.variants?.some((v) => v.configuratorBackImage)
              )
            ),
          })
        );

        // Products with images (any images)
        categoryToProductsWithImagesMap.set(
          categoryName,
          Object.freeze({
            front: Object.freeze(
              categoryProducts.filter(
                (p) =>
                  p.configuratorFrontImage ||
                  p.variants?.some((v) => v.configuratorFrontImage)
              )
            ),
            back: Object.freeze(
              categoryProducts.filter(
                (p) =>
                  p.configuratorBackImage ||
                  p.variants?.some((v) => v.configuratorBackImage)
              )
            ),
          })
        );
      });

      Object.freeze(categoryToMaterialAwareProductsMap);
      Object.freeze(categoryToProductsWithImagesMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built material-aware product maps - ${categoryToMaterialAwareProductsMap.size} categories with material products, ` +
            `${categoryToProductsWithImagesMap.size} categories with images (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Material-aware image lookups complexity reduced from O(n) iterations to O(1) lookups`
        );

      // 🚀 LAYER 4 (ANTI-PATTERN #2 FIX): Build categoryToInitialLayerStateMap for O(1) initial layer state lookups
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryToInitialLayerStateMap for image layer initialization'
        );

      const categoryToInitialLayerStateMap = new Map();

      categories.forEach((categoryData, categoryId) => {
        // Find first product with images for this category
        const categoryProducts = categoryToProductsMap.get(categoryId) || [];

        // Find first variant with configurator images (images are on variants, not products)
        let frontImage = null;
        let backImage = null;

        for (const product of categoryProducts) {
          if (product.variants && Array.isArray(product.variants)) {
            const variantWithImages = product.variants.find(
              (v) => v.configurator_front_image || v.configurator_back_image
            );
            if (variantWithImages) {
              frontImage = variantWithImages.configurator_front_image || null;
              backImage = variantWithImages.configurator_back_image || null;
              break;
            }
          }
        }

        if (frontImage || backImage) {
          // Get the category config for z-index
          const categoryConfig = materialData.configData?.tabCategories?.find(
            (cat) => cat.category === categoryId
          );

          categoryToInitialLayerStateMap.set(categoryId, {
            state: 'initial',
            images: {
              front: frontImage,
              back: backImage,
            },
            zIndex: categoryConfig?.media_z_index || 0,
          });
        }
      });

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryToInitialLayerStateMap - ${categoryToInitialLayerStateMap.size} categories with initial layer states (ANTI-PATTERN #2 FIX)`
        );

      // Freeze for immutability (prevent accidental runtime mutations)
      Object.freeze(categoryToInitialLayerStateMap);

      // 🚀 LAYER 4: Build variantIdToVariantMap for O(1) variant lookups
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building variantIdToVariantMap for O(1) variant→object lookups'
        );

      const variantIdToVariantMap = new Map();

      for (const [productId, product] of products.entries()) {
        if (product.variants && product.variants.length > 0) {
          for (const variant of product.variants) {
            if (variant.id) {
              // Store variant with parent product reference
              variantIdToVariantMap.set(
                variant.id,
                Object.freeze({
                  ...variant,
                  productId: productId,
                })
              );
            }
          }
        }
      }

      Object.freeze(variantIdToVariantMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built variantIdToVariantMap - ${variantIdToVariantMap.size} variants indexed for O(1) lookups (IMMUTABLE)`
        );

      // 🚀 LAYER 4 (ANTI-PATTERN #1 FIX): Build variantIdToVariantObjectMap for O(1) variant lookups with product context
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building variantIdToVariantObjectMap for O(1) variant→object lookups with product metadata'
        );

      const variantIdToVariantObjectMap = new Map();

      for (const [productId, product] of products.entries()) {
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach((variant) => {
            if (variant.id) {
              variantIdToVariantObjectMap.set(variant.id, {
                ...variant,
                productId: productId,
                category: product.category,
                productTitle: product.title,
              });
            }
          });
        }
      }

      isDev() &&
        console.log(
          `✅ LAYER 4: Built variantIdToVariantObjectMap - ${variantIdToVariantObjectMap.size} variants with product context (ANTI-PATTERN #1 FIX)`
        );

      // Freeze for immutability (prevent accidental runtime mutations)
      Object.freeze(variantIdToVariantObjectMap);

      // 🚀 LAYER 4: Build categoryDisplayModeMap for pre-classified display strategies
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryDisplayModeMap for O(1) display mode lookups'
        );

      const categoryDisplayModeMap = new Map();

      for (const [categoryId, categoryData] of categories.entries()) {
        // Get products for this category (using the map we just built!)
        const categoryProducts = categoryToProductsMap.get(categoryId) || [];

        // ✅ FIX: For LONELY_MULTI_VARIANT categories, hasDirectVariant should be true
        // and directVariantProductId should be the single multi-variant product
        const isLonelyMultiVariant =
          categoryData.type === 'LONELY_MULTI_VARIANT';
        const directVariantProduct = isLonelyMultiVariant
          ? categoryProducts.find(
              (p) => p.type === 'CompositeProduct' || p.isMultiVariant
            )
          : null;

        categoryDisplayModeMap.set(
          categoryId,
          Object.freeze({
            hasDirectVariant: isLonelyMultiVariant,
            directVariantProductId: directVariantProduct?.id || null,
            displayMode: categoryData.displayMode || categoryData.type, // ✅ FIX: Add displayMode for UI updater
            displayStrategy: categoryData.type, // ✅ FIX: Use .type instead of .classification
            productCount: categoryProducts.length,
          })
        );
      }

      Object.freeze(categoryDisplayModeMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryDisplayModeMap - ${categoryDisplayModeMap.size} categories pre-classified (IMMUTABLE)`
        );

      // 🚀 LAYER 4 PRIORITY 2: Build UI-ready product groups data structures
      // Pre-computed group metadata with price ranges and merged variants
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building Priority 2 UI-ready product groups data'
        );

      const uiReadyProductGroupsMap = new Map(); // Map<groupName, uiReadyGroupData>
      const groupIdToGroupDataMap = new Map(); // Map<groupId, groupData>

      // Helper function to calculate price range from variants
      const calculatePriceRange = (variants) => {
        if (!variants || !Array.isArray(variants) || variants.length === 0) {
          return null;
        }

        const prices = variants
          .map((variant) => {
            const price = variant.price || variant.price_cents;
            return typeof price === 'string' ? parseFloat(price) : price;
          })
          .filter((price) => !isNaN(price) && price >= 0);

        if (prices.length === 0) {
          return null;
        }

        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        return {
          min: minPrice,
          max: maxPrice,
          range: maxPrice - minPrice,
        };
      };

      // Helper function to merge variants from multiple products
      const mergeProductVariants = (products) => {
        const mergedVariants = [];
        const variantIds = new Set();

        products.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              if (variant && !variantIds.has(variant.id)) {
                variantIds.add(variant.id);
                mergedVariants.push({
                  ...variant,
                  sourceProductId: product.id,
                  sourceProductHandle: product.handle,
                  sourceProductTitle: product.title,
                  groupDisplayName: product.display_name,
                });
              }
            });
          }
        });

        return mergedVariants;
      };

      // Process display name groups into UI-ready format
      displayNameToProductIdsMap.forEach((productIds, groupName) => {
        // Get full product objects for this group using O(1) Map lookup
        const groupProducts = productIds
          .map((id) => products.get(id.toString()))
          .filter((p) => p);

        if (groupProducts.length === 0) return;

        // Determine if this is an actual group (2+ products) or single product
        const isActualGroup = groupProducts.length > 1;
        const primaryProduct = groupProducts[0];

        if (isActualGroup) {
          // Create UI-ready group data for actual groups
          const mergedVariants = mergeProductVariants(groupProducts);
          const groupId = `group_${groupName.replace(/\s+/g, '-').toLowerCase()}`;

          const groupData = {
            isGroup: true,
            groupId: groupId,
            groupName: groupName,
            displayName: groupName,
            productCount: groupProducts.length,
            variantCount: mergedVariants.length,
            products: groupProducts,
            mergedVariants: mergedVariants,
            primaryProduct: primaryProduct,
            // UI properties
            thumbnailImageSrc:
              primaryProduct.image_src || primaryProduct.featured_image || null,
            thumbnailImageAlt: `${groupName} product group`,
            price: primaryProduct.price || null,
            priceRange: calculatePriceRange(mergedVariants),
            // Metadata
            category: primaryProduct.category || null,
          };

          uiReadyProductGroupsMap.set(groupName, groupData);
          groupIdToGroupDataMap.set(groupId, groupData);
        }
      });

      // Process individual products without display_name
      allProducts.forEach((product) => {
        if (!product.display_name) {
          const individualKey = `individual_${product.id}`;
          const individualData = {
            isGroup: false,
            groupId: null,
            groupName: null,
            displayName: product.title || product.handle,
            productCount: 1,
            variantCount: product.variants ? product.variants.length : 0,
            products: [product],
            mergedVariants: product.variants || [],
            primaryProduct: product,
            // UI properties
            thumbnailImageSrc:
              product.image_src || product.featured_image || null,
            thumbnailImageAlt: product.title || product.handle,
            price: product.price || null,
            priceRange: product.variants
              ? calculatePriceRange(product.variants)
              : null,
            // Metadata
            category: product.category || null,
          };

          uiReadyProductGroupsMap.set(individualKey, individualData);
        }
      });

      isDev() &&
        console.log(
          `✅ LAYER 4 PRIORITY 2: Built UI-ready group data - ${uiReadyProductGroupsMap.size} total groups/individuals, ` +
            `${groupIdToGroupDataMap.size} actual groups with 2+ products`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Product grouping complexity reduced from O(n) runtime processing to O(1) lookups`
        );

      // Build image layer structure from original product data
      this.buildImageLayerStructure(
        materialData.productData,
        imageLayerStructure
      );

      // ═══════════════════════════════════════════════════════
      // ✅ Layer 4: Load Unified Forbidden Combination Rules (GUI-Friendly)
      // ═══════════════════════════════════════════════════════
      // MODERNIZED: Legacy forbiddenRulesById and itemToForbiddenRuleIds Maps REMOVED.
      // All forbidden combination validation uses the unified metaobject-driven system exclusively.
      isDev() &&
        console.log(
          '🚫 LAYER 4: Loading unified forbidden combination rules...'
        );

      const unifiedRulesData = this._loadUnifiedForbiddenRules(materialData);

      // PRE-PARTITION: Split unified rules into conflict rules and auto-apply rules at bootstrap.
      // This eliminates 984 runtime "Skipping AUTO_APPLY_OPTION" log entries per session
      // by giving conflict validation a pre-filtered map to iterate.
      const conflictRules = new Map();
      const autoApplyRules = new Map();
      unifiedRulesData.rules.forEach(function (rule, ruleId) {
        if (rule.targetAction === 'AUTO_APPLY_OPTION') {
          autoApplyRules.set(ruleId, rule);
        } else {
          conflictRules.set(ruleId, rule);
        }
      });

      const forbiddenCombinationMaps = {
        unifiedConditionGroups: unifiedRulesData.conditionGroups,
        unifiedRules: unifiedRulesData.rules,
        conflictRules: conflictRules, // Pre-partitioned: conflict rules only (excludes AUTO_APPLY_OPTION)
        autoApplyRules: autoApplyRules, // Pre-partitioned: AUTO_APPLY_OPTION rules only
      };

      isDev() &&
        console.log('✅ LAYER 4.7: Forbidden rules structure', {
          unifiedConditionGroupCount: unifiedRulesData.conditionGroups.size,
          unifiedRuleCount: unifiedRulesData.rules.size,
          conflictRuleCount: conflictRules.size,
          autoApplyRuleCount: autoApplyRules.size,
        });

      // ✅ baseProduct already extracted earlier in Layer 4 (line ~1290) for shipping map usage
      // Reusing the same baseProduct reference for consistency

      // Build category fallback images map for Phase 2 modules
      isDev() && console.log('🔄 LAYER 4: Building categoryFallbackImages map');
      const categoryFallbackImages = new Map([
        [
          'Front Casing',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/flush-mount-door-casing-placeholder.jpg?v=1722540000',
        ],
        [
          'Wood & Finish',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/wood-finish-placeholder.jpg?v=1722540000',
        ],
        [
          'Width',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/width-placeholder.jpg?v=1722540000',
        ],
        [
          'Height',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/height-placeholder.jpg?v=1722540000',
        ],
        [
          'Depth',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/depth-placeholder.jpg?v=1722540000',
        ],
        [
          'Interior',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/interior-placeholder.jpg?v=1722540000',
        ],
        [
          'Hardware',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/hardware-placeholder.jpg?v=1722540000',
        ],
        [
          '_generic',
          'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/murphy-door-placeholder.jpg?v=1722540000',
        ],
      ]);

      isDev() &&
        console.log(
          `✅ LAYER 4: Created categoryFallbackImages with ${categoryFallbackImages.size} entries`
        );

      // 🔥 ANTI-PATTERN #2 FIX: Ensure ALL products have valid images
      // This eliminates 50+ runtime fallback checks in navigation-manager
      // MUST RUN BEFORE productIdToImageMap is built to ensure map has fallback URLs
      isDev() &&
        console.log(
          '🔄 LAYER 4: Validating product images and applying fallbacks'
        );
      let imageValidationCount = 0;
      let fallbackAssignedCount = 0;

      allProducts.forEach((product) => {
        imageValidationCount++;

        // Check if product has a valid image
        const hasValidImage =
          product.image?.src ||
          (product.image && typeof product.image === 'string') ||
          product.image_url ||
          product.featured_image?.src;

        if (!hasValidImage) {
          // Get category-specific fallback image
          const category = product.category || 'Unknown';
          const fallbackUrl =
            categoryFallbackImages.get(category) ||
            categoryFallbackImages.get('_generic');

          // Assign fallback image in standard Shopify format
          product.image = {
            src: fallbackUrl,
            alt: product.title || 'Product Image',
            width: 100,
            height: 100,
          };

          fallbackAssignedCount++;
          isDev() &&
            console.warn(
              `⚠️ LAYER 4: Product "${product.title}" (ID: ${product.id}) in category "${category}" missing image - assigned fallback: ${fallbackUrl}`
            );
        }
      });

      isDev() &&
        console.log(
          `✅ LAYER 4: Image validation complete - ${imageValidationCount} products checked, ${fallbackAssignedCount} fallback images assigned`
        );

      // Build product ID to image URL mapping for Phase 2 modules
      const productIdToImageMap = new Map();

      // Add all products (both regular and CompositeProducts)
      allProducts.forEach((product) => {
        let imageUrl = null;

        // Check product.image first (works for both regular products and CompositeProducts)
        if (product.image) {
          imageUrl =
            typeof product.image === 'string'
              ? product.image
              : product.image.src;
        }
        // Fall back to featured_image
        else if (product.featured_image) {
          imageUrl =
            typeof product.featured_image === 'string'
              ? product.featured_image
              : product.featured_image.src;
        }
        // For CompositeProducts without image property, try first variant's featured_image
        // Guard: featured_image may be null after Liquid payload slimming (variants without own images)
        else if (
          product.type === 'CompositeProduct' &&
          product.variants &&
          product.variants.length > 0
        ) {
          const firstVariant = product.variants[0];
          if (firstVariant.featured_image) {
            imageUrl =
              typeof firstVariant.featured_image === 'string'
                ? firstVariant.featured_image
                : firstVariant.featured_image.src;
          }
          // Fallback: try the variant's normalizedImage (populated by variant-enhancer with product fallback)
          if (
            !imageUrl &&
            firstVariant.normalizedImage &&
            firstVariant.normalizedImage.src !== '/assets/placeholder.jpg'
          ) {
            imageUrl = firstVariant.normalizedImage.src;
          }
        }

        if (imageUrl) {
          // Normalize protocol-relative URLs (//shop.myshopify.com/cdn/...) to explicit https://.
          // On production (HTTPS) they resolve correctly either way, but on localhost (HTTP) the
          // browser resolves // to http:// which is blocked by the img-src https: CSP directive.
          if (imageUrl.startsWith('//')) {
            imageUrl = 'https:' + imageUrl;
          }
          productIdToImageMap.set(product.id, imageUrl);
        }
      });

      isDev() &&
        console.log(
          `🖼️ LAYER 4: Built productIdToImageMap with ${productIdToImageMap.size} image URLs`
        );

      // Build group-id to composite product lookup map for Phase 2 modules
      const compositeGroupIdToProductMap = new Map();
      allProducts.forEach((product) => {
        if (product.type === 'CompositeProduct' && product['data-group-id']) {
          compositeGroupIdToProductMap.set(product['data-group-id'], product);
        }
      });

      isDev() &&
        console.log(
          `🔗 LAYER 4: Built compositeGroupIdToProductMap with ${compositeGroupIdToProductMap.size} composites`
        );

      // 🚀 ANTI-PATTERN FIX #1: Build variantIdToCompositeProductMap for O(1) composite lookups by variant
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building variantIdToCompositeProductMap for O(1) variant→composite lookups'
        );
      const variantIdToCompositeProductMap = new Map();

      for (const [, product] of products.entries()) {
        if (product.type === 'CompositeProduct' && product.variants) {
          product.variants.forEach((variant) => {
            if (variant.id) {
              variantIdToCompositeProductMap.set(variant.id, product);
            }
          });
        }
      }

      Object.freeze(variantIdToCompositeProductMap);
      isDev() &&
        console.log(
          `✅ LAYER 4: Built variantIdToCompositeProductMap - ${variantIdToCompositeProductMap.size} variant→composite mappings (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          '🚀 PERFORMANCE BOOST: Eliminated O(n×m) composite search - now O(1) lookups!'
        );

      // 🚀 ANTI-PATTERN FIX #1: Build sourceProductIdToCompositeMap for O(1) composite lookups by source product
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building sourceProductIdToCompositeMap for O(1) sourceProduct→composite lookups'
        );
      const sourceProductIdToCompositeMap = new Map();

      for (const [, product] of products.entries()) {
        if (product.type === 'CompositeProduct' && product.sourceProductIds) {
          product.sourceProductIds.forEach((sourceId) => {
            if (sourceId) {
              sourceProductIdToCompositeMap.set(sourceId, product);
            }
          });
        }
      }

      Object.freeze(sourceProductIdToCompositeMap);
      isDev() &&
        console.log(
          `✅ LAYER 4: Built sourceProductIdToCompositeMap - ${sourceProductIdToCompositeMap.size} sourceProduct→composite mappings (IMMUTABLE)`
        );

      // ═══════════════════════════════════════════════════════════════════════════════
      // 🎯 PRIORITY 1: CORE GROUPING INFRASTRUCTURE
      // ═══════════════════════════════════════════════════════════════════════════════
      // PURPOSE: Group products by display_name for merged variant presentation
      // CONSUMERS: Navigation Manager (multi-option category rendering)
      // IMMUTABILITY: Map<displayName, products[]> and Map<displayName, mergedVariants[]>
      //               Read-only structures - modifications require new Map creation
      // ═══════════════════════════════════════════════════════════════════════════════

      isDev() &&
        console.log('🔄 LAYER 4: Building Core Grouping Infrastructure');

      // displayNameToProductsMap: Map<displayName, products[]>
      // Groups products sharing the same display_name for merged presentation
      const displayNameToProductsMap = new Map();

      // displayNameToMergedVariantsMap: Map<displayName, mergedVariants[]>
      // Pre-merged variant arrays with source product metadata for each display group
      const displayNameToMergedVariantsMap = new Map();

      // individualProducts: Set<productId>
      // Tracks products without display_name (render as standalone cards)
      const individualProducts = new Set();

      allProducts.forEach((product) => {
        const displayName = product.display_name?.trim();

        if (displayName) {
          // Group products by display_name
          if (!displayNameToProductsMap.has(displayName)) {
            displayNameToProductsMap.set(displayName, []);
            displayNameToMergedVariantsMap.set(displayName, []);
          }

          displayNameToProductsMap.get(displayName).push(product);

          // Merge variants with source product metadata
          if (product.variants && Array.isArray(product.variants)) {
            const mergedVariants =
              displayNameToMergedVariantsMap.get(displayName);
            product.variants.forEach((variant) => {
              mergedVariants.push({
                ...variant,
                sourceProductId: product.id,
                sourceProductHandle: product.handle,
                sourceProductTitle: product.title,
                sourceProductCategory: product.category,
              });
            });
          }
        } else {
          // Track individual products (no display_name grouping)
          individualProducts.add(product.id);
        }
      });

      isDev() &&
        console.log(`✅ LAYER 4: Core Grouping Infrastructure complete`);
      isDev() &&
        console.log(
          `   - displayNameToProductsMap: ${displayNameToProductsMap.size} display groups`
        );
      isDev() &&
        console.log(
          `   - displayNameToMergedVariantsMap: ${displayNameToMergedVariantsMap.size} merged variant arrays`
        );
      isDev() &&
        console.log(
          `   - individualProducts: ${individualProducts.size} standalone products`
        );

      // Build variantIdToProductIdMap for O(1) variant → product lookups
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building variantIdToProductIdMap for O(1) variant lookups'
        );
      const variantIdToProductIdMap = new Map();

      products.forEach((product, productId) => {
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach((variant) => {
            variantIdToProductIdMap.set(variant.id, productId);
          });
        }
      });

      isDev() &&
        console.log(
          `✅ LAYER 4: Created variantIdToProductIdMap with ${variantIdToProductIdMap.size} variants`
        );

      // Build productIdToVariantIdMap for O(1) productId → variantId lookups (single-variant products only)
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building productIdToVariantIdMap for single-variant products'
        );
      const productIdToVariantIdMap = new Map();

      products.forEach((product, productId) => {
        if (
          product.variants &&
          Array.isArray(product.variants) &&
          product.variants.length === 1
        ) {
          const variant = product.variants[0];
          // Only auto-resolve for products with a single "Default Title" variant
          // This prevents auto-selection of meaningful single variants (e.g., "With Engraving")
          if (variant && variant.id && variant.title === 'Default Title') {
            productIdToVariantIdMap.set(productId, variant.id);
          }
        }
      });

      isDev() &&
        console.log(
          `✅ LAYER 4: Created productIdToVariantIdMap with ${productIdToVariantIdMap.size} single-variant products`
        );

      // 🔥 ANTI-PATTERN #4 FIX: Annotate ALL products with pre-computed classification properties
      // This eliminates runtime variant counting in Navigation Manager and other modules
      isDev() &&
        console.log(
          '🔄 LAYER 4: Annotating products with classification properties'
        );
      let annotatedCount = 0;

      products.forEach((product) => {
        const variantCount = product.variants?.length || 0;

        // 🎯 BUSINESS LOGIC: isSingleVariant means "true single variant with Default Title"
        // A product with 1 meaningfully-named variant (e.g., "With Lock") is NOT single-variant
        // because it requires explicit user selection, not auto-selection
        // This matches the original conductor logic:
        //   product.variants.length === 1 && product.variants[0]?.title === 'Default Title'
        const isDefaultTitleSingleVariant =
          variantCount === 1 && product.variants[0]?.title === 'Default Title';

        product.isSingleVariant = isDefaultTitleSingleVariant;
        product.isMultiVariant = !product.isSingleVariant; // Multi-variant = NOT single-variant
        product.variantCount = variantCount;

        annotatedCount++;
      });

      isDev() &&
        console.log(
          `✅ LAYER 4: Annotated ${annotatedCount} products with classification properties (isSingleVariant, isMultiVariant, variantCount)`
        );

      // 🚀 LAYER 4: Build material compatibility index for O(1) lookups
      // This eliminates O(n×m) runtime iterations in sorting-manager
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building SKU-based material compatibility index for O(1) material lookups'
        );

      const woodCompatibilityMap = new Map(); // Map<woodSku, Set<variantId>>
      const finishCompatibilityMap = new Map(); // Map<finishSku, Set<variantId>>
      const variantMaterialScoreBase = new Map(); // Map<variantId, {hasWood, hasFinish, tagCount, woodSku, finishSku}>

      let indexedVariantCount = 0;
      let woodCompatibilityCount = 0;
      let finishCompatibilityCount = 0;

      // Iterate through all products and their variants to build the compatibility index
      products.forEach((product) => {
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach((variant) => {
            const variantId = variant.id;
            indexedVariantCount++;

            // Extract material SKUs from variant
            const hasWood = !!variant.wood_sku;
            const hasFinish = !!variant.finish_sku;
            const compatibilityTags = variant.compatibility_tags || [];
            const tagCount = compatibilityTags.length;

            // Store base scoring metadata for this variant (SKU-based)
            variantMaterialScoreBase.set(variantId, {
              hasWood: hasWood,
              hasFinish: hasFinish,
              tagCount: tagCount,
              woodSku: variant.wood_sku || null,
              finishSku: variant.finish_sku || null,
            });

            // Index wood compatibility by SKU
            if (hasWood) {
              const woodSku = variant.wood_sku;
              if (!woodCompatibilityMap.has(woodSku)) {
                woodCompatibilityMap.set(woodSku, new Set());
              }
              woodCompatibilityMap.get(woodSku).add(variantId);
              woodCompatibilityCount++;
            }

            // Index finish compatibility by SKU
            if (hasFinish) {
              const finishSku = variant.finish_sku;
              if (!finishCompatibilityMap.has(finishSku)) {
                finishCompatibilityMap.set(finishSku, new Set());
              }
              finishCompatibilityMap.get(finishSku).add(variantId);
              finishCompatibilityCount++;
            }

            // Index compatibility tags (wood:SKU and finish:SKU patterns)
            compatibilityTags.forEach((tag) => {
              if (tag.startsWith('wood:')) {
                const woodSku = tag.substring(5); // Extract SKU after 'wood:'
                if (!woodCompatibilityMap.has(woodSku)) {
                  woodCompatibilityMap.set(woodSku, new Set());
                }
                woodCompatibilityMap.get(woodSku).add(variantId);
              } else if (tag.startsWith('finish:')) {
                const finishSku = tag.substring(7); // Extract SKU after 'finish:'
                if (!finishCompatibilityMap.has(finishSku)) {
                  finishCompatibilityMap.set(finishSku, new Set());
                }
                finishCompatibilityMap.get(finishSku).add(variantId);
              }
            });
          });
        }
      });

      // 🚀 ANTI-PATTERN #3 FIX: Build variant-specific material compatibility relationships
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building variant-specific material compatibility sets (Anti-Pattern #3 fix)'
        );
      const variantMaterialRelationships = new Map();
      let relationshipsBuilt = 0;

      products.forEach((product) => {
        if (product.variants && product.variants.length > 0) {
          product.variants.forEach((variant) => {
            const key = `${product.id}_${variant.id}`;
            const woodSku = variant.wood_sku || null;
            const finishSku = variant.finish_sku || null;
            const compatibilityTags = variant.compatibility_tags || [];

            // Build compatible wood/finish sets (SKU-based)
            const compatibleWoods = new Set();
            const compatibleFinishes = new Set();

            // Add direct SKUs
            if (woodSku) compatibleWoods.add(woodSku);
            if (finishSku) compatibleFinishes.add(finishSku);

            // Add from compatibility tags
            compatibilityTags.forEach((tag) => {
              if (tag.startsWith('wood:')) {
                compatibleWoods.add(tag.substring(5));
              } else if (tag.startsWith('finish:')) {
                compatibleFinishes.add(tag.substring(7));
              }
            });

            variantMaterialRelationships.set(key, {
              woodSku,
              finishSku,
              materialType: variant.material_type || null,
              compatibleWoods,
              compatibleFinishes,
              hasWoodCompatibility: compatibleWoods.size > 0,
              hasFinishCompatibility: compatibleFinishes.size > 0,
            });
            relationshipsBuilt++;
          });
        }
      });

      isDev() &&
        console.log(
          `✅ LAYER 4: Built ${relationshipsBuilt} variant material relationships for O(1) compatibility scoring (Anti-Pattern #3 fix)`
        );

      const materialCompatibilityIndex = {
        woodCompatibilityMap: woodCompatibilityMap,
        finishCompatibilityMap: finishCompatibilityMap,
        variantMaterialScoreBase: variantMaterialScoreBase,
        variantMaterialRelationships: variantMaterialRelationships, // Anti-Pattern #3 fix
      };

      isDev() &&
        console.log(
          `✅ LAYER 4: Built material compatibility index - ${indexedVariantCount} variants indexed, ` +
            `${woodCompatibilityMap.size} unique wood GIDs (${woodCompatibilityCount} mappings), ` +
            `${finishCompatibilityMap.size} unique finish GIDs (${finishCompatibilityCount} mappings)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Sorting complexity reduced from O(n×m) to O(n) with O(1) compatibility lookups`
        );

      // ========================================================================
      // 🔄 LAYER 4.7: WOOD & FINISH INVERTED INDEX (Concept A — finish-first UX)
      // Re-pivots the Wood & Finish category's (wood × finish) variants into a
      // finishSku → [woods] lookup, so simplified mode can let the customer pick
      // a FINISH first and then the wood species that offers it (the inverse of
      // the default wood-first flow). Downstream selection/cascade/forbidden is
      // unchanged — each finish→wood pick still resolves to the SAME single
      // Wood & Finish variant; only the ENTRY ORDER inverts.
      //
      // Pure re-pivot of data already present on the enhanced variants
      // (wood_sku, finish_sku, title "Wood / Finish"). No new Shopify data.
      // Concept B (tone-family grouping) would layer on top via a finish_family
      // metafield — see memory project_wood_finish_inversion_tone_families.
      // ========================================================================
      const WOOD_FINISH_CATEGORY = 'Wood & Finish';
      const woodFinishInvertedIndex = {
        // finishSku → [{ finishSku, finishName, woodSku, woodName, productId,
        //               variantId, variantTitle, price, image }]
        finishToWoods: new Map(),
        // first-seen finishSku order, for stable swatch rendering
        finishOrder: [],
        // finishSku → { finishSku, finishName, sampleImage, woodCount }
        finishMeta: new Map(),
      };
      const wfProducts = categoryToProductsMap.get(WOOD_FINISH_CATEGORY) || [];
      wfProducts.forEach((product) => {
        const productWoodName = (
          product.display_name ||
          product.title ||
          ''
        ).trim();
        (product.variants || []).forEach((variant) => {
          const finishSku = variant.finish_sku;
          if (!finishSku) return;
          // Use the enhancer-populated material names (set directly from the
          // woods/finishes metafield arrays, not the variant title — the title
          // is unreliably ordered "Finish / Wood" on these products). Fall back
          // to the product title for the wood (the product IS the wood species)
          // and the SKU for the finish.
          const finishName = variant.finish_name || finishSku;
          const woodName =
            variant.wood_name || productWoodName || variant.wood_sku || '';
          const image =
            variant.configurator_front_image ||
            (variant.featured_image && variant.featured_image.src) ||
            product.configurator_front_image ||
            (product.featured_image && product.featured_image.src) ||
            null;
          const entry = {
            finishSku: finishSku,
            finishName: finishName,
            woodSku: variant.wood_sku || null,
            woodName: woodName,
            productId: product.id,
            variantId: variant.id,
            variantTitle: variant.title || '',
            price: variant.price != null ? variant.price : null,
            image: image,
          };
          if (!woodFinishInvertedIndex.finishToWoods.has(finishSku)) {
            woodFinishInvertedIndex.finishToWoods.set(finishSku, []);
            woodFinishInvertedIndex.finishOrder.push(finishSku);
            woodFinishInvertedIndex.finishMeta.set(finishSku, {
              finishSku: finishSku,
              finishName: finishName,
              sampleImage: image,
              woodCount: 0,
            });
          }
          woodFinishInvertedIndex.finishToWoods.get(finishSku).push(entry);
        });
      });
      // Backfill woodCount now that all variants are grouped.
      woodFinishInvertedIndex.finishToWoods.forEach((woods, finishSku) => {
        const meta = woodFinishInvertedIndex.finishMeta.get(finishSku);
        if (meta) meta.woodCount = woods.length;
      });
      isDev() &&
        console.log(
          `✅ LAYER 4.7: Built Wood & Finish inverted index — ${woodFinishInvertedIndex.finishOrder.length} finishes across ${wfProducts.length} wood products`,
          woodFinishInvertedIndex.finishOrder.map((sku) => {
            const m = woodFinishInvertedIndex.finishMeta.get(sku);
            return `${m.finishName} (${sku}): ${m.woodCount} woods`;
          })
        );

      // 🚀 LAYER 4: Build material combination validation map for O(1) validation
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building material combination validation map for O(1) wood+finish validation'
        );

      const materialCombinationValidationMap = new Map();
      let validationEntriesBuilt = 0;

      // Build validation map from materialRelationships (which has keys like "woodGid::finishGid")
      materialRelationships.forEach((relationship, combinationKey) => {
        // combinationKey format: "gid://shopify/Metaobject/walnut-veneer::gid://shopify/Metaobject/medium-stain"
        materialCombinationValidationMap.set(combinationKey, true);
        validationEntriesBuilt++;
      });

      Object.freeze(materialCombinationValidationMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built material combination validation map - ${validationEntriesBuilt} valid combinations indexed (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Material validation complexity reduced from O(n) search to O(1) Map lookup`
        );

      // 🚀 ANTI-PATTERN FIX: Build variantIdToProductMap for O(1) variant→product lookups
      // This eliminates 200+ O(n) searches per session in updateSelectionText()
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building variantIdToProductMap for O(1) variant→product lookups'
        );
      const variantIdToProductMap = new Map();

      products.forEach((product) => {
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach((variant) => {
            variantIdToProductMap.set(variant.id, product);
          });
        }
      });

      Object.freeze(variantIdToProductMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built variantIdToProductMap - ${variantIdToProductMap.size} variant→product mappings (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Variant→product lookups reduced from O(n) to O(1)`
        );

      // 🚀 ANTI-PATTERN FIX #4: Build productIdToEnhancedVariantsMap for O(1) product→enhanced variants lookups
      // This eliminates runtime filtering in isSingleVariant() (100+ lines of complexity → single property read)
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building productIdToEnhancedVariantsMap for O(1) product→enhanced variants lookups'
        );
      const productIdToEnhancedVariantsMap = new Map();

      products.forEach((product, productId) => {
        if (product.variants && Array.isArray(product.variants)) {
          // Use product's variants array directly - these ARE the enhanced variants from Layer 2
          // No filtering needed since each product already has its correct enhanced variants
          const productEnhancedVariants = product.variants;

          productIdToEnhancedVariantsMap.set(
            productId,
            Object.freeze(productEnhancedVariants)
          );

          // 🔥 PRE-COMPUTE: Add isSingleVariant property to product for instant detection
          product.isSingleVariant =
            product.type !== 'CompositeProduct' && // CompositeProducts are NEVER single-variant
            productEnhancedVariants.length === 1 &&
            productEnhancedVariants[0].title === 'Default Title';

          // 🔥 PRE-COMPUTE: Add enhancedVariantCount for quick access
          product.enhancedVariantCount = productEnhancedVariants.length;
        } else {
          // Product without variants
          productIdToEnhancedVariantsMap.set(productId, Object.freeze([]));
          product.isSingleVariant = false;
          product.enhancedVariantCount = 0;
        }
      });

      Object.freeze(productIdToEnhancedVariantsMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built productIdToEnhancedVariantsMap - ${productIdToEnhancedVariantsMap.size} product→enhanced variants mappings (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: isSingleVariant() reduced from O(n*m) filtering (100+ lines) to single property read`
        );

      // 🚀 ANTI-PATTERN FIX: Build flatCategoryOrder array for summary list ordering
      // This eliminates 100+ flatMap operations per page load in updateSummaryList()
      // Sort tabs by loadOrder to ensure correct tab → category order for Summary list
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building flatCategoryOrder array for summary ordering'
        );
      const flatCategoryOrder = Array.from(tabStructure.values())
        .sort((a, b) => a.loadOrder - b.loadOrder) // Sort tabs by loadOrder (Appearance → Features → Specs)
        .flatMap((tab) => tab.categories); // Then flat map categories in sorted tab order

      Object.freeze(flatCategoryOrder);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built flatCategoryOrder - ${flatCategoryOrder.length} categories in tab order (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Summary ordering reduced from O(n) flatMap to O(1) array read`
        );

      // 🚀 ANTI-PATTERN FIX: Build categoryToSortIndexMap for O(1) category sort index lookups
      // This enables instant category ordering in summary list without linear array searches
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryToSortIndexMap for O(1) sort index lookups'
        );
      const categoryToSortIndexMap = new Map();
      flatCategoryOrder.forEach((categoryId, index) => {
        categoryToSortIndexMap.set(categoryId, index);
      });

      Object.freeze(categoryToSortIndexMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryToSortIndexMap - ${categoryToSortIndexMap.size} category→sortIndex mappings (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Category sort index lookups reduced from O(n) indexOf to O(1) Map.get()`
        );

      // 🚀 ANTI-PATTERN FIX: Build categoryToVirtualizedProductsMap for instant category rendering
      // This eliminates 50+ O(n) filtering operations per page load in showCategoryContent()
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryToVirtualizedProductsMap for O(1) category rendering'
        );
      const categoryToVirtualizedProductsMap = new Map();

      for (const [
        categoryId,
        categoryProducts,
      ] of categoryToProductsMap.entries()) {
        // Virtualized products include both regular products and composite products
        // This is already done in Phase 1, just reference the frozen array!
        categoryToVirtualizedProductsMap.set(categoryId, categoryProducts);
      }

      Object.freeze(categoryToVirtualizedProductsMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryToVirtualizedProductsMap - ${categoryToVirtualizedProductsMap.size} categories with virtualized products (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Category rendering eliminated O(n) filtering, direct frozen array access`
        );

      // 🚀 ANTI-PATTERN FIX: Build categorySortedProductsMap for material-aware thumbnail sorting
      // This eliminates 50+ O(n log n) sorts per page load in generateSmartSortedThumbnails()
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categorySortedProductsMap with material preference variants'
        );
      const categorySortedProductsMap = new Map();

      for (const [
        categoryId,
        categoryProducts,
      ] of categoryToProductsMap.entries()) {
        // Default sort: The frozen array from categoryToProductsMap (UX-optimized sorted order)
        const defaultSorted = categoryProducts;

        // Material-preferred sort: Not yet implemented - set to null to make contract explicit
        // This prevents future developers from assuming the feature exists when it doesn't
        const materialPreferredSorted = null;

        categorySortedProductsMap.set(
          categoryId,
          Object.freeze({
            default: defaultSorted,
            materialPreferred: materialPreferredSorted,
          })
        );
      }

      Object.freeze(categorySortedProductsMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categorySortedProductsMap - ${categorySortedProductsMap.size} categories with dual sort modes (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Thumbnail sorting eliminated O(n log n) runtime sorts, pre-computed arrays`
        );

      // 🚀 LAYER 5: Pre-Render HTML Cache (Performance Optimization - Scaffold Only)
      // This eliminates 20+ O(n) HTML generation calls per page load (300ms each = ~6 seconds total!)
      isDev() &&
        console.log(
          '🎨 LAYER 5: Creating thumbnail HTML cache structure (will hydrate post-bootstrap)'
        );
      const categoryToThumbnailsHTMLMap = new Map();

      // Create cache scaffold with null values - will be hydrated after bootstrap
      // We can't pre-render HTML here because navigationManager isn't available yet
      for (const [categoryId] of categoryToProductsMap.entries()) {
        categoryToThumbnailsHTMLMap.set(categoryId, {
          default: null, // Will be hydrated post-bootstrap
          materialPreferred: null, // Lazy-build on first material selection
        });
      }

      isDev() &&
        console.log(
          `✅ LAYER 5: Created HTML cache structure for ${categoryToThumbnailsHTMLMap.size} categories (awaiting post-bootstrap hydration)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE TARGET: ~6 seconds eliminated once hydrated (20 categories × 300ms)`
        );

      // 🚀 ANTI-PATTERN #2 FIX: categoryToDirectVariantProductMap for O(1) direct variant product lookups
      // Eliminates array access in showDirectVariantSelection(), 10% faster category rendering
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryToDirectVariantProductMap for O(1) direct variant access'
        );
      const categoryToDirectVariantProductMap = new Map();

      categories.forEach((classification, categoryId) => {
        const displayMode = categoryDisplayModeMap.get(categoryId);
        if (
          displayMode?.hasDirectVariant &&
          displayMode.directVariantProductId
        ) {
          const product = products.get(displayMode.directVariantProductId);
          if (product) {
            categoryToDirectVariantProductMap.set(categoryId, product);
          }
        }
      });

      Object.freeze(categoryToDirectVariantProductMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryToDirectVariantProductMap - ${categoryToDirectVariantProductMap.size} direct variant products (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: showDirectVariantSelection() eliminated array access, pure O(1) lookups`
        );

      // 🚀 LAYER 5: Build HTML caches for variant layouts (UI Updater optimization)
      // These eliminate O(n) HTML generation in click handlers
      isDev() &&
        console.log(
          '🎨 LAYER 5: Building variant layout HTML caches for instant rendering'
        );

      // 🎯 THUMBNAIL STATUS ICON: Read SVG templates from DOM for status overlay injection
      // These are Liquid-rendered hidden elements available post-bootstrap (Layer 5)
      const _saveIconSvgHTML =
        document
          .getElementById('murphy-door-save-icon-svg')
          ?.querySelector('svg')?.outerHTML || '';
      const _circleCheckSvgHTML =
        document
          .getElementById('murphy-door-circle-check-svg')
          ?.querySelector('svg')?.outerHTML || '';

      if (!_saveIconSvgHTML || !_circleCheckSvgHTML) {
        isDev() &&
          console.warn(
            '[DataPipeline] Thumbnail status SVG templates not found in DOM. ' +
              'Ensure snippets icon-save and murphy-door-circle-check are included with correct IDs.'
          );
      }

      const _statusIconHTML =
        _saveIconSvgHTML && _circleCheckSvgHTML
          ? `<div class="murphy-door-configurator__thumbnail-status-icon" aria-hidden="true">
            <span class="thumbnail-save-button">${_saveIconSvgHTML}</span>
            <span class="thumbnail-circle-check">${_circleCheckSvgHTML}</span>
          </div>`
          : '';

      // Helper function delegates to shared _generateVariantThumbnailHTML method
      const generateVariantThumbnailHTML = (
        variant,
        isComposite,
        productId,
        groupId = null,
        groupName = null
      ) => {
        return config._generateVariantThumbnailHTML(
          variant,
          isComposite,
          productId,
          variantIdToVariantMap,
          _statusIconHTML,
          groupId,
          groupName
        );
      };

      // 1. productToDirectVariantLayoutHTMLMap: Pre-built layouts for LONELY_MULTI_VARIANT categories
      const productToDirectVariantLayoutHTMLMap = new Map();

      categoryDisplayModeMap.forEach((displayMode) => {
        if (
          displayMode.hasDirectVariant &&
          displayMode.directVariantProductId
        ) {
          const product = products.get(displayMode.directVariantProductId);
          if (product && product.variants && product.variants.length > 0) {
            const isComposite = product.type === 'CompositeProduct';
            const variantGridHTML = product.variants
              .map((v) =>
                generateVariantThumbnailHTML(v, isComposite, product.id)
              )
              .join('');

            const layoutHTML = `
              <div class="murphy-door-configurator__variant-view murphy-door-configurator__variant-view--direct"
                   data-variant-selection="true"
                   data-product-type="${isComposite ? 'composite' : 'regular'}"
                   style="opacity: 1;">
                <div class="murphy-door-configurator__variants-grid">
                  ${variantGridHTML}
                </div>
              </div>
            `.trim();

            productToDirectVariantLayoutHTMLMap.set(product.id, layoutHTML);
          }
        }
      });

      Object.freeze(productToDirectVariantLayoutHTMLMap);

      isDev() &&
        console.log(
          `✅ LAYER 5: Built productToDirectVariantLayoutHTMLMap - ${productToDirectVariantLayoutHTMLMap.size} direct variant layouts cached`
        );

      // 2. productToVariantLayoutHTMLMap: Pre-built variant grids for multi-variant products
      const productToVariantLayoutHTMLMap = new Map();

      products.forEach((product, productId) => {
        if (
          product.variants &&
          product.variants.length > 1 &&
          product.type !== 'single-variant'
        ) {
          const isComposite = product.type === 'CompositeProduct';
          // NUMERIC INPUT PRODUCTS (constants.js, top-level sibling of
          // CATEGORY_TYPES): a product-scoped opt-in for products whose
          // variant count makes a tile grid unusable (Custom Jamb Depth: 133
          // variants). Swaps the tile grid for a typed-measurement input card
          // — same map, same key, so generateVariantLayout() in
          // md-config-ui-updater.js needs zero changes to consume either.
          // The lookup this card's Phase 2 module needs to resolve a typed
          // value is attached directly to the product object (no products are
          // frozen at this point in the pipeline), so
          // conductor.getProductFromDictionary(productId).numericInputLookup
          // reaches it with no new conductor method required.
          const usesNumericInputUI =
            window.MurphyDoorConfigurator.CONSTANTS.NUMERIC_INPUT_PRODUCTS.has(
              product.title
            );

          let variantGridHTML;
          if (usesNumericInputUI) {
            product.numericInputLookup = config._buildNumericInputLookup(
              product,
              variantIdToVariantMap
            );
            variantGridHTML = config._generateNumericInputCardHTML(
              product,
              productId,
              product.numericInputLookup,
              _statusIconHTML
            );
          } else {
            variantGridHTML = product.variants
              .map((variant) =>
                generateVariantThumbnailHTML(variant, isComposite, productId)
              )
              .join('');
          }

          // Store just the variant grid HTML - wrapper added at runtime (needs dynamic back button SVG)
          productToVariantLayoutHTMLMap.set(productId, variantGridHTML);
        }
      });

      Object.freeze(productToVariantLayoutHTMLMap);

      isDev() &&
        console.log(
          `✅ LAYER 5: Built productToVariantLayoutHTMLMap - ${productToVariantLayoutHTMLMap.size} variant grids cached`
        );

      // 3. compositeProductToVariantGridHTMLMap: Pre-built variant grids for composite products
      const compositeProductToVariantGridHTMLMap = new Map();

      if (compositeGroupIdToProductMap) {
        compositeGroupIdToProductMap.forEach((compositeProduct, groupId) => {
          if (
            compositeProduct.variants &&
            compositeProduct.variants.length > 0
          ) {
            // 🧬 BROWSING STATE FIX: Pass groupId and groupName to variant thumbnail generator
            // This enables composite product browsing state with Apply button and display text
            // NOTE: isComposite=false for individual variants (they're not group thumbnails!)
            const groupName = compositeProduct.display_name;
            const variantGridHTML = compositeProduct.variants
              .map((variant) =>
                generateVariantThumbnailHTML(
                  variant,
                  false,
                  compositeProduct.id,
                  groupId,
                  groupName
                )
              )
              .join('');

            compositeProductToVariantGridHTMLMap.set(groupId, variantGridHTML);
          }
        });
      }

      Object.freeze(compositeProductToVariantGridHTMLMap);

      isDev() &&
        console.log(
          `✅ LAYER 5: Built compositeProductToVariantGridHTMLMap - ${compositeProductToVariantGridHTMLMap.size} composite variant grids cached`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: UI Updater eliminated 40-60% click latency with pre-built HTML caches!`
        );

      // ═══════════════════════════════════════════════════════════════════════════
      // LAYER 5.5: STATIC ASSETS CACHE
      // ═══════════════════════════════════════════════════════════════════════════
      isDev() && console.log('📦 LAYER 5.5: Caching static DOM assets...');

      const staticAssets = {
        backArrowSVG:
          document.querySelector('#murphy-door-back-arrow-svg')?.innerHTML ||
          '← Back',
      };

      Object.freeze(staticAssets);
      isDev() &&
        console.log('✅ LAYER 5.5 COMPLETE:', {
          hasBackArrowSVG: !!staticAssets.backArrowSVG,
        });

      // ═══════════════════════════════════════════════════════════════════════════
      // 🚀 LAYER 4: CATEGORY METADATA (High Priority Enhancement)
      // ═══════════════════════════════════════════════════════════════════════════
      // PURPOSE: Pre-compute all required category IDs to eliminate runtime O(n) operations
      // ELIMINATES: Runtime flatMap/reduce operations to collect all category IDs
      // PERFORMANCE: O(1) access to all categories instead of O(n) iterations
      // ═══════════════════════════════════════════════════════════════════════════
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryMetadata for O(1) category validation'
        );

      const requiredCategoryIdsSet = new Set();
      tabStructure.forEach((tab) => {
        tab.categories.forEach((categoryId) => {
          requiredCategoryIdsSet.add(categoryId);
        });
      });

      const allRequiredCategoryIdsArray = Array.from(requiredCategoryIdsSet);
      Object.freeze(allRequiredCategoryIdsArray);

      const categoryMetadata = {
        allRequiredCategoryIdsArray: allRequiredCategoryIdsArray,
        totalCategoryCount: requiredCategoryIdsSet.size,
      };

      Object.freeze(categoryMetadata);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built categoryMetadata - ${categoryMetadata.totalCategoryCount} unique categories indexed (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Category validation reduced from O(n) iterations to O(1) Set.has() lookups`
        );

      // ═══════════════════════════════════════════════════════════════════════════
      // 🚀 LAYER 4: TAB METADATA (Low Priority Enhancement)
      // ═══════════════════════════════════════════════════════════════════════════
      // PURPOSE: Pre-compute tab IDs and counts for quick reference
      // ELIMINATES: Runtime Array.from(tabStructure.keys()) operations
      // PERFORMANCE: O(1) access to tab structure metadata
      // ═══════════════════════════════════════════════════════════════════════════
      isDev() &&
        console.log('🔄 LAYER 4: Building tabMetadata for O(1) tab reference');

      const allTabIds = [...tabStructure.keys()];
      const tabMetadata = {
        allTabIds: allTabIds,
        tabCount: tabStructure.size,
      };

      // Freeze for immutability
      Object.freeze(tabMetadata.allTabIds);
      Object.freeze(tabMetadata);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built tabMetadata - ${tabMetadata.tabCount} tabs indexed (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Tab iteration eliminated, pre-computed tab arrays and counts`
        );

      // ═══════════════════════════════════════════════════════════════════════════
      // LAYER 4: MATERIAL COMPATIBILITY SCORE CACHE (O(1) material scoring)
      // ═══════════════════════════════════════════════════════════════════════════
      isDev() &&
        console.log('🔄 LAYER 4: Building material compatibility score cache');

      const materialCompatibilityScoreCache = new Map();

      // Helper function for SKU-based score calculation
      function calculateCompatibilityScore(variant, woodSku, finishSku) {
        let score = 0;

        if (woodSku && variant.materialData?.woodSku === woodSku) {
          score += 100;
        }

        if (finishSku && variant.materialData?.finishSku === finishSku) {
          score += 100;
        }

        return score;
      }

      // Get wood and finish metaobjects for iteration
      const woodMetaobjects = materialData.woodMetaobjects || [];
      const finishMetaobjects = materialData.finishMetaobjects || [];

      // Pre-calculate scores for all SKU combinations
      woodMetaobjects.forEach((wood) => {
        finishMetaobjects.forEach((finish) => {
          allProducts.forEach((product) => {
            if (product.variants && product.variants.length > 0) {
              product.variants.forEach((variant) => {
                const score = calculateCompatibilityScore(
                  variant,
                  wood.sku,
                  finish.sku
                );
                const key = `${product.id}_${variant.id}_${wood.sku}_${finish.sku}`;
                materialCompatibilityScoreCache.set(key, score);
              });
            }
          });
        });
      });

      // Also pre-calculate for 'none' states (no material preference)
      allProducts.forEach((product) => {
        if (product.variants && product.variants.length > 0) {
          product.variants.forEach((variant) => {
            // none_none (no preferences)
            materialCompatibilityScoreCache.set(
              `${product.id}_${variant.id}_none_none`,
              0
            );

            // Only wood preference
            if (variant.materialData?.woodSku) {
              materialCompatibilityScoreCache.set(
                `${product.id}_${variant.id}_${variant.materialData.woodSku}_none`,
                100
              );
            }

            // Only finish preference
            if (variant.materialData?.finishSku) {
              materialCompatibilityScoreCache.set(
                `${product.id}_${variant.id}_none_${variant.materialData.finishSku}`,
                100
              );
            }
          });
        }
      });

      Object.freeze(materialCompatibilityScoreCache);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built materialCompatibilityScoreCache - ${materialCompatibilityScoreCache.size} entries (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Material scoring reduced from runtime calculation to O(1) cache lookup`
        );

      // ═══════════════════════════════════════════════════════════════════════════
      // LAYER 4: VALIDATION METADATA (O(1) validation lookups)
      // ═══════════════════════════════════════════════════════════════════════════
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building validationMetadata with schema validation'
        );

      // ⚠️ SCHEMA VALIDATION KEYS - MUST STAY IN SYNC WITH OBJECT CONSTRUCTION
      //
      // These arrays define the required properties for tab and category objects.
      // IF YOU MODIFY the structure of objects at these locations, UPDATE these arrays:
      //
      // Tab objects constructed at:
      //   - Line ~1041: tabStructure.set(tabGid, { id, name, loadOrder, ... })
      //
      // Category objects constructed at:
      //   - Line ~2354-2360: classifyCategory() return statement
      //   - Line ~970-973: classification.displayElements = { ... }
      //   - Line ~975: categories.set(categoryName, classification)
      //
      // 🔍 How to verify schema sync:
      //   1. Check console logs for "❌ LAYER 4: Schema validation errors" on page load
      //   2. Review the properties set in the locations above
      //   3. Update these arrays if properties have changed
      //
      const requiredTabKeys = ['id', 'name', 'loadOrder', 'categories'];
      const requiredCategoryKeys = [
        'displayElements',
        'type',
        'products',
        'multiVariantCount',
        'singleVariantCount',
        'totalProducts',
      ];

      // Pre-validate tab schema
      const tabSchemaErrors = [];
      tabStructure.forEach((tab, tabId) => {
        requiredTabKeys.forEach((key) => {
          if (!tab.hasOwnProperty(key)) {
            tabSchemaErrors.push(`Tab ${tabId} missing required key: ${key}`);
          }
        });
      });

      // Pre-validate category schema
      const categorySchemaErrors = [];
      categories.forEach((category, categoryId) => {
        requiredCategoryKeys.forEach((key) => {
          if (!category.hasOwnProperty(key)) {
            categorySchemaErrors.push(
              `Category ${categoryId} missing required key: ${key}`
            );
          }
        });
      });

      // Build O(1) validation Sets
      //
      // ⚠️ TAB ID FORMAT INCONSISTENCY (Intentional for validation-manager compatibility)
      //
      // This Set stores NUMERIC IDs extracted from full Shopify GIDs for validation purposes.
      // Other PPD structures (like tabStructure) use the full GID string as the key.
      //
      // Why numeric IDs here?
      //   - validation-manager.validateTabChange() receives numeric string IDs from DOM
      //   - DOM elements use data-tab="94796480557" (numeric ID, not full GID)
      //   - This Set must match the ID format that validation-manager receives
      //
      // Alternative approaches considered:
      //   1. Convert validation-manager to use full GIDs ❌ (requires DOM refactor)
      //   2. Store both formats in PPD ❌ (unnecessary duplication)
      //   3. Extract numeric ID at validation time ❌ (adds O(n) operation to O(1) lookup)
      //   4. Store numeric IDs in validationMetadata ✅ (current approach - O(1) with no overhead)
      //
      // Format examples:
      //   - Full GID: "gid://shopify/Metaobject/94796480557"
      //   - Numeric ID stored here: 94796480557
      //   - DOM attribute: data-tab="94796480557"
      //
      const validTabIds = new Set();
      tabStructure.forEach((tab, tabGid) => {
        // Extract numeric ID from GID string (e.g., "gid://.../94796480557" → 94796480557)
        const numericIdMatch = tabGid.match(/\/(\d+)$/);
        if (numericIdMatch) {
          validTabIds.add(parseInt(numericIdMatch[1], 10));
        }
      });

      const validCategoryIds = new Set(categories.keys());

      // Freeze Sets for immutability
      Object.freeze(validTabIds);
      Object.freeze(validCategoryIds);

      const validationMetadata = {
        validTabIds: validTabIds,
        validCategoryIds: validCategoryIds,
        schemaErrors: [...tabSchemaErrors, ...categorySchemaErrors],
        hasSchemaErrors:
          tabSchemaErrors.length > 0 || categorySchemaErrors.length > 0,
        validatedTabCount: tabStructure.size,
        validatedCategoryCount: categories.size,
      };

      // Freeze for immutability
      if (validationMetadata.schemaErrors.length > 0) {
        Object.freeze(validationMetadata.schemaErrors);
      }
      Object.freeze(validationMetadata);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built validationMetadata - ${validationMetadata.validatedTabCount} tabs, ${validationMetadata.validatedCategoryCount} categories validated (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Validation checks reduced from conductor delegation + Map.has() to direct O(1) Set.has()`
        );

      if (validationMetadata.hasSchemaErrors) {
        isDev() &&
          console.error(
            '❌ LAYER 4: Schema validation errors found:',
            validationMetadata.schemaErrors
          );
      } else {
        isDev() &&
          console.log(
            '✅ LAYER 4: Schema validation passed - all required keys present'
          );
      }

      // 🚀 LAYER 4: Pre-build Selection Display Data Map
      isDev() && console.log('🔄 LAYER 4: Building selection display data map');

      const selectionDisplayDataMap = new Map();
      let selectionDataEntries = 0;

      allProducts.forEach((product) => {
        const categoryData = categories.get(product.category);

        // For Single-variant products, store by product ID
        if (
          product.type === 'Single-variant' &&
          (!product.variants || product.variants.length === 1)
        ) {
          const variant =
            product.variants && product.variants[0]
              ? product.variants[0]
              : null;

          // ✅ FIX: Use display_name (normalized in Layer 1) with parenthetical stripped,
          // consistent with Layer 1's cleanProductName. Raw product.title includes size info
          // like "(Single/French Door)" which causes duplication in selection display.
          const cleanDisplayTitle = (
            product.display_name ||
            product.title ||
            ''
          )
            .replace(/\s*\([^)]*\)\s*/g, '')
            .trim();

          const displayEntry = {
            displayTitle: cleanDisplayTitle,
            price: variant?.price || '',
            productTitle: product.title || '',
            images: {
              front:
                variant?.configurator_front_image ||
                product.configurator_front_image ||
                null,
              back:
                variant?.configurator_back_image ||
                product.configurator_back_image ||
                null,
            },
            zIndex: categoryData?.mediaZIndex || 0,
            category: product.category,
          };

          selectionDisplayDataMap.set(`product_${product.id}`, displayEntry);
          selectionDataEntries++;

          // FIX: Also index single-variant products by their variant ID.
          // When the state manager auto-resolves a variantId for single-variant products,
          // it looks up `variant_${variantId}` which previously wouldn't exist, causing
          // a fallback to `product_${productId}` and noisy console warnings.
          if (variant && variant.id) {
            selectionDisplayDataMap.set(`variant_${variant.id}`, displayEntry);
            selectionDataEntries++;
          }
        }
        // For Multi-variant and CompositeProduct, store by variant ID
        else if (product.variants && product.variants.length > 0) {
          product.variants.forEach((variant) => {
            // ⚡ PPD-FIRST: Use pre-computed clean variant name from Layer 1
            // Eliminates DRY violation by reusing displayFormatting.cleanVariantName
            // Example: "Product / Variant" → "Variant" (correctly shows last part, NOT first)
            // FIX: Was showing FIRST part ("Product"), now correctly shows LAST part ("Variant")
            const enhancedVariant = variantIdToVariantMap.get(variant.id);
            const cleanVariantName =
              enhancedVariant?.displayFormatting?.cleanVariantName ||
              variant.title ||
              '';

            selectionDisplayDataMap.set(`variant_${variant.id}`, {
              displayTitle: cleanVariantName,
              price: variant.price || '',
              productTitle: product.title || '',
              images: {
                front:
                  variant.configurator_front_image ||
                  product.configurator_front_image ||
                  null,
                back:
                  variant.configurator_back_image ||
                  product.configurator_back_image ||
                  null,
              },
              zIndex: categoryData?.mediaZIndex || 0,
              category: product.category,
            });
            selectionDataEntries++;
          });
        }
      });

      Object.freeze(selectionDisplayDataMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built selectionDisplayDataMap - ${selectionDisplayDataMap.size} entries (${selectionDataEntries} products/variants indexed) (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Selection state reduced from multiple PPD lookups to single O(1) map access`
        );

      // 🚀 LAYER 4: Build initialMaterialPreferences structure
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building initialMaterialPreferences structure'
        );
      const layer4MaterialPrefsStart = performance.now();

      const initialMaterialPreferences = Object.freeze({
        woodApplied: false,
        finishApplied: false,
        primaryMaterialPreference: null,
        secondaryMaterialPreferences: new Map(),
        appliedWoodGid: null,
        appliedFinishGid: null,
        materialCompatibilityMap: new Map(),
        lastUpdated: null, // Will be set at runtime when preferences are modified
      });

      const layer4MaterialPrefsTime =
        performance.now() - layer4MaterialPrefsStart;
      isDev() &&
        console.log(
          `✅ LAYER 4: Built initialMaterialPreferences structure in ${layer4MaterialPrefsTime.toFixed(2)}ms (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Material preferences no longer require lazy initialization at runtime`
        );

      // 🚀 LAYER 4: Build initialImageLayerStates complete map
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building initialImageLayerStates complete map'
        );
      const layer4ImageLayerStart = performance.now();

      const initialImageLayerStates = new Map();

      // Build complete layer states for all categories
      categories.forEach((categoryData, categoryId) => {
        // Get initial layer state data
        const layerState = categoryToInitialLayerStateMap.get(categoryId);

        if (layerState) {
          initialImageLayerStates.set(categoryId, {
            state: 'initial', // LAYER_STATES.INITIAL constant value
            images: layerState.images,
            zIndex: layerState.zIndex,
            timestamp: null, // Will be set at runtime
          });
        }
      });

      Object.freeze(initialImageLayerStates); // Immutable for safety

      const layer4ImageLayerTime = performance.now() - layer4ImageLayerStart;
      isDev() &&
        console.log(
          `✅ LAYER 4: Built initialImageLayerStates - ${initialImageLayerStates.size} layer states (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Image layer initialization now uses pre-built structures`
        );
      isDev() &&
        console.log(
          `⏱️ Layer 4 Image Layer States Time: ${layer4ImageLayerTime.toFixed(2)}ms`
        );

      // 🚀 LAYER 4: Build category-specific variant filtering maps for O(1) material filtering
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building categoryToCompatibleVariantsMap for O(1) material-based variant filtering'
        );

      const categoryToCompatibleVariantsMap = new Map();
      let categoryVariantMapsBuilt = 0;

      // For each category, pre-build variant lookup structures
      categories.forEach((categoryData, categoryName) => {
        const categoryProducts = categoryToProductsMap.get(categoryName) || [];

        // Collect all variant IDs in this category
        const allVariantIds = new Set();
        categoryProducts.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              allVariantIds.add(variant.id);
            });
          }
        });

        // Store frozen set for this category
        categoryToCompatibleVariantsMap.set(
          categoryName,
          Object.freeze(allVariantIds)
        );
        categoryVariantMapsBuilt++;
      });

      Object.freeze(categoryToCompatibleVariantsMap);

      isDev() &&
        console.log(
          `✅ LAYER 4: Built category variant maps - ${categoryVariantMapsBuilt} categories indexed`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Category-based filtering complexity reduced from O(n×m) to O(1) Set intersection`
        );

      // 🚀 ANTI-PATTERN FIX #5: Initialize materialPreferences state structure
      // This eliminates runtime caching in conductor (removes _materialPreferencesCache)
      isDev() &&
        console.log(
          '🔄 LAYER 4: Initializing materialPreferences state structure'
        );

      const materialPreferences = Object.freeze({
        selectedWood: null,
        selectedFinish: null,
        selectedWoodGid: null,
        selectedFinishGid: null,
        preferredCombinations: Object.freeze(new Set()),
        lastUpdated: null,
        initialized: false,
      });

      isDev() &&
        console.log(
          '✅ LAYER 4: Material preferences structure initialized (IMMUTABLE scaffold - will be hydrated by state-manager during Phase 2)'
        );
      isDev() &&
        console.log(
          '🚀 PERFORMANCE BOOST: Eliminates runtime caching overhead in conductor (~50-100 cache validation checks per page load)'
        );

      // 🚀 LAYER 4: Build cartItemTemplates for O(1) cart preparation (ANTI-PATTERN #1 FIX)
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building cartItemTemplates for O(1) cart preparation'
        );
      const cartTemplateStart = performance.now();
      const cartItemTemplates = new Map();

      // Build templates for ALL products (composites + standalone)
      // NOTE: Base product template is added later after base product validation
      allProducts.forEach((product) => {
        const category =
          product.category || product.configurator_tab_category?.category;

        if (!product.variants || product.variants.length === 0) {
          return; // Skip products without variants
        }

        product.variants.forEach((variant) => {
          const variantId = parseInt(variant.id);

          // Pre-build complete cart item template
          const cartItemTemplate = Object.freeze({
            id: variantId,
            quantity: 1,
            properties: {
              _Base_Product: product.type === 'base',
              _Category: category,
              _Display_Name: `${product.display_name}: ${variant.title || variant.name}`,
              _Tab_Category:
                product.configurator_tab_category?.category || category,
              _Price: variant.price,
              // Additional properties added at runtime:
              // _Configuration_ID: (added during cart preparation)
              // _Configuration_Time: (added during cart preparation)
              // _Add_on_for: (added for add-on products)
              // _Door_Title: (added for base product)
            },
            // Metadata for cart manager to use
            productId: product.id,
            variantId: variantId,
            productHandle: product.handle,
            productTitle: product.title,
            available: variant.available !== false, // false only when explicitly unavailable
          });

          cartItemTemplates.set(variantId, cartItemTemplate);
        });
      });

      const cartTemplateTime = performance.now() - cartTemplateStart;
      isDev() &&
        console.log(
          `✅ LAYER 4: Built cartItemTemplates - ${cartItemTemplates.size} templates in ${cartTemplateTime.toFixed(2)}ms (IMMUTABLE)`
        );
      isDev() &&
        console.log(
          `🚀 PERFORMANCE BOOST: Cart preparation reduced from O(n) async lookups to O(1) template reads`
        );

      // 🚀 LAYER 4: Build mediaSlidePositions map from Liquid template data
      isDev() &&
        console.log(
          '🔄 LAYER 4: Building mediaSlidePositions map for O(1) gallery lookups'
        );
      const mediaSlideStart = performance.now();

      const mediaSlidePositions = new Map();
      const mediaSlideData = materialData.mediaSlideData;

      if (
        mediaSlideData &&
        mediaSlideData.media &&
        Array.isArray(mediaSlideData.media)
      ) {
        mediaSlideData.media.forEach((mediaItem) => {
          if (mediaItem && mediaItem.id) {
            // Store complete media item data with position as key field
            const mediaData = {
              position: mediaItem.position,
              type: mediaItem.type,
              mediaType: mediaItem.mediaType,
            };

            // Include shopifyMediaId for product media (optional field)
            if (mediaItem.shopifyMediaId) {
              mediaData.shopifyMediaId = mediaItem.shopifyMediaId;
            }

            mediaSlidePositions.set(mediaItem.id, Object.freeze(mediaData));
          }
        });

        Object.freeze(mediaSlidePositions); // Make map immutable

        const mediaSlideTime = performance.now() - mediaSlideStart;
        isDev() &&
          console.log(
            `✅ LAYER 4: Built mediaSlidePositions - ${mediaSlidePositions.size} slides in ${mediaSlideTime.toFixed(2)}ms (IMMUTABLE)`
          );
        isDev() &&
          console.log(
            `🚀 PERFORMANCE BOOST: Gallery slide lookups reduced from O(n) DOM queries to O(1) Map access`
          );
      } else {
        isDev() &&
          console.warn(
            '⚠️ LAYER 4: No mediaSlideData available - mediaSlidePositions will be empty'
          );
      }

      // 🚀 LAYER 4: Pre-validate base product structure (ANTI-PATTERN #2 FIX)
      isDev() &&
        console.log('🔄 LAYER 4: Pre-validating base product structure');
      const baseProductData = materialData.productData; // Base door product
      let baseProductError = null;
      let baseProductSelectedVariant = null;

      if (
        !baseProductData ||
        !baseProductData.variants ||
        baseProductData.variants.length === 0
      ) {
        baseProductError = {
          hasError: true,
          message: 'Base product has no variants',
          productId: baseProductData?.id || null,
        };
        isDev() && console.error(`❌ LAYER 4: ${baseProductError.message}`);
      } else if (baseProductData.variants.length > 1) {
        baseProductError = {
          hasError: true,
          message: `Base product ${baseProductData.id} has multiple variants - ambiguous cart selection`,
          productId: baseProductData.id,
          variantCount: baseProductData.variants.length,
        };
        isDev() && console.error(`❌ LAYER 4: ${baseProductError.message}`);
      } else {
        // Exactly 1 variant - this is the correct state
        baseProductSelectedVariant = baseProductData.variants[0];
        isDev() &&
          console.log(
            `✅ LAYER 4: Base product validated - single variant ${baseProductSelectedVariant.id}`
          );
      }

      const baseProductValidation = Object.freeze({
        error: baseProductError,
        selectedVariant: baseProductSelectedVariant,
        validatedAt: Date.now(),
      });

      // 🚀 CRITICAL FIX: Add base product cart template
      // Base product is NOT in allProducts array (stored separately in baseProductValidation),
      // so we must create its cart template separately to prevent add-to-cart failures
      if (baseProductSelectedVariant) {
        const baseVariantId = parseInt(baseProductSelectedVariant.id);
        const baseCartTemplate = Object.freeze({
          id: baseVariantId,
          quantity: 1,
          properties: {
            _Base_Product: true,
            _Category: 'Base Product',
            _Display_Name: `${baseProductData.title}: ${baseProductSelectedVariant.title || 'Default'}`,
            _Tab_Category: 'Base Product',
            _Price: baseProductSelectedVariant.price,
          },
          // Metadata for cart manager to use
          productId: baseProductData.id,
          variantId: baseVariantId,
          productHandle: baseProductData.handle,
          productTitle: baseProductData.title,
          available: baseProductSelectedVariant.available !== false,
        });

        cartItemTemplates.set(baseVariantId, baseCartTemplate);
        isDev() &&
          console.log(
            `✅ LAYER 4: Added base product cart template for variant ${baseVariantId}`
          );
      }

      // Freeze cart templates Map now that base product template is added
      Object.freeze(cartItemTemplates);
      isDev() &&
        console.log(
          `✅ LAYER 4: Finalized cartItemTemplates - ${cartItemTemplates.size} total templates (including base product) (IMMUTABLE)`
        );

      // 🔍 DEBUG: Log Map sizes before returning PPD
      isDev() &&
        console.log('🔍 DEBUG TRACE: finalPPD HTML Maps before return:', {
          productToDirectVariantLayoutHTMLMap_size:
            productToDirectVariantLayoutHTMLMap.size,
          productToVariantLayoutHTMLMap_size:
            productToVariantLayoutHTMLMap.size,
          compositeProductToVariantGridHTMLMap_size:
            compositeProductToVariantGridHTMLMap.size,
          productToVariantLayoutHTMLMap_isMap:
            productToVariantLayoutHTMLMap instanceof Map,
          productToVariantLayoutHTMLMap_keys: Array.from(
            productToVariantLayoutHTMLMap.keys()
          ).slice(0, 3),
        });

      // ============================================================================
      // LAYER 4.6: SLIDER CATEGORIES MAP
      // Builds O(1) bidirectional Maps for dimension categories (Width, Height)
      // that use horizontal slider UI instead of thumbnail grid.
      // ============================================================================
      const sliderCategoriesMap = this.layer46_buildSliderCategoriesMap(
        categories,
        categoryToProductsMap,
        categoryDisplayModeMap,
        materialData.configData
      );

      // ============================================================================
      // SIMPLIFIED MODE CONFIG MAPS
      // Built from configurator_tab_categories metafields:
      //   simplified_required (boolean) → which categories show in "My Door" tab
      //   simplified_default_variant_id (json) → which variant to auto-apply at completion
      //     - string: static variant ID
      //     - {material_aware:true, by_material_title:{...}, fallback:id}: resolved at runtime
      // ============================================================================
      const simplifiedRequiredCategories = [];
      const simplifiedDefaultsByCategory = new Map();
      // apo_load_order (number) -> the order apo_style renders this card in.
      // Admin-editable replacement for the hardcoded APO_STYLE_*_CATEGORY_ORDER
      // arrays in md-config-core-constants.js (Ryan review 2026-08-17: card
      // order belongs in metafields, not conditionals in code). Sparse by
      // design: a category with no value simply isn't in the map, and its
      // consumer falls back to the constant, so a half-finished admin pass
      // never changes behaviour.
      const apoCategoryOrder = new Map();
      // slider_note (single line) -> text shown under a category's slider,
      // e.g. Extra Shelves' "ships with 3 adjustable shelves" (2026-09-30).
      const categorySliderNotes = new Map();
      const apoVisibilityRules = new Map();
      const selectionDependencyRules = new Map();
      if (materialData.configData.tabCategories) {
        materialData.configData.tabCategories.forEach((catData) => {
          if (!catData.category) return;
          if (catData.simplified_required === true) {
            simplifiedRequiredCategories.push(catData.category);
          }
          if (
            typeof catData.slider_note === 'string' &&
            catData.slider_note.trim()
          ) {
            categorySliderNotes.set(
              catData.category,
              catData.slider_note.trim()
            );
          }
          if (catData.simplified_default_variant_id != null) {
            simplifiedDefaultsByCategory.set(
              catData.category,
              catData.simplified_default_variant_id
            );
          }
          // Number() not parseInt: the metafield arrives as a JSON number when
          // populated and null when blank, and an explicit 0 is a legitimate
          // "render first" that must not be discarded as falsy.
          if (
            catData.apo_load_order !== null &&
            catData.apo_load_order !== undefined &&
            catData.apo_load_order !== ''
          ) {
            const parsed = Number(catData.apo_load_order);
            if (Number.isFinite(parsed)) {
              apoCategoryOrder.set(catData.category, parsed);
            }
          }
          // apo_visibility_rule (JSON) -> which gate answer reveals this card.
          // Admin-editable overlay on CATEGORY_VISIBILITY_DEPS. Shape:
          //   { "gate": "cabinets", "showWhen": ["top_only", "top_and_bottom"] }
          // Sparse like apo_load_order: a category with no rule isn't in the
          // map and keeps whatever the constant says, so a half-finished admin
          // pass never changes behaviour.
          //
          // Anything malformed is DROPPED, not partially honoured. A rule that
          // half-applies would hide a card the customer needs with no error
          // anywhere -- the constant is always the safer thing to fall back to.
          const rawRule = catData.apo_visibility_rule;
          if (rawRule !== null && rawRule !== undefined && rawRule !== '') {
            try {
              const rule =
                typeof rawRule === 'string' ? JSON.parse(rawRule) : rawRule;
              if (
                rule &&
                typeof rule.gate === 'string' &&
                rule.gate &&
                Array.isArray(rule.showWhen)
              ) {
                apoVisibilityRules.set(catData.category, {
                  gate: rule.gate,
                  showWhen: rule.showWhen.filter(
                    (a) => typeof a === 'string' && a
                  ),
                });
              }
            } catch (error) {
              isDev() &&
                console.warn(
                  '[DataPipeline] apo_visibility_rule ignored for "' +
                    catData.category +
                    '" -- unparseable, falling back to the constant:',
                  error
                );
            }
          }

          // selection_dependency_rule (JSON) -> this category is forced to a
          // "none" fallback when its PARENT category is set to none. Keyed on
          // the DEPENDENT category (e.g. "Top Knobs & Pulls"). Shape:
          //   { "dependsOn": "Top Cabinet Doors",
          //     "noneHandle": "no-top-cabinet-doors-single-french",
          //     "applyVariantTitle": "No Knobs or Pulls" }
          //
          // Admin-editable overlay on SELECTION_VISIBILITY_DEPS. Sparse and
          // all-or-nothing for the same reason as apo_visibility_rule: a rule
          // missing any of the three fields is DROPPED whole rather than
          // half-merged, because a partially-applied rule would either leave a
          // knob in the cart that the customer never sees (they are charged for
          // hardware that ships with no door to mount it on) or strand the
          // category unapplied and lock Add to Cart. The constant is always the
          // safer thing to fall back to.
          const rawDep = catData.selection_dependency_rule;
          if (rawDep !== null && rawDep !== undefined && rawDep !== '') {
            try {
              const dep =
                typeof rawDep === 'string' ? JSON.parse(rawDep) : rawDep;
              if (
                dep &&
                typeof dep.dependsOn === 'string' &&
                dep.dependsOn &&
                typeof dep.noneHandle === 'string' &&
                dep.noneHandle &&
                typeof dep.applyVariantTitle === 'string' &&
                dep.applyVariantTitle
              ) {
                selectionDependencyRules.set(catData.category, {
                  dependsOnCategory: dep.dependsOn,
                  hiddenWhenProductHandle: dep.noneHandle,
                  autoApplyVariantTitleOnHide: dep.applyVariantTitle,
                });
              } else {
                isDev() &&
                  console.warn(
                    '[DataPipeline] selection_dependency_rule ignored for "' +
                      catData.category +
                      '" -- needs all of dependsOn/noneHandle/applyVariantTitle, ' +
                      'falling back to the constant.'
                  );
              }
            } catch (error) {
              isDev() &&
                console.warn(
                  '[DataPipeline] selection_dependency_rule ignored for "' +
                    catData.category +
                    '" -- unparseable, falling back to the constant:',
                  error
                );
            }
          }
        });
      }

      // Sort simplified categories by tab loadOrder first, then category load_order within the tab.
      // tabStructure is fully built above with sorted categories — use it as the canonical sort source.
      if (simplifiedRequiredCategories.length > 1) {
        // Build tab loadOrder lookup: tabGid → tabLoadOrder
        const tabLoadOrderMap = new Map();
        tabStructure.forEach((tabData, tabGid) => {
          tabLoadOrderMap.set(tabGid, tabData.loadOrder || 0);
        });

        // Build category load_order lookup from raw config (categoryLoadOrderMap is in an inner scope above)
        const simplifiedCatLoadOrderMap = new Map();
        if (materialData.configData.tabCategories) {
          materialData.configData.tabCategories.forEach((catData) => {
            if (catData.category)
              simplifiedCatLoadOrderMap.set(
                catData.category,
                catData.load_order || 0
              );
          });
        }

        simplifiedRequiredCategories.sort((a, b) => {
          const tabGidA = categoryToParentTabMap.get(a);
          const tabGidB = categoryToParentTabMap.get(b);
          const tabOrderA =
            tabGidA != null ? (tabLoadOrderMap.get(tabGidA) ?? 999) : 999;
          const tabOrderB =
            tabGidB != null ? (tabLoadOrderMap.get(tabGidB) ?? 999) : 999;
          if (tabOrderA !== tabOrderB) return tabOrderA - tabOrderB;
          // Same tab — sort by category load_order within the tab
          const catOrderA = simplifiedCatLoadOrderMap.get(a) ?? 999;
          const catOrderB = simplifiedCatLoadOrderMap.get(b) ?? 999;
          return catOrderA - catOrderB;
        });
      }

      Object.freeze(simplifiedRequiredCategories);
      Object.freeze(simplifiedDefaultsByCategory);
      Object.freeze(apoCategoryOrder);
      Object.freeze(categorySliderNotes);
      Object.freeze(apoVisibilityRules);
      Object.freeze(selectionDependencyRules);

      const finalPPD = {
        products: products, // Map
        categories: categories, // Map
        categoryTypeClassificationMap: categoryTypeClassificationMap, // Map - O(1) category type lookups (MVC vs SVC)
        dimensionCategoryIndex: dimensionCategoryIndex, // Object - O(1) dimension category classification (ANTI-PATTERN #3 FIX)
        materialRelationships: materialRelationships, // Map
        materialCompatibilityScoreCache: materialCompatibilityScoreCache, // Map - O(1) pre-computed material compatibility scores (eliminates runtime calculations)
        variantShippingTimeMap: variantShippingTimeMap, // Map - O(1) variant→shipping time lookups (eliminates O(n×m) searches)
        productMaxShippingTimeMap: productMaxShippingTimeMap, // Map - O(1) product→max shipping time lookups (eliminates O(v) iterations)
        categoryMetadata: categoryMetadata, // Object (frozen) - Pre-computed category IDs and counts for O(1) validation (HIGH PRIORITY)
        tabMetadata: tabMetadata, // Object (frozen) - Pre-computed tab IDs and counts for O(1) reference (LOW PRIORITY)
        validationMetadata: validationMetadata, // Object (frozen) - O(1) validation Sets and pre-validated schema checks
        tabStructure: tabStructure, // Map
        categoryToParentTabMap: categoryToParentTabMap, // Map - O(1) category→tab lookups for navigation manager
        sortedTabsByLoadOrder: sortedTabsByLoadOrder, // Array - Pre-sorted tabs by loadOrder (Anti-Pattern #2 fix - eliminates O(n log n) runtime sorting)
        tabToSortedCategoryIds: tabToSortedCategoryIds, // Map - Pre-sorted categories per tab (Anti-Pattern #2 fix - eliminates O(m log m) runtime sorting)
        sortedCategories: sortedCategoriesArray, // Array - Pre-sorted categories by displayOrder (eliminates O(n log n) runtime sorting)
        categoryDisplayOrder: categoryDisplayOrder, // Map - O(1) categoryId→displayOrder lookups (eliminates Map.get() during sort comparisons)
        imageLayerStructure: imageLayerStructure, // Object with Maps
        mediaSlidePositions: mediaSlidePositions, // Map - O(1) media slide position lookups for gallery (IMMUTABLE)
        unifiedConditionGroups: forbiddenCombinationMaps.unifiedConditionGroups, // Map - Unified condition groups (GUI-Friendly forbidden system)
        unifiedRules: forbiddenCombinationMaps.unifiedRules, // Map - Unified forbidden rules (GUI-Friendly forbidden system)
        conflictRules: forbiddenCombinationMaps.conflictRules, // Map - Pre-partitioned conflict rules (excludes AUTO_APPLY_OPTION)
        autoApplyRules: forbiddenCombinationMaps.autoApplyRules, // Map - Pre-partitioned AUTO_APPLY_OPTION rules only
        baseProduct: baseProduct, // Object - base product for price/shipping calculations
        productIdToImageMap: productIdToImageMap, // Map - Product ID to image URL for UI modules
        compositeGroupIdToProductMap: compositeGroupIdToProductMap, // Map - O(1) group-id to composite product mapping
        variantIdToCompositeProductMap: variantIdToCompositeProductMap, // Map - O(1) variant→composite lookups (ANTI-PATTERN #1 FIX)
        sourceProductIdToCompositeMap: sourceProductIdToCompositeMap, // Map - O(1) sourceProduct→composite lookups (ANTI-PATTERN #1 FIX)
        variantIdToProductIdMap: variantIdToProductIdMap, // Map - O(1) variant-id to product-id mapping
        productIdToVariantIdMap: productIdToVariantIdMap, // Map - O(1) product-id to variant-id mapping (single-variant products only)
        productIdToCategoryMap: productIdToCategoryMap, // Map - O(1) product-id to category mapping
        categoryFallbackImages: categoryFallbackImages, // Map - Static fallback images for categories
        materialCompatibilityIndex: materialCompatibilityIndex, // Object - O(1) material compatibility lookups for sorting
        materialCombinationValidationMap: materialCombinationValidationMap, // Map - O(1) wood+finish validation (eliminates O(n) searches)
        woodFinishInvertedIndex: woodFinishInvertedIndex, // Object - finishSku→[woods] re-pivot for finish-first simplified UX (Concept A)
        progressMaps: progressMaps, // Object (frozen) - Pre-built, immutable progress tracking maps with O(1) lookups
        categoryToProductsMap: categoryToProductsMap, // Map - O(1) category→products lookups (IMMUTABLE arrays)
        categoryToMaterialAwareProductsMap: categoryToMaterialAwareProductsMap, // Map - O(1) category→material-aware products with front/back images (IMMUTABLE)
        categoryToProductsWithImagesMap: categoryToProductsWithImagesMap, // Map - O(1) category→products with front/back images (IMMUTABLE)
        categoryToInitialLayerStateMap: categoryToInitialLayerStateMap, // Map - O(1) category→initial layer state lookups (ANTI-PATTERN #2 FIX)
        variantIdToVariantMap: variantIdToVariantMap, // Map - O(1) variant→object lookups (IMMUTABLE) - Replaces runtime .find() calls
        variantIdToVariantObjectMap: variantIdToVariantObjectMap, // Map - O(1) variant→object with product context (ANTI-PATTERN #1 FIX)
        categoryDisplayModeMap: categoryDisplayModeMap, // Map - O(1) pre-classified display modes (directVariant detection) - Replaces runtime .find() calls
        variantMap: materialData.variantMap, // Map - O(1) variantId→variant with product context (Layer 1 - PRICE CALCULATOR OPTIMIZATION)
        // Priority 1: Grouping maps for O(1) display_name group lookups
        displayNameToProductIdsMap: displayNameToProductIdsMap, // Map - display_name → productId[] for group identification
        categoryToGroupsMap: categoryToGroupsMap, // Map - categoryId → Set<displayName> for category group analysis
        // Priority 2: UI-ready product group data structures
        uiReadyProductGroupsMap: uiReadyProductGroupsMap, // Map - groupName/individualKey → complete UI-ready group data
        groupIdToGroupDataMap: groupIdToGroupDataMap, // Map - groupId → group data for O(1) group lookups
        displayNameToProductsMap: displayNameToProductsMap, // Map - Groups products by display_name for merged variant presentation
        displayNameToMergedVariantsMap: displayNameToMergedVariantsMap, // Map - Pre-merged variants with source product metadata
        individualProducts: individualProducts, // Set - Products without display_name (render as standalone cards)
        // 🚀 ANTI-PATTERN FIXES: Phase 1 pre-computed maps for Phase 2 O(1) access
        variantIdToProductMap: variantIdToProductMap, // Map - O(1) variant→product lookups (eliminates 200+ O(n) searches)
        flatCategoryOrder: flatCategoryOrder, // Array - Pre-flattened category order (eliminates 100+ flatMap operations)
        categoryToSortIndexMap: categoryToSortIndexMap, // Map - O(1) category→sortIndex lookups for summary list ordering (eliminates O(n) indexOf calls)
        categoryToVirtualizedProductsMap: categoryToVirtualizedProductsMap, // Map - O(1) category→virtualized products (eliminates 50+ O(n) filters)
        categorySortedProductsMap: categorySortedProductsMap, // Map - Pre-sorted products with material preference variants (eliminates 50+ O(n log n) sorts)
        categoryToThumbnailsHTMLMap: categoryToThumbnailsHTMLMap, // Map - Pre-rendered HTML cache (eliminates 20+ O(n) HTML generation calls)
        // 🚀 ANTI-PATTERN FIXES: UI Updater click performance optimizations
        categoryToDirectVariantProductMap: categoryToDirectVariantProductMap, // Map - O(1) category→direct variant product (eliminates array access)
        productToDirectVariantLayoutHTMLMap:
          productToDirectVariantLayoutHTMLMap, // Map - Pre-built direct variant layouts (70% faster LONELY_MULTI_VARIANT rendering)
        productToVariantLayoutHTMLMap: productToVariantLayoutHTMLMap, // Map - Pre-built variant grids (60% faster multi-variant clicks)
        compositeProductToVariantGridHTMLMap:
          compositeProductToVariantGridHTMLMap, // Map - Pre-built composite variant grids (50% faster composite clicks)
        staticAssets: staticAssets, // Cached static DOM elements
        selectionDisplayDataMap: selectionDisplayDataMap, // Map - O(1) selection display data lookups (eliminates multiple PPD accesses per selection)
        initialMaterialPreferences: initialMaterialPreferences, // Object (frozen) - Pre-built material preferences structure (eliminates lazy initialization)
        initialImageLayerStates: initialImageLayerStates, // Map (frozen) - Pre-built complete image layer states (eliminates O(n) iteration at init)
        productIdToEnhancedVariantsMap: productIdToEnhancedVariantsMap, // Map - O(1) product→enhanced variants lookups (ANTI-PATTERN #4 FIX)
        materialPreferences: materialPreferences, // Object (frozen) - Material preferences state structure (ANTI-PATTERN #5 FIX - eliminates conductor cache)
        categoryToCompatibleVariantsMap: categoryToCompatibleVariantsMap, // Map - O(1) category → variant IDs for filtering
        cartItemTemplates: cartItemTemplates, // Map - O(1) pre-built cart item templates for instant cart preparation (ANTI-PATTERN #1 FIX)
        baseProductValidation: baseProductValidation, // Object (frozen) - Pre-validated base product structure (ANTI-PATTERN #2 FIX)
        sliderCategoriesMap: sliderCategoriesMap, // Map - O(1) slider category metadata for horizontal slider UI (Width, Height)
        simplifiedRequiredCategories: simplifiedRequiredCategories, // Array - Category IDs with simplified_required=true (from metafields)
        simplifiedDefaultsByCategory: simplifiedDefaultsByCategory, // Map - Category ID → default variant config (static string or material-aware object)
        categorySliderNotes: categorySliderNotes, // Map - Category ID -> note under its slider (admin metafield `slider_note`; sparse)
        apoCategoryOrder: apoCategoryOrder, // Map - Category ID → apo_style render order (admin metafield; sparse, callers fall back to CONSTANTS)
        apoVisibilityRules: apoVisibilityRules, // Map - Category ID → {gate, showWhen} (admin metafield; sparse, callers fall back to CATEGORY_VISIBILITY_DEPS)
        selectionDependencyRules: selectionDependencyRules, // Map - dependent Category ID → {dependsOnCategory, hiddenWhenProductHandle, autoApplyVariantTitleOnHide} (admin metafield; sparse, callers fall back to SELECTION_VISIBILITY_DEPS)
      };

      // ═══════════════════════════════════════════════════════════════════════════
      // LAYER 6: HTML CACHE VALIDATION (DISABLED - Caches hydrated post-bootstrap)
      // ═══════════════════════════════════════════════════════════════════════════
      // 🔧 ARCHITECTURAL NOTE: HTML caches are created as NULL scaffolds during PPD build
      // and hydrated AFTER bootstrap completion by md-config-html-cache-hydrator.js
      // This validation was incorrectly expecting populated caches during bootstrap phase.
      //
      // REMOVED VALIDATION: Was checking for populated cache entries which shouldn't
      // exist yet during PPD creation. Caches are intentionally null until hydration.
      isDev() &&
        console.log(
          '✅ LAYER 6 SKIPPED: HTML cache validation disabled (caches hydrated post-bootstrap)'
        );

      isDev() &&
        console.log(
          `✅ PIPELINE COMPLETE: Built perfectProductDictionary with ${products.size} products, ${categories.size} categories, ${categoryTypeClassificationMap.size} category type mappings, ${materialRelationships.size} material relationships, categoryMetadata with ${categoryMetadata.totalCategoryCount} categories indexed (HIGH PRIORITY ENHANCEMENT), tabMetadata with ${tabMetadata.tabCount} tabs indexed (LOW PRIORITY ENHANCEMENT), ${tabStructure.size} tabs, ${categoryToParentTabMap.size} category→tab mappings, ${categoryDisplayOrder.size} category display order mappings (IMMUTABLE), ${imageLayerStructure.front.size + imageLayerStructure.back.size} image layers, ${forbiddenCombinationMaps.unifiedRules.size} unified forbidden rules, ${productIdToImageMap.size} product image mappings, ${compositeGroupIdToProductMap.size} composite group-id mappings, ${variantIdToProductIdMap.size} variant-id mappings, ${productIdToCategoryMap.size} product→category mappings, ${categoryFallbackImages.size} category fallback images, material compatibility index with ${materialCompatibilityIndex.woodCompatibilityMap.size} wood GIDs + ${materialCompatibilityIndex.finishCompatibilityMap.size} finish GIDs, ${variantShippingTimeMap.size} variant shipping times (O(1) lookups), ${productMaxShippingTimeMap.size} product max shipping times (O(1) lookups), progressMaps with ${progressMaps.categoryToTabMap.size} category-to-tab mappings + ${progressMaps.tabCategoryCounts.size} tab counts + ${progressMaps.tabCategoriesMap.size} tab arrays (${progressMaps.totalCategoryCount} total categories, IMMUTABLE), ${categoryToProductsMap.size} category→products mappings (IMMUTABLE), ${categoryToInitialLayerStateMap.size} category→initial layer states (ANTI-PATTERN #2 FIX), ${variantIdToVariantMap.size} variant→object lookups (IMMUTABLE), ${variantIdToVariantObjectMap.size} variant→object with product context (ANTI-PATTERN #1 FIX), ${categoryDisplayModeMap.size} category display modes (IMMUTABLE), ${displayNameToProductsMap.size} display groups, ${displayNameToMergedVariantsMap.size} merged variant arrays, ${individualProducts.size} individual products, ${variantIdToProductMap.size} variant→product mappings (ANTI-PATTERN FIX), ${flatCategoryOrder.length} category order array (ANTI-PATTERN FIX), ${categoryToSortIndexMap.size} category→sortIndex mappings (ANTI-PATTERN FIX), ${categoryToVirtualizedProductsMap.size} category virtualized products (ANTI-PATTERN FIX), ${categorySortedProductsMap.size} category sorted products (ANTI-PATTERN FIX), ${categoryToThumbnailsHTMLMap.size} thumbnail HTML cache (LAYER 5 - awaiting hydration), ${categoryToDirectVariantProductMap.size} direct variant products (UI OPTIMIZER), ${productToDirectVariantLayoutHTMLMap.size} direct variant layouts (UI OPTIMIZER), ${productToVariantLayoutHTMLMap.size} variant grids (UI OPTIMIZER), ${compositeProductToVariantGridHTMLMap.size} composite variant grids (UI OPTIMIZER), ${Object.keys(staticAssets).length} static assets cached (LAYER 5.5), ${cartItemTemplates.size} cart item templates (ANTI-PATTERN #1 FIX - O(1) cart preparation), baseProductValidation (ANTI-PATTERN #2 FIX - pre-validated base product structure)`
        );

      return finalPPD;
    },

    /**
     * Classify a single category based on its products
     * Returns: LONELY_MULTI_VARIANT, MULTI_VARIANT_ONLY, MIX, SINGLE_VARIANT_ONLY
     */
    // ============================================================================
    // LAYER 4.6: SLIDER CATEGORIES MAP BUILDER
    // ============================================================================
    /**
     * Layer 4.6: Build slider categories map for horizontal slider UI
     *
     * Detects dimension categories (Width, Height) and builds O(1) bidirectional
     * Maps for slider rendering. Extracts numeric values from variant titles,
     * sorts variants numerically, and sets default positions.
     *
     * @param {Map} categories - PPD categories map
     * @param {Map} categoryToProductsMap - Category to products mapping
     * @param {Map} categoryDisplayModeMap - Category display mode mapping
     * @param {Object} configData - Configuration data (may contain sliderDefaults from metafield)
     * @returns {Map} sliderCategoriesMap - Map<categoryId, sliderMetadata>
     */
    layer46_buildSliderCategoriesMap: function (
      categories,
      categoryToProductsMap,
      categoryDisplayModeMap,
      configData
    ) {
      if (bootstrapComplete) {
        throw new Error(
          'CRITICAL: layer46_buildSliderCategoriesMap() called AFTER bootstrap complete! Phase 1 only.'
        );
      }

      isDev() &&
        console.log(
          '🔄 LAYER 4.6: Building slider categories map for horizontal slider UI'
        );

      const sliderCategoriesMap = new Map();

      // Slider-eligible category names sourced from Phase 0 constants (single source of truth)
      const SLIDER_CATEGORY_NAMES =
        window.MurphyDoorConfigurator.CONSTANTS.CATEGORY_TYPES
          .SLIDER_CATEGORIES;

      // DATA-DRIVEN DEFAULTS: Preferred default per category, most-authoritative
      // first.
      // Priority 1: the configurator_tab_categories metaobject's
      //             simplified_default_variant_id -- an explicit per-product,
      //             per-category variant the merchant chose.
      // Priority 2: configData.sliderDefaults (metafield custom.configurator_defaults),
      //             a numeric dimension.
      // Priority 3: the hardcoded dimensions below.
      //
      // 🚨 PRIORITY 1 EXISTS BECAUSE 2 AND 3 SILENTLY DISAGREED WITH IT. On
      // flush-mount-bookcase-door-2-0 the metaobject names variant
      // 43255342170157 = 36 in. while this file's hardcoded fallback resolved
      // Width to 30 in. -- so the slider label read "Use default (30 in.)" and
      // any consumer of the metaobject (the curated-defaults coordinator, and
      // the load-time seeding in md-config-ui-horizontal-slider.js) applied 36.
      // Two "defaults" for one category, differing by six inches, with nothing
      // reconciling them. Ryan, UAT 2026-08-27: "metaobject should be source of
      // truth on defaults, not hard code."
      //
      // Reading it here rather than from ppd.simplifiedDefaultsByCategory is
      // deliberate: that Map is built AFTER this layer runs, but both derive
      // from the same configData.tabCategories, so this needs no reordering and
      // introduces no second source.
      const FALLBACK_DEFAULT_DIMENSIONS = {
        Width: 30, // 30 in. - standard door width
        Height: 80, // 80 in. - standard door height
        // 8-1/4 in. - the $0 "Included" depth the metaobject names. Without it
        // a product whose metaobject omits the Door Depth default would seed
        // index 0 (5-1/4 in., a $100 upcharge) on the customer's behalf.
        'Door Depth': 8.25,
        // "0 Extra Shelves" -- the door already ships with its 3 shelves.
        'Extra Shelves': 0,
      };

      // categoryId -> variantId string from the metaobject. Sparse by design:
      // a category the merchant has not set simply is not here and falls
      // through to the dimension logic below, so a half-configured catalogue
      // behaves exactly as it did before this change.
      const metaobjectDefaultVariantIds = new Map();
      if (configData && Array.isArray(configData.tabCategories)) {
        configData.tabCategories.forEach((catData) => {
          if (!catData || !catData.category) return;
          const raw = catData.simplified_default_variant_id;
          if (raw === null || raw === undefined) return;
          // The field is a string variant id, or the material-aware shape
          // { material_aware, by_material_title, fallback }. A measurement has
          // no material dimension, so only the fallback is meaningful here.
          const variantId =
            typeof raw === 'object' ? raw.fallback : String(raw);
          if (variantId) {
            metaobjectDefaultVariantIds.set(
              catData.category,
              String(variantId)
            );
          }
        });
      }

      // Resolve defaults from configData metafield (if available) or use fallback dimensions
      let sliderDefaultDimensions;
      if (
        configData &&
        configData.sliderDefaults &&
        typeof configData.sliderDefaults === 'object'
      ) {
        // Priority 1: Data-driven from Shopify metafield (custom.configurator_defaults)
        // Expected format: { "Width": 30, "Height": 80 } (numeric dimension values)
        // Layered OVER the fallback, per category: a metafield written before
        // Door Depth became a slider names only Width/Height, and replacing the
        // whole object would drop Door Depth to index 0 (a $100 upcharge).
        sliderDefaultDimensions = {
          ...FALLBACK_DEFAULT_DIMENSIONS,
          ...configData.sliderDefaults,
        };
        isDev() &&
          console.log(
            '✅ LAYER 4.6: Using metafield-driven slider defaults:',
            sliderDefaultDimensions
          );
      } else {
        // Priority 2: Intelligent fallback using standard industry dimensions
        sliderDefaultDimensions = FALLBACK_DEFAULT_DIMENSIONS;
        isDev() &&
          console.log(
            '📋 LAYER 4.6: No metafield sliderDefaults found, using standard industry defaults:',
            sliderDefaultDimensions
          );
      }

      for (const [categoryId] of categories.entries()) {
        if (!SLIDER_CATEGORY_NAMES.has(categoryId)) continue;

        const categoryProducts = categoryToProductsMap.get(categoryId) || [];
        if (categoryProducts.length === 0) {
          isDev() &&
            console.warn(
              `⚠️ LAYER 4.6: Slider category "${categoryId}" has no products, skipping`
            );
          continue;
        }

        // Helper: simplified title for the range labels / "Use default (X)".
        // "24 in. (Rough Opening 26 in.)" -> "24 in."
        // '8-1/4" Door | 6-1/2" Shelf'    -> "8-1/4 in."
        // One Phase 0 rule (utils.simplifyDimensionTitle), which keeps the
        // Width/Height output byte-identical to the inline regex it replaced.
        // Guarded like this file's other Phase 0 reads: no utils -> the full
        // title (a longer label, never a crash in PPD construction).
        const simplifyTitle = (fullTitle) => {
          const u = window.MurphyDoorConfigurator.utils;
          return u && typeof u.simplifyDimensionTitle === 'function'
            ? u.simplifyDimensionTitle(fullTitle)
            : fullTitle;
        };

        // Collect all variants from all products in this category
        const allVariants = [];
        categoryProducts.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              const fullTitle = variant.title || variant.display_name || '';
              const numericValue = parseNumericDimension(fullTitle);
              if (numericValue !== null) {
                allVariants.push({
                  variantId: String(variant.id),
                  productId: String(product.id),
                  title: fullTitle,
                  simplifiedTitle: simplifyTitle(fullTitle), // NEW: For range labels
                  numericValue: numericValue,
                  price: variant.price || 0,
                  // Preserve full variant reference for selection coordinator
                  variant: variant,
                });
              } else {
                isDev() &&
                  console.warn(
                    `⚠️ LAYER 4.6: Could not parse numeric value from variant "${fullTitle}" in ${categoryId}`
                  );
              }
            });
          }
        });

        if (allVariants.length === 0) {
          isDev() &&
            console.warn(
              `⚠️ LAYER 4.6: No parseable variants for slider category "${categoryId}"`
            );
          continue;
        }

        // Sort variants by numeric value (smallest to largest)
        allVariants.sort((a, b) => a.numericValue - b.numericValue);

        // Build O(1) bidirectional Maps
        const variantIdToIndexMap = new Map();
        const indexToVariantIdMap = new Map();

        allVariants.forEach((entry, index) => {
          variantIdToIndexMap.set(entry.variantId, index);
          indexToVariantIdMap.set(index, entry.variantId);
        });

        // DATA-DRIVEN DEFAULT RESOLUTION: Find default by numeric dimension value (not variant ID)
        // This is resilient to Shopify variant ID changes - defaults are resolved by matching
        // the target dimension value against actual variant data at build time.
        const targetDimension = sliderDefaultDimensions[categoryId];
        let defaultIndex = 0; // Ultimate fallback: first variant

        // PRIORITY 1: the merchant's explicit metaobject choice for THIS
        // product. Matched by variant id against this category's own variant
        // list, so a stale id (variant deleted or recreated in Shopify admin)
        // simply misses and falls through to the dimension logic below --
        // keeping the variant-id resilience that logic was written for.
        const metaobjectVariantId = metaobjectDefaultVariantIds.get(categoryId);
        let resolvedFromMetaobject = false;
        if (metaobjectVariantId) {
          const metaIdx = allVariants.findIndex(
            (v) => String(v.variantId) === metaobjectVariantId
          );
          if (metaIdx !== -1) {
            defaultIndex = metaIdx;
            resolvedFromMetaobject = true;
            isDev() &&
              console.log(
                `✅ LAYER 4.6: Default for ${categoryId} resolved from METAOBJECT variant ${metaobjectVariantId} → index ${metaIdx} (${allVariants[metaIdx].title})`
              );
          } else {
            isDev() &&
              console.warn(
                `⚠️ LAYER 4.6: Metaobject default variant ${metaobjectVariantId} for "${categoryId}" is not among this product's variants — falling back to dimension defaults`
              );
          }
        }

        if (
          !resolvedFromMetaobject &&
          targetDimension !== undefined &&
          targetDimension !== null
        ) {
          // Find variant matching the target dimension value (exact match)
          const exactMatch = allVariants.findIndex(
            (v) => v.numericValue === targetDimension
          );
          if (exactMatch !== -1) {
            defaultIndex = exactMatch;
            isDev() &&
              console.log(
                `✅ LAYER 4.6: Default for ${categoryId} resolved by dimension match: ${targetDimension} → index ${defaultIndex} (${allVariants[defaultIndex].title})`
              );
          } else {
            // No exact match: find closest numeric value
            let closestIdx = 0;
            let closestDiff = Math.abs(
              allVariants[0].numericValue - targetDimension
            );
            for (let i = 1; i < allVariants.length; i++) {
              const diff = Math.abs(
                allVariants[i].numericValue - targetDimension
              );
              if (diff < closestDiff) {
                closestDiff = diff;
                closestIdx = i;
              }
            }
            defaultIndex = closestIdx;
            isDev() &&
              console.warn(
                `⚠️ LAYER 4.6: No exact match for default dimension ${targetDimension} in ${categoryId}, ` +
                  `using closest: ${allVariants[closestIdx].numericValue} (index ${closestIdx}, diff ${closestDiff})`
              );
          }
        } else if (!resolvedFromMetaobject) {
          // Only a genuine "nothing configured anywhere" case warrants this.
          // Without the guard it also fired on the SUCCESS path, warning
          // "using first variant" while defaultIndex was correctly set from
          // the metaobject -- a log that would send the next reader hunting a
          // bug that isn't there.
          isDev() &&
            console.warn(
              `⚠️ LAYER 4.6: No default dimension configured for ${categoryId}, using first variant`
            );
        }

        const sliderMetadata = Object.freeze({
          categoryId: categoryId,
          useSliderUI: true,
          sortedVariants: Object.freeze(allVariants),
          variantIdToIndexMap: variantIdToIndexMap,
          indexToVariantIdMap: indexToVariantIdMap,
          defaultIndex: defaultIndex,
          defaultVariantId: allVariants[defaultIndex].variantId,
          minValue: allVariants[0].numericValue,
          maxValue: allVariants[allVariants.length - 1].numericValue,
          totalSteps: allVariants.length,
          unit: 'in.',
        });

        sliderCategoriesMap.set(categoryId, sliderMetadata);

        isDev() &&
          console.log(
            `✅ [PPD Layer 4.6] Built slider map for ${categoryId}: ${allVariants.length} variants, ` +
              `default index ${defaultIndex} (${allVariants[defaultIndex].title}), ` +
              `range ${allVariants[0].numericValue}-${allVariants[allVariants.length - 1].numericValue} ${sliderMetadata.unit}`
          );
      }

      Object.freeze(sliderCategoriesMap);

      isDev() &&
        console.log(
          `✅ LAYER 4.6: Built sliderCategoriesMap - ${sliderCategoriesMap.size} slider categories (IMMUTABLE)`
        );

      return sliderCategoriesMap;
    },

    classifyCategory: function (categoryProducts, categoryName) {
      isDev() &&
        console.log(
          `🔍 LAYER 4: Analyzing category "${categoryName}" with ${categoryProducts.length} products`
        );

      let multiVariantCount = 0;
      let singleVariantCount = 0;

      // Count product types (treating CompositeProducts as multi-variant)
      categoryProducts.forEach((product) => {
        if (product.type === 'CompositeProduct' || product.isMultiVariant) {
          multiVariantCount++;
          isDev() &&
            console.log(
              `  🧩 Multi-variant: "${product.title}" (${product.variants?.length || 0} variants)`
            );
        } else {
          singleVariantCount++;
          isDev() && console.log(`  🏠 Single-variant: "${product.title}"`);
        }
      });

      // Determine category type based on the 4 classification rules
      let categoryType;
      if (multiVariantCount === 1 && singleVariantCount === 0) {
        categoryType = 'LONELY_MULTI_VARIANT';
      } else if (multiVariantCount > 0 && singleVariantCount === 0) {
        categoryType = 'MULTI_VARIANT_ONLY';
      } else if (multiVariantCount === 0 && singleVariantCount > 0) {
        categoryType = 'SINGLE_VARIANT_ONLY';
      } else {
        categoryType = 'MIX';
      }

      // Check if category contains CompositeProducts (for display strategy)
      const hasCompositeProducts = categoryProducts.some(
        (p) => p.type === 'CompositeProduct'
      );

      // ============================================================================
      // 🚀 UX BEHAVIOR FLAGS (PPD Layer 4 Enhancement)
      // ============================================================================
      // Pre-computed navigation hints that enable O(1) runtime UX decisions
      // These flags eliminate complex O(n) conditionals in Phase 2 UI modules
      // Performance: <1ms per category, improves overall runtime by removing checks
      //
      // ARCHITECTURAL RATIONALE:
      // The 4 category types (LONELY_MULTI_VARIANT, MULTI_VARIANT_ONLY, etc.) are
      // classification labels, but Phase 2 modules need explicit UX behavior flags
      // to handle edge cases like MIX categories with composite + single-variant products
      //
      // These flags answer specific UX questions like:
      // - "Should I show product thumbnails?" → requiresThumbnails
      // - "Should I show the APPLY button?" → requiresApplyButton
      // - "Should I auto-select the product?" → autoSelectProduct
      // - "Can I skip straight to variant browser?" → hasDirectVariant
      // ============================================================================
      const uxFlags = Object.freeze({
        // 🖼️ Show product thumbnails (false for LONELY_MULTI_VARIANT which skips to variants)
        requiresThumbnails: categoryType !== 'LONELY_MULTI_VARIANT',

        // 🎯 Auto-select the only product (true for LONELY_MULTI_VARIANT - skip product choice)
        autoSelectProduct: categoryType === 'LONELY_MULTI_VARIANT',

        // ⚡ Category has direct variant access (LONELY_MULTI_VARIANT pattern)
        hasDirectVariant: categoryType === 'LONELY_MULTI_VARIANT',

        // 🔍 Show variant browser/grid for multi-variant products
        showVariantBrowser: multiVariantCount > 0,

        // ✅ CRITICAL FIX: Show APPLY button for products with multiple variants
        // This fixes the "No Back Casing" bug where single-variant products in MIX
        // categories didn't show APPLY button because we only checked category type
        // Now we check if ANY product in category has multiple variants
        requiresApplyButton: categoryProducts.some(
          (p) => p.variants?.length > 1
        ),

        // 🎚️ Slider UI: Category uses horizontal slider instead of thumbnail grid
        // True for Width/Height/Door Depth (sourced from Phase 0 SLIDER_CATEGORIES constant)
        // Phase 2 modules use this O(1) flag to gate Apply button rendering/visibility
        usesSliderUI:
          window.MurphyDoorConfigurator.CONSTANTS.CATEGORY_TYPES.SLIDER_CATEGORIES.has(
            categoryName
          ),

        // 🏃 One-step selection (single-variant product as only option - auto-apply)
        isSingleStep:
          categoryType === 'SINGLE_VARIANT_ONLY' &&
          categoryProducts.length === 1,

        // 🔮 FUTURE: Material filtering system support
        // Will enable simplified UX toggle (filter mode vs full sort mode)
        supportsMaterialFilter: false,

        // 🎚️ FUTURE: Material filter currently enabled
        // Runtime toggle for simplified vs full UX experience
        materialFilterEnabled: false,
      });

      isDev() &&
        console.log(`🎯 LAYER 4: UX Flags calculated for "${categoryName}":`, {
          requiresThumbnails: uxFlags.requiresThumbnails,
          autoSelectProduct: uxFlags.autoSelectProduct,
          hasDirectVariant: uxFlags.hasDirectVariant,
          showVariantBrowser: uxFlags.showVariantBrowser,
          requiresApplyButton: uxFlags.requiresApplyButton,
          usesSliderUI: uxFlags.usesSliderUI,
          isSingleStep: uxFlags.isSingleStep,
        });

      return {
        type: categoryType,
        displayMode: categoryType, // ✅ ADD displayMode for UI updater compatibility
        classificationResult: categoryType, // ✅ ADD classificationResult for backward compatibility
        products: categoryProducts,
        multiVariantCount: multiVariantCount,
        singleVariantCount: singleVariantCount,
        totalProducts: categoryProducts.length,
        // Display strategy metadata: indicates if category contains CompositeProducts
        // Future use: variant grid rendering can use this to determine display format strategy
        useCompositeVariantDisplay: hasCompositeProducts,
        // 🚀 UX BEHAVIOR FLAGS: Pre-computed navigation hints for Phase 2 modules
        uxFlags: uxFlags,
      };
    },

    /**
     * Build image layer structure for the PPD from original product data
     * Extracts front/back configurator images with z-index metadata
     * @param {Object} productData - Original product data from Layer 3
     * @param {Object} imageLayerStructure - Target structure to populate (front/back Maps)
     */
    buildImageLayerStructure: function (productData, imageLayerStructure) {
      isDev() &&
        console.log('🖼️ LAYER 4: Building image layer structure for PPD');

      // Get config data for z-index metadata
      const configData = JSON.parse(
        document.getElementById('murphy-door-config-data').textContent
      );

      // Extract parent product from original_products (the base door)
      const baseProduct = productData.original_products?.find(
        (p) => !p.configurator_tab_category
      );

      // Initialize parent product layers with validation
      if (baseProduct) {
        if (this.isValidImageUrl(baseProduct.configurator_back_image)) {
          imageLayerStructure.back.set('parent', {
            category: 'parent',
            imageUrl: baseProduct.configurator_back_image,
            zIndex: 0,
          });
          isDev() &&
            console.log(`🖼️ LAYER 4: Added parent back image (z-index: 0)`);
        }
        if (this.isValidImageUrl(baseProduct.configurator_front_image)) {
          imageLayerStructure.front.set('parent', {
            category: 'parent',
            imageUrl: baseProduct.configurator_front_image,
            zIndex: 0,
          });
          isDev() &&
            console.log(`🖼️ LAYER 4: Added parent front image (z-index: 0)`);
        }
      }

      // Initialize category layers with validation
      if (productData.original_products) {
        productData.original_products.forEach((product) => {
          const category = product.configurator_tab_category?.category;
          if (!category) return;

          // Get z-index from config metadata
          const categoryConfig = configData.tabCategories.find(
            (cat) => cat.category === category
          );
          const zIndex = categoryConfig?.media_z_index || 1;

          // Add back image layer if valid and not already present
          if (
            !imageLayerStructure.back.has(category) &&
            this.isValidImageUrl(product.configurator_back_image)
          ) {
            imageLayerStructure.back.set(category, {
              category: category,
              imageUrl: product.configurator_back_image,
              zIndex: zIndex,
            });
            isDev() &&
              console.log(
                `🖼️ LAYER 4: Added category "${category}" back image (z-index: ${zIndex})`
              );
          }

          // Add front image layer if valid and not already present
          if (
            !imageLayerStructure.front.has(category) &&
            this.isValidImageUrl(product.configurator_front_image)
          ) {
            imageLayerStructure.front.set(category, {
              category: category,
              imageUrl: product.configurator_front_image,
              zIndex: zIndex,
            });
            isDev() &&
              console.log(
                `🖼️ LAYER 4: Added category "${category}" front image (z-index: ${zIndex})`
              );
          }
        });

        // 🚨 Ryan, UAT 2026-09-15: "Add LEDs to interior of door" was rendering
        // as the bootstrap slide for "Appearance Upgrades" — a $750 opt-in
        // add-on, not applied — because the loop above is first-come-first-
        // served across original_products, and that product happened to
        // iterate before "No Appearance Upgrades" (the category's configured
        // $0 default). This pass re-points any category with a PLAIN (non
        // material-aware) simplified_default_variant_id — the same field
        // APO_STYLE_MATERIAL_SEED_CATEGORIES' wood-triggered seed already
        // reads (md-config-bl-curated-defaults-coordinator.js) — at the
        // product that actually owns that variant, so the cosmetic bootstrap
        // guess agrees with the category's real default (e.g. its shared
        // transparent placeholder) instead of whichever product iterated
        // first. Material-aware defaults (an object, not a plain id) are left
        // alone: there is no correct single image to prefer before a wood is
        // chosen. This does not touch appliedSelections or the URL — the
        // wood-triggered seed above remains the only thing that writes a real
        // selection, deliberately never at t=0.
        const defaultVariantIdByCategory = new Map();
        configData.tabCategories.forEach((catData) => {
          const raw = catData.simplified_default_variant_id;
          if (raw !== null && raw !== undefined && typeof raw !== 'object') {
            defaultVariantIdByCategory.set(catData.category, String(raw));
          }
        });

        if (defaultVariantIdByCategory.size) {
          productData.original_products.forEach((product) => {
            const category = product.configurator_tab_category?.category;
            if (!category) return;

            const defaultVariantId = defaultVariantIdByCategory.get(category);
            if (!defaultVariantId) return;

            const ownsDefault =
              Array.isArray(product.variants) &&
              product.variants.some(
                (variant) => String(variant.id) === defaultVariantId
              );
            if (!ownsDefault) return;

            const categoryConfig = configData.tabCategories.find(
              (cat) => cat.category === category
            );
            const zIndex = categoryConfig?.media_z_index || 1;

            if (this.isValidImageUrl(product.configurator_back_image)) {
              imageLayerStructure.back.set(category, {
                category: category,
                imageUrl: product.configurator_back_image,
                zIndex: zIndex,
              });
            }
            if (this.isValidImageUrl(product.configurator_front_image)) {
              imageLayerStructure.front.set(category, {
                category: category,
                imageUrl: product.configurator_front_image,
                zIndex: zIndex,
              });
            }
            isDev() &&
              console.log(
                `🖼️ LAYER 4: Category "${category}" bootstrap image re-pointed at its configured default variant ${defaultVariantId}`
              );
          });
        }
      }

      isDev() &&
        console.log(
          `✅ LAYER 4: Image layer structure complete - ${imageLayerStructure.front.size} front layers, ${imageLayerStructure.back.size} back layers`
        );
    },

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    isValidImageUrl: function (url) {
      if (!url || url === '') return false;

      // Handle versioned image URLs (both .jpg and .png)
      if (
        url.includes('/files/') &&
        (url.includes('.jpg?v=') || url.includes('.png?v='))
      ) {
        return true;
      }

      // Handle standard image URLs
      if (
        url.includes('/files/') &&
        (url.includes('.jpg') || url.includes('.png')) &&
        !url.match(/\?72\d{3}$/)
      ) {
        return true;
      }

      return false;
    },

    // 🏗️ BOOTSTRAP FIX: sectionId comes from murphy-door-media-slide-data DOM element, not window.murphyDoorConfig
    // This property is initialized lazily on first access to handle race conditions
    _sectionId: null,
    get sectionId() {
      if (this._sectionId) return this._sectionId;

      // Try reading from DOM element (source of truth)
      try {
        const mediaDataElement = document.getElementById(
          'murphy-door-media-slide-data'
        );
        if (mediaDataElement) {
          const mediaData = JSON.parse(mediaDataElement.textContent);
          if (mediaData?.sectionId) {
            this._sectionId = mediaData.sectionId;
            return this._sectionId;
          }
        }
      } catch (error) {
        isDev() &&
          console.warn(
            '[DataPipeline] Failed to parse media slide data for sectionId:',
            error
          );
      }

      // Fallback: Try legacy window.murphyDoorConfig (may not exist)
      if (window.murphyDoorConfig?.sectionId) {
        this._sectionId = window.murphyDoorConfig.sectionId;
        return this._sectionId;
      }

      return null;
    },
    set sectionId(value) {
      this._sectionId = value;
    },

    // Material relationships Map - populated during pipeline processing
    materialRelationships: new Map(),

    /* LEGACY METHODS - Will be reorganized into pipeline layers */
    /**
     * @deprecated Use createPerfectProductDictionary() instead - this method generates legacy processedData structure
     *
     * DEPRECATION NOTICE:
     * This method is kept for backward compatibility only. The new unified pipeline (createPerfectProductDictionary)
     * should be used for all new code. This legacy method will log deprecation warnings when called.
     *
     * MIGRATION PATH:
     * - OLD: initializer.processExtractedData(rawData) → conductor.processedData
     * - NEW: initializer.createPerfectProductDictionary(rawData) → conductor.perfectProductDictionary
     *
     * ARCHITECTURAL FIX: Process pre-extracted data instead of doing DOM extraction
     * Moved DOM extraction to md-config-core-initializer.js for proper separation of concerns
     *
     * @param {Object} extractedData - Raw data extracted from DOM by initializer
     * @returns {Object} Processed data ready for PPD creation
     */
    processExtractedData: function (extractedData) {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // ============================================================================
      if (bootstrapComplete) {
        const error =
          'CRITICAL ARCHITECTURAL VIOLATION: dataPipeline.processExtractedData() called AFTER bootstrap complete! ' +
          'This DEPRECATED Phase 1 Bootstrap method must never be called at runtime. ' +
          'Use PPD data via conductor delegation instead.';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      isDev() &&
        console.warn(
          '⚠️ DEPRECATED: processExtractedData() is deprecated. Use createPerfectProductDictionary() instead for PPD-based data flow.'
        );
      const startTime = performance.now();

      try {
        // Publish start of configuration loading
        if (window.MurphyDoorConfigurator.CoreEventBus) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.SYSTEM_CONFIGURATION_LOADING_STARTED
          );
        }

        isDev() &&
          console.log(
            '🔧 CONFIG: Processing extracted data (no more DOM access needed)'
          );

        // Use pre-extracted data instead of DOM parsing
        const {
          configData,
          productData,
          woodMetaobjects,
          finishMetaobjects,
          forbiddenConditionGroups,
          forbiddenRulesUnified,
        } = extractedData;

        // Create slug-to-name Maps for Woods and Finishes
        // P3.1.4 FIX: Map SKU to slug format to match variant enhancer GID extraction
        this.woodGidToNameMap = new Map();
        const woodSkuToSlugMap = {
          WN: 'walnut-veneer',
          MP: 'maple-veneer',
          RO: 'red-oak-veneer',
          KA: 'knotty-alder-veneer',
          'EU-O': 'european-oak',
          PG: 'paint-grade',
          TFL: 'tfl',
          WO: 'white-oak-veneer',
        };

        isDev() &&
          console.log(
            `🔍 DEBUG: woodMetaobjects.length = ${woodMetaobjects?.length || 0}`
          );
        woodMetaobjects.forEach((meta) => {
          if (meta.sku && meta.type && woodSkuToSlugMap[meta.sku]) {
            const slug = woodSkuToSlugMap[meta.sku];
            this.woodGidToNameMap.set(slug, meta.type);
            isDev() &&
              console.log(
                `🔍 DEBUG: Wood mapping added - ${meta.sku} → ${slug} → ${meta.type}`
              );
          }
        });

        this.finishGidToNameMap = new Map();
        const finishSkuToSlugMap = {
          CHS: 'cherry-stain',
          MS: 'medium-stain',
          DS: 'dark-stain',
          CS: 'clear-stain',
          BS: 'burnt-coal-stain',
          UN: 'unfinished',
          BL: 'black',
          DD: 'dew-drop',
          DG: 'dorian-gray',
          HN: 'hale-navy',
          MN: 'monaco',
          NS: 'natural-stain',
          WW: 'white-washed-white-oak',
          PO: 'primer-only',
          SC: 'suman-clay',
          SS: 'suman-sand',
          WH: 'white',
          WG: 'willow-grey',
        };

        isDev() &&
          console.log(
            `🔍 DEBUG: finishMetaobjects.length = ${finishMetaobjects?.length || 0}`
          );
        finishMetaobjects.forEach((meta) => {
          const finishName = meta.finish || meta.type;
          if (meta.sku && finishName && finishSkuToSlugMap[meta.sku]) {
            const slug = finishSkuToSlugMap[meta.sku];
            this.finishGidToNameMap.set(slug, finishName);
            isDev() &&
              console.log(
                `🔍 DEBUG: Finish mapping added - ${meta.sku} → ${slug} → ${finishName}`
              );
          }
        });

        isDev() &&
          console.log(
            `🔍 DEBUG: Final map sizes - woodGidToNameMap: ${this.woodGidToNameMap.size}, finishGidToNameMap: ${this.finishGidToNameMap.size}`
          );
        isDev() &&
          console.log(
            `🔍 DEBUG INSTANCE INIT: Instance ${this.constructor.name} built maps successfully`
          );

        // Log initial state in a single, organized group
        // console.group('Murphy Door Configurator - Configuration'); // REMOVED Console log
        // console.log('Initial Data:', { // REMOVED Console log
        //   baseProduct: {
        //     id: productData.id,
        //     title: productData.title,
        //     handle: productData.handle,
        //     images: {
        //       front: productData.configurator_front_image,
        //       back: productData.configurator_back_image,
        //     },
        //     finishes: productData.finishes,
        //     woods: productData.woods,
        //     productionTime: productData.production_time,
        //     variants: productData.variants.map((variant) => ({
        //       id: variant.id,
        //       title: variant.title,
        //       price: variant.price,
        //       finishes: variant.finishes,
        //       woods: variant.woods,
        //       images: {
        //         front: variant.configurator_front_image,
        //         back: variant.configurator_back_image,
        //       },
        //       productionTime: variant.production_time,
        //     })),
        //   },
        //   addOnProducts: productData.add_on_products.map((product) => ({
        //     id: product.id,
        //     title: product.title,
        //     handle: product.handle,
        //     price: product.price,
        //     images: {
        //       front: product.configurator_front_image,
        //       back: product.configurator_back_image,
        //       featured: product.featured_image,
        //     },
        //     finishes: product.finishes,
        //     woods: product.woods,
        //     productionTime: product.production_time,
        //     displayName: product.display_name,
        //     category: product.configurator_tab_category,
        //     variants: product.variants.map((variant) => ({
        //       id: variant.id,
        //       title: variant.title,
        //       price: variant.price,
        //       finishes: variant.finishes,
        //       woods: variant.woods,
        //       images: {
        //         front: variant.configurator_front_image,
        //         back: variant.configurator_back_image,
        //         featured: variant.featured_image,
        //       },
        //       productionTime: variant.production_time,
        //     })),
        //   })),
        // });

        const validCategories = this.collectValidCategories(productData);
        const processedData = this.initializeDataStructures(productData);

        this.processTabData(configData, validCategories, processedData);
        this.processProductData(productData, validCategories, processedData);
        this.initializeImageLayers(productData, processedData);

        // Auto-update log files for development
        this.updateApiLogs(
          configData,
          productData,
          woodMetaobjects,
          finishMetaobjects,
          forbiddenConditionGroups,
          forbiddenRulesUnified
        );

        // Build material relationships from enhanced variants (P3.1.3)
        this.buildEnhancedMaterialRelationships(productData, processedData);

        // P3.1.4 CCP COMPLIANT PRODUCT VIRTUALIZATION: Use enhanced variants but with proper execution order
        // CRITICAL: This MUST happen AFTER processProductData() and buildEnhancedMaterialRelationships()
        if (
          window.MurphyDoorConfigurator.compositeProductPreprocessor &&
          this.enhancedVariants &&
          this.enhancedVariants.length > 0
        ) {
          isDev() &&
            console.log(
              '🧬 CONFIG: Applying CCP-compliant product virtualization using enhanced variants (after product processing)'
            );

          // 🎯 CCP FIX: Build enhanced products by reconstructing from processedData (now populated by processProductData)
          const enhancedProducts = [];

          // Iterate through processed product structure (now fully populated)
          processedData.productStructure.addOnProducts.forEach(
            (productMetadata, productId) => {
              // Find enhanced variants that belong to this product by matching variant IDs
              const productEnhancedVariants = this.enhancedVariants.filter(
                (enhancedVariant) => {
                  // Enhanced variants retain original variant IDs, match by ID
                  return productMetadata.variants.some(
                    (originalVariant) =>
                      originalVariant.id === enhancedVariant.id
                  );
                }
              );

              // If we found enhanced variants for this product, create enhanced product
              if (productEnhancedVariants.length > 0) {
                enhancedProducts.push({
                  id: productId,
                  title: productMetadata.title || `Product ${productId}`,
                  display_name: productMetadata.display_name,
                  configurator_tab_category:
                    productMetadata.configurator_tab_category,
                  category: productMetadata.category,
                  variants: productEnhancedVariants, // Use enhanced variants instead of raw
                  // Copy other necessary properties from processedData
                  ...productMetadata,
                });
              }
            }
          );

          isDev() &&
            console.log(
              `🎯 CONFIG: Reconstructed ${enhancedProducts.length} enhanced products from ${this.enhancedVariants.length} enhanced variants (CCP compliant)`
            );

          const virtualizedProducts =
            window.MurphyDoorConfigurator.compositeProductPreprocessor.process(
              enhancedProducts
            );
          processedData.groupedProducts = virtualizedProducts;

          // P3.1.4 TEST COMPATIBILITY: Also store composite products separately for test access
          processedData.compositeProducts = virtualizedProducts.filter(
            (product) => product.isComposite === true
          );
          isDev() &&
            console.log(
              `✅ CONFIG: CCP-compliant virtualization complete - ${virtualizedProducts.length} grouped products (${processedData.compositeProducts.length} composite) created with enhanced variant data`
            );

          // 🔧 CRITICAL FIX: Add CompositeProducts to addOnProducts Map
          // CompositeProducts have new IDs (composite-tfl, composite-paint-grade) so they need to be added
          virtualizedProducts.forEach((product) => {
            if (
              product.isComposite &&
              !processedData.productStructure.addOnProducts.has(product.id)
            ) {
              // 🔥 TWO-STEP DETECTION: Use centralized helper for consistent logic
              // Legacy pipeline uses thumbnail count as proxy for product count (1:1 relationship)
              const categoryThumbnails =
                processedData.categoryStructure.get(product.category)
                  ?.displayElements.thumbnails || [];

              // Add CompositeProduct with COMPLETE schema matching regular products
              // This ensures cart manager, analytics, and all downstream consumers work correctly
              processedData.productStructure.addOnProducts.set(product.id, {
                // Product type & display configuration
                type: 'multi-variant',
                displayMode: this._calculateDisplayMode(
                  product,
                  categoryThumbnails.length
                ),
                category: product.category,
                isComposite: true,

                // Core product identification (matching regular product schema)
                id: product.id,
                title: product.title,
                display_name: product.display_name,
                handle: product.handle,

                // Pricing data (CRITICAL for cart manager)
                price: product.price || 0,
                compareAtPrice: product.compareAtPrice || 0,
                priceRange: product.priceRange,

                // Images (CRITICAL for UI, cart, and analytics)
                image: product.image || product.featured_image,
                featured_image: product.featured_image || product.image,
                configuratorBackImage: product.configuratorBackImage || '',
                configuratorFrontImage: product.configuratorFrontImage || '',
                images: product.images || [],

                // Production time & availability
                production_time: product.production_time || {
                  low: null,
                  high: null,
                },
                availability: product.availability || 'available',

                // Variants (CRITICAL for variant selection & cart)
                variants: (product.variants || []).map((variant) => ({
                  id: variant.id,
                  title: variant.title,
                  productionTime: {
                    low:
                      variant.production_time?.low ||
                      variant.productionTime?.low ||
                      null,
                    high:
                      variant.production_time?.high ||
                      variant.productionTime?.high ||
                      null,
                  },
                  price: variant.price,
                  image: variant.featured_image || variant.image,
                  configuratorBackImage:
                    variant.configurator_back_image ||
                    variant.configuratorBackImage,
                  configuratorFrontImage:
                    variant.configurator_front_image ||
                    variant.configuratorFrontImage,
                  properties: {
                    _Configuration_ID: null,
                    _Is_Base_Product: false,
                    _Category: product.category,
                  },
                })),

                // Material compatibility (CRITICAL for wood/finish filtering)
                woods: product.woods || [],
                finishes: product.finishes || [],
                materials: product.materials || [],

                // Metadata (for analytics, cart, and tracking)
                vendor: product.vendor || 'Murphy Door',
                productType:
                  product.productType ||
                  product.product_type ||
                  'Door Component',
                tags: product.tags || [],
                configurator_tab_category:
                  product.configurator_tab_category || {
                    category: product.category,
                  },

                // CompositeProduct-specific metadata
                compositeGroupName: product.compositeGroupName,
                compositeProductCount: product.compositeProductCount,
                variantCount: product.variantCount,
                sourceProducts: product.sourceProducts || [],

                // Cart properties
                cartProperties: {
                  isAddOn: true,
                  requiresConfiguration: true,
                },
              });
            }
          });
        } else {
          isDev() &&
            console.error(
              '❌ CCP VIOLATION BLOCKED: CompositeProductPreprocessor or enhanced variants not available - cannot proceed with raw data fallbacks'
            );
          processedData.groupedProducts = [];
          processedData.compositeProducts = [];
        }

        // Group add-on products using local implementation
        const groupedAddOnProducts = this.groupProductsByDisplayName(
          productData.add_on_products
        );

        // Store in processedData (allowed for data processing pipeline modules)
        processedData.productStructure.groupedAddOnProducts =
          groupedAddOnProducts;

        // Log final processed state
        // console.log('Processed Configuration:', { // REMOVED Console log
        //   baseProduct: {
        //     ...processedData.productStructure.baseProduct,
        //     hasValidImages: {
        //       front: this.isValidImageUrl(
        //         processedData.productStructure.baseProduct
        //           .configuratorFrontImage
        //       ),
        //       back: this.isValidImageUrl(
        //         processedData.productStructure.baseProduct
        //           .configuratorBackImage
        //       ),
        //     },
        //   },
        //   tabs: Array.from(processedData.tabStructure.entries()).map(
        //     ([tabId, tabValue]) => ({
        //       id: tabId,
        //       name: tabValue.name,
        //       loadOrder: tabValue.loadOrder,
        //     })),
        //   // REMOVED Console log
        // });

        // console.groupEnd(); // REMOVED Console log

        // 🔧 CCP COMPLIANT: Publish configuration loaded event with processed data structures
        // NOTE: Config module is a data processing pipeline module - exempted from data publishing restrictions
        if (window.MurphyDoorConfigurator.CoreEventBus) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.SYSTEM_CONFIGURATION_LOADED,
            {
              processedData: {
                tabStructure: processedData.tabStructure,
                categoryStructure: processedData.categoryStructure,
                productStructure: processedData.productStructure,
                imageLayerStructure: processedData.imageLayerStructure,
                validationStructure: processedData.validationStructure,
                materialRelationships:
                  processedData.productStructure.materialRelationships,
              },
              timestamp: performance.now() - startTime,
            }
          );
        }

        // Add the maps to the final processed data for event payload
        processedData.woodGidToNameMap = this.woodGidToNameMap;
        processedData.finishGidToNameMap = this.finishGidToNameMap;

        // P3.1.4 PIPELINE INTEGRATION: Ensure enhanced variants are available globally
        this.processedData = processedData; // Store reference for conductor access

        // P3.1.4 DEBUG: Test property setting mechanism
        this.testProperty = new Map([['test', 'works']]);

        return processedData;
      } catch (error) {
        // Route through centralized error handler (DRY: single publication point)
        window.MurphyDoorConfigurator.utils.handleSystemError({
          type: 'ConfigurationError',
          error: error,
          source: 'dataPipeline.processConfiguratorData',
          impact: 'critical',
        });
        throw error;
      }
    },

    /**
     * DEPRECATED LEGACY METHOD: Use new initializer flow instead
     *
     * NEW FLOW (Preferred):
     * 1. Initializer extracts DOM data
     * 2. Initializer calls createPerfectProductDictionary() for PPD
     * 3. Initializer calls processExtractedData() for legacy processedData
     * 4. Conductor receives both structures and skips this method
     *
     * LEGACY FLOW (Fallback only):
     * - Conductor calls this method if processedData not pre-provided
     * - This method extracts DOM data and delegates to processExtractedData
     * - Triggers deprecation warning to alert developers
     *
     * @deprecated Use initializer flow instead (md-config-core-initializer.js)
     * @returns {Object} processedData structure for conductor compatibility
     */
    processConfiguratorData: function () {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // ============================================================================
      if (bootstrapComplete) {
        const error =
          'CRITICAL ARCHITECTURAL VIOLATION: dataPipeline.processConfiguratorData() called AFTER bootstrap complete! ' +
          'This DEPRECATED Phase 1 Bootstrap method must never be called at runtime. ' +
          'Use PPD data via conductor delegation instead.';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      isDev() &&
        console.warn(
          '🚨 DEPRECATED: Using legacy processConfiguratorData() - new initializer flow should have provided data'
        );

      // Extract DOM data (legacy pattern)
      const extractedData = {
        configData: JSON.parse(
          document.getElementById('murphy-door-config-data').textContent
        ),
        productData: JSON.parse(
          document.getElementById('murphy-door-product-data').textContent
        ),
        woodMetaobjects: JSON.parse(
          document.getElementById('murphy-door-wood-metaobjects-data')
            ?.textContent || '[]'
        ),
        finishMetaobjects: JSON.parse(
          document.getElementById('murphy-door-finish-metaobjects-data')
            ?.textContent || '[]'
        ),
        // Unified forbidden combinations system
        forbiddenConditionGroups: JSON.parse(
          document.getElementById('murphy-door-forbidden-condition-groups')
            ?.textContent || '[]'
        ),
        forbiddenRulesUnified: JSON.parse(
          document.getElementById('murphy-door-forbidden-rules-unified')
            ?.textContent || '[]'
        ),
      };

      // Delegate to new method
      return this.processExtractedData(extractedData);
    },

    collectValidCategories(productData) {
      const validCategories = new Set();
      const imageValidation = new Map();

      productData.add_on_products.forEach((product) => {
        const category = product.configurator_tab_category?.category;
        if (category) {
          validCategories.add(category);

          if (!imageValidation.has(category)) {
            imageValidation.set(category, {
              hasBackImage: false,
              hasFrontImage: false,
            });
          }

          const validation = imageValidation.get(category);
          validation.hasBackImage =
            validation.hasBackImage ||
            this.isValidImageUrl(product.configurator_back_image);
          validation.hasFrontImage =
            validation.hasFrontImage ||
            this.isValidImageUrl(product.configurator_front_image);

          product.variants.forEach((variant) => {
            validation.hasBackImage =
              validation.hasBackImage ||
              this.isValidImageUrl(variant.configurator_back_image);
            validation.hasFrontImage =
              validation.hasFrontImage ||
              this.isValidImageUrl(variant.configurator_front_image);
          });
        }
      });

      return validCategories;
    },

    initializeDataStructures(productData) {
      return {
        tabStructure: new Map(),
        categoryStructure: new Map(),
        productStructure: {
          baseProduct: {
            id: productData.id,
            title: productData.title,
            production_time: {
              low: productData.production_time?.low || null,
              high: productData.production_time?.high || null,
            },
            variant: productData.variants[0],
            configuratorBackImage: productData.configurator_back_image,
            configuratorFrontImage: productData.configurator_front_image,
            finishes: productData.finishes || [],
            woods: productData.woods || [],
          },
          addOnProducts: new Map(),
          materialRelationships: new Map(), // Will be populated with enhanced material relationships
        },
        imageLayerStructure: {
          front: new Map(),
          back: new Map(),
        },
      };
    },

    /* Structure Processing Methods */
    processTabData(configData, validCategories, processedData) {
      configData.configuratorTabs.forEach((tab) => {
        const validTabCategories = configData.tabCategories
          .filter((cat) => cat.configurator_tab === tab.id)
          .filter((cat) => validCategories.has(cat.category))
          .sort((a, b) => a.load_order - b.load_order);

        if (validTabCategories.length > 0) {
          processedData.tabStructure.set(tab.id, {
            id: tab.id,
            name: tab.tab_name,
            loadOrder: tab.load_order,
            categories: validTabCategories.map((c) => c.category), // Simple array for UI
            categoryData: validTabCategories.map((c) => ({
              // Detailed data for image handling
              category: c.category,
              mediaZIndex: c.media_z_index,
              layerRole: c.layer_role || null,
              loadOrder: c.load_order,
            })),
            requiredSelections: validTabCategories.length,
          });
        }
      });
    },

    processProductData(productData, validCategories, processedData) {
      const configData = JSON.parse(
        document.getElementById('murphy-door-config-data').textContent
      );

      // First pass: Create category structures and add products
      productData.add_on_products.forEach((product) => {
        const category = product.configurator_tab_category?.category;
        if (!category || !validCategories.has(category)) return;

        this.ensureCategoryStructure(
          category,
          product,
          processedData,
          configData
        );
        this.addProductToCategory(product, category, processedData);
      });

      // Second pass: Add products to product structure
      productData.add_on_products.forEach((product) => {
        const category = product.configurator_tab_category?.category;
        if (!category || !validCategories.has(category)) return;

        this.addProductToStructure(product, category, processedData);
      });

      // Legacy forbidden combos removed - unified system handles all rules via PPD unifiedRules Map
      // No legacy Maps needed - forbidden validation uses unifiedRules exclusively

      if (
        productData.add_on_products &&
        Array.isArray(productData.add_on_products)
      ) {
        productData.add_on_products.forEach((product) => {
          const categoryName = product.configurator_tab_category?.category;
          if (categoryName && validCategories.has(categoryName)) {
          } else {
          }
        });
      }
    },

    initializeImageLayers(productData, processedData) {
      const configData = JSON.parse(
        document.getElementById('murphy-door-config-data').textContent
      );

      // Initialize parent product layers with validation
      if (
        this.isValidImageUrl(productData.configurator_back_image) ||
        this.isValidImageUrl(productData.configurator_front_image)
      ) {
        processedData.imageLayerStructure.back.set('parent', {
          image: productData.configurator_back_image,
          zIndex: 0,
        });
        processedData.imageLayerStructure.front.set('parent', {
          image: productData.configurator_front_image,
          zIndex: 0,
        });
      }

      // Initialize category layers with validation
      productData.add_on_products.forEach((product) => {
        const category = product.configurator_tab_category?.category;
        if (!category) return;

        const categoryConfig = configData.tabCategories.find(
          (cat) => cat.category === category
        );
        const zIndex = categoryConfig?.media_z_index || 1;

        if (
          !processedData.imageLayerStructure.back.has(category) &&
          this.isValidImageUrl(product.configurator_back_image)
        ) {
          processedData.imageLayerStructure.back.set(category, {
            image: product.configurator_back_image,
            zIndex,
          });
        }

        if (
          !processedData.imageLayerStructure.front.has(category) &&
          this.isValidImageUrl(product.configurator_front_image)
        ) {
          processedData.imageLayerStructure.front.set(category, {
            image: product.configurator_front_image,
            zIndex,
          });
        }
      });
    },

    /* Category Management Methods */
    ensureCategoryStructure(category, product, processedData, configData) {
      if (!processedData.categoryStructure.has(category)) {
        // Get all products for this category from the raw configuration data
        // const configDataObj = JSON.parse( // REMOVED - Unused variable
        const productDataObj = JSON.parse(
          document.getElementById('murphy-door-product-data').textContent
        );

        const categoryProducts = productDataObj.add_on_products.filter(
          (p) => p.configurator_tab_category?.category === category
        );

        // Determine display mode based on product count and types
        let displayMode = 'standard';
        if (
          categoryProducts.length === 1 &&
          categoryProducts[0].variants.length > 1
        ) {
          displayMode = 'directVariant';
        }

        const categoryConfig = configData.tabCategories.find(
          (cat) => cat.category === category
        );

        processedData.categoryStructure.set(category, {
          id: category,
          name: categoryConfig?.category_name || `Category ${category}`,
          displayElements: {
            thumbnails: [],
            displayMode: displayMode,
          },
          validationRules: {
            requiresVariantSelection: product.variants.length > 1,
            minimumSelection: 1,
            maximumSelection: 1,
          },
          mediaZIndex: categoryConfig?.media_z_index || 0,
          loadOrder: categoryConfig?.load_order || 0,
        });
      }
    },

    addProductToCategory(product, category, processedData) {
      processedData.categoryStructure
        .get(category)
        .displayElements.thumbnails.push({
          id: product.id,
          category: category,
          image: {
            src: product.featured_image?.src || '',
            alt: product.featured_image?.alt || product.title,
          },
          title: product.display_name,
          price: product.price,
        });
    },

    addProductToStructure(product, category, processedData) {
      const categoryThumbnails =
        processedData.categoryStructure.get(category)?.displayElements
          .thumbnails || [];

      const isSingleTwoStep =
        product.variants.length > 1 && categoryThumbnails.length === 1;

      processedData.productStructure.addOnProducts.set(product.id, {
        type:
          product.variants.length === 1 ? 'single-variant' : 'multi-variant',
        displayMode: isSingleTwoStep ? 'directVariant' : 'standard',
        category: category,
        isComposite: product.isComposite || false, // Preserve composite flag from preprocessor
        production_time: {
          low: product.production_time?.low || null,
          high: product.production_time?.high || null,
        },
        configuratorBackImage: product.configurator_back_image,
        configuratorFrontImage: product.configurator_front_image,
        variants: product.variants.map((variant) => ({
          id: variant.id,
          title: variant.title,
          productionTime: {
            low: variant.production_time?.low || null,
            high: variant.production_time?.high || null,
          },
          price: variant.price,
          image: variant.featured_image,
          configuratorBackImage: variant.configurator_back_image,
          configuratorFrontImage: variant.configurator_front_image,
          properties: {
            _Configuration_ID: null,
            _Is_Base_Product: false,
            _Category: category,
            // Removed legacy _Product_Type - use dynamic isSingleVariant() classification instead
          },
        })),
        cartProperties: {
          isAddOn: true,
          requiresConfiguration: true,
        },
      });
    },

    // Build material relationships from enhanced variants with wood_sku/finish_sku properties
    buildMaterialRelationshipsFromEnhancedVariants: function (
      enhancedVariants
    ) {
      isDev() &&
        console.log(
          '🔧 BUILDING SKU-BASED MATERIAL RELATIONSHIPS: Processing',
          enhancedVariants.length,
          'variants with material SKUs'
        );

      if (
        !enhancedVariants ||
        !Array.isArray(enhancedVariants) ||
        enhancedVariants.length === 0
      ) {
        isDev() &&
          console.log(
            '📋 MATERIAL RELATIONSHIPS: Empty input, returning empty Map'
          );
        return new Map();
      }

      const materialRelationships = new Map();
      const processedCombinations = new Set();

      const startTime = performance.now();

      for (const variant of enhancedVariants) {
        // Skip variants without material SKUs (agnostic variants)
        if (!variant.wood_sku || !variant.finish_sku) {
          continue;
        }

        // SKU-based combination key (e.g., "PG::UN" for Paint Grade + Unfinished)
        const combinationKey = `${variant.wood_sku}::${variant.finish_sku}`;

        // Skip if we've already processed this combination
        if (processedCombinations.has(combinationKey)) {
          // Add variant ID to existing relationship
          const existingRelationship =
            materialRelationships.get(combinationKey);
          if (
            existingRelationship &&
            !existingRelationship.variants.includes(variant.id)
          ) {
            existingRelationship.variants.push(variant.id);
          }
          continue;
        }

        // Get names directly from variant (already extracted in Layer 1!)
        const woodName = variant.wood_name;
        const finishName = variant.finish_name;

        if (!woodName || !finishName) {
          isDev() &&
            console.log(
              '⚠️ SKIPPING VARIANT:',
              variant.id || 'unknown',
              '- Missing wood or finish names despite having SKUs'
            );
          isDev() &&
            console.log(
              `🔍 DEBUG: wood_sku="${variant.wood_sku}" → wood_name="${woodName}"`
            );
          isDev() &&
            console.log(
              `🔍 DEBUG: finish_sku="${variant.finish_sku}" → finish_name="${finishName}"`
            );
          continue;
        }

        const relationship = {
          key: combinationKey,
          wood_sku: variant.wood_sku,
          finish_sku: variant.finish_sku,
          wood_name: woodName,
          finish_name: finishName,
          variants: [variant.id],
        };

        materialRelationships.set(combinationKey, relationship);
        processedCombinations.add(combinationKey);
      }

      const processingTime = performance.now() - startTime;
      isDev() &&
        console.log(
          '🎯 MATERIAL RELATIONSHIPS COMPLETE:',
          materialRelationships.size,
          'relationships built in',
          processingTime.toFixed(2),
          'ms'
        );

      return materialRelationships;
    },

    // Pipeline integration method that collects enhanced variants and builds material relationships
    buildEnhancedMaterialRelationships: function (productData, processedData) {
      isDev() &&
        console.log(
          '🔧 PIPELINE INTEGRATION: Building enhanced material relationships'
        );

      // 🔧 Initialize variant enhancer if not already initialized
      // This is needed for legacy pipeline flow
      if (
        window.MurphyDoorConfigurator.supportVariantEnhancer &&
        !window.MurphyDoorConfigurator.supportVariantEnhancer.initialized
      ) {
        // Extract metaobjects from DOM if not already available
        const woodMetaobjects = JSON.parse(
          document.getElementById('murphy-door-wood-metaobjects-data')
            ?.textContent || '[]'
        );
        const finishMetaobjects = JSON.parse(
          document.getElementById('murphy-door-finish-metaobjects-data')
            ?.textContent || '[]'
        );

        window.MurphyDoorConfigurator.supportVariantEnhancer.initialize(
          woodMetaobjects,
          finishMetaobjects
        );
      }

      // P3.1.4 FIX: Collect variants with their parent product context for proper enhancement
      const variantProductPairs = [];

      // Add main product variants with context
      if (productData.variants && Array.isArray(productData.variants)) {
        productData.variants.forEach((variant) => {
          variantProductPairs.push({
            variant: variant,
            productContext: {
              woods: productData.woods || [],
              finishes: productData.finishes || [],
              featured_image: productData.featured_image || null,
            },
          });
        });
      }

      // Add variants from add-on products with their specific context
      if (
        productData.add_on_products &&
        Array.isArray(productData.add_on_products)
      ) {
        productData.add_on_products.forEach((product) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant) => {
              variantProductPairs.push({
                variant: variant,
                productContext: {
                  woods: product.woods || [],
                  finishes: product.finishes || [],
                  featured_image: product.featured_image || null,
                },
              });
            });
          }
        });
      }

      isDev() &&
        console.log(
          '📊 VARIANT-PRODUCT PAIRS COLLECTED:',
          variantProductPairs.length,
          'total variant-product pairs'
        );

      // Enhance variants if variant enhancer is available
      let enhancedVariants = [];
      if (window.MurphyDoorConfigurator.supportVariantEnhancer) {
        isDev() &&
          console.log(
            '✨ VARIANT ENHANCER AVAILABLE: Enhancing variants with correct product-specific wood/finish GIDs'
          );
        enhancedVariants = variantProductPairs.map((pair) => {
          const enhanced =
            window.MurphyDoorConfigurator.supportVariantEnhancer.enhanceVariant(
              pair.variant,
              pair.productContext
            );
          return enhanced;
        });
      } else {
        isDev() &&
          console.warn(
            '⚠️ VARIANT ENHANCER NOT AVAILABLE: Using raw variants (may have limited material intelligence)'
          );
        enhancedVariants = variantProductPairs.map((pair) => pair.variant);
      }

      // Build SKU-based material relationships from enhanced variants
      const materialRelationships =
        this.buildMaterialRelationshipsFromEnhancedVariants(enhancedVariants);

      // 🔧 CCP FIX: Store enhanced variants in config module for conductor delegation access
      this.enhancedVariants = enhancedVariants;
      isDev() &&
        console.log(
          `📊 GLOBAL ACCESS: Stored ${enhancedVariants.length} enhanced variants in config module for global access`
        );

      // Store in processed data structure (allowed for data processing pipeline modules)
      processedData.enhancedVariants = enhancedVariants;
      processedData.productStructure.materialRelationships =
        materialRelationships;

      // Store in main config object for conductor delegation
      this.materialRelationships = materialRelationships;

      // Publish event for CCP compliance
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.SYSTEM_MATERIAL_RELATIONSHIPS_BUILT,
          {
            relationshipCount: materialRelationships.size,
            variantCount: enhancedVariants.length,
            enhancedVariantsAvailable:
              !!window.MurphyDoorConfigurator.supportVariantEnhancer,
          }
        );
      }

      isDev() &&
        console.log(
          '🎯 ENHANCED MATERIAL RELATIONSHIPS COMPLETE:',
          materialRelationships.size,
          'relationships from',
          enhancedVariants.length,
          'enhanced variants'
        );
    },

    // Legacy material relationships method (deprecated)
    buildMaterialRelationships: function (/* product, processedData */) {
      isDev() &&
        console.warn(
          '⚠️ buildMaterialRelationships is deprecated, use buildEnhancedMaterialRelationships instead'
        );
    },

    // Legacy processForbiddenCombinations removed - unified system handles all rules

    /**
     * ✅ Layer 4.7: Load Unified Forbidden Combination Rules (GUI-Friendly)
     * Loads condition groups and unified rules from extractedData parameter (direct data flow).
     *
     * ARCHITECTURAL FIX: Reads from extractedData parameter instead of DOM script elements.
     * Data flows directly through the pipeline: initializer -> rawData -> pipeline layers -> here.
     * No DOM intermediate step needed (eliminates brittle DOM injection coupling).
     *
     * @param {Object} extractedData - Pipeline data containing forbiddenConditionGroups and forbiddenRulesUnified
     * @returns {{ conditionGroups: Map, rules: Map }} Processed forbidden rules data
     */
    _loadUnifiedForbiddenRules: function (extractedData) {
      isDev() &&
        console.log(
          '🔄 DATA PIPELINE: Loading unified forbidden rules (condition groups + rules)'
        );

      try {
        // ═══════════════════════════════════════════════════════
        // Step 1: Load Condition Groups (Building Blocks)
        // Read from extractedData parameter (direct data flow, no DOM)
        // ═══════════════════════════════════════════════════════
        const conditionGroupsData =
          extractedData.forbiddenConditionGroups || [];

        if (!Array.isArray(conditionGroupsData)) {
          isDev() &&
            console.warn(
              '⚠️ DATA PIPELINE: Condition groups data is not an array',
              typeof conditionGroupsData
            );
          return { conditionGroups: new Map(), rules: new Map() };
        }

        if (conditionGroupsData.length === 0) {
          isDev() &&
            console.warn(
              '⚠️ DATA PIPELINE: No condition groups found in extractedData'
            );
          return { conditionGroups: new Map(), rules: new Map() };
        }

        isDev() &&
          console.log(
            `✅ DATA PIPELINE: Loaded ${conditionGroupsData.length} condition groups`
          );

        // ── Live product → variant index, for ALL_IN_PRODUCT expansion ──────────
        // Condition groups normally enumerate variant IDs by hand. That list is a
        // snapshot: a variant created after the group was authored is simply absent, so
        // the rule silently stops matching it. Reproduced live 2026-09-03 — the enabled
        // "Upper Cabinet Doors <-> Book Latch" rule let both apply, because the Aug-2026
        // finish variants were never added to its groups (rule authored 2026-02-17,
        // listed variants created 2025-07-29, the one selected created 2026-08-25).
        //
        // A group with match_type ALL_IN_PRODUCT declares intent instead of a snapshot —
        // "every variant of this product" — and is expanded here at PPD build time, so a
        // new variant is covered the moment it exists with no rule edit and no deploy.
        //
        // Expanding from the PPD's own product data is COMPLETE for this purpose: the
        // customer can only select variants the PPD contains, so a variant absent here is
        // also unselectable and cannot form a forbidden pair.
        const variantIdsByProduct = new Map();
        (extractedData.productData?.add_on_products || []).forEach(function (prod) {
          if (!prod || prod.id == null) return;
          const key = String(prod.id);
          const ids = (prod.variants || [])
            .map(function (v) { return v && v.id != null ? String(v.id) : null; })
            .filter(Boolean);
          if (ids.length) {
            variantIdsByProduct.set(
              key,
              (variantIdsByProduct.get(key) || []).concat(ids)
            );
          }
        });

        // Build lookup map for condition groups
        const conditionGroupMap = new Map(
          conditionGroupsData
            .filter(function (group) {
              return group.enabled !== false;
            }) // Only enabled groups
            .map(function (group) {
              // Extract product ID (remove GID prefix)
              // CRITICAL FIX: group.check_product is a FULL OBJECT {id, title, ...}, not a string!
              const productId = group.check_product
                ? typeof group.check_product === 'object' &&
                  group.check_product !== null
                  ? String(group.check_product.id).replace(
                      'gid://shopify/Product/',
                      ''
                    )
                  : String(group.check_product).replace(
                      'gid://shopify/Product/',
                      ''
                    )
                : null;

              // Extract variant IDs (remove GID prefixes)
              // CRITICAL FIX: required_variants is ARRAY OF OBJECTS {id, title, ...}, not strings!
              const variantIds = (group.required_variants || []).map(
                function (v) {
                  const variantId =
                    typeof v === 'object' && v !== null ? v.id : v;
                  return String(variantId).replace(
                    'gid://shopify/ProductVariant/',
                    ''
                  );
                }
              );

              // ALL_IN_PRODUCT: ignore the hand-written list and use every variant the
              // product currently has. Falls back to the enumerated list when expansion
              // yields nothing (product absent from this PPD, e.g. a rule that targets a
              // different door line) — degrading to today's behaviour is right; degrading
              // to an empty set would silently disable the rule entirely.
              let effectiveVariantIds = variantIds;
              if (group.match_type === 'ALL_IN_PRODUCT') {
                const expanded = productId
                  ? variantIdsByProduct.get(String(productId)) || []
                  : [];
                if (expanded.length) {
                  effectiveVariantIds = expanded;
                } else {
                  isDev() &&
                    console.warn(
                      '⚠️ DATA PIPELINE: condition group "' +
                        (group.name || group.id) +
                        '" is ALL_IN_PRODUCT but product ' +
                        productId +
                        ' has no variants in this PPD — falling back to its ' +
                        variantIds.length +
                        ' enumerated variant(s).'
                    );
                }
              }

              // expand_from_product: ADD every current variant of the named product to the
              // list. Distinct from check_product on purpose — check_product also selects
              // the EVALUATION MODE (a null productId means "cross-product check across
              // ALL products", see the branch in md-config-bl-forbidden-validator.js), so
              // setting it to fix staleness would silently switch 30 working groups from
              // cross-product to single-product matching. This field only ever composes the
              // variant list; it never reaches productId, so the mode cannot change.
              //
              // UNION, never replace: expansion can only ADD coverage. A group that matches
              // today still matches after expansion, which is what makes populating this
              // field on live rules a reviewable, non-regressive data change.
              const expandFromRaw = group.expand_from_product;
              if (expandFromRaw) {
                const expandFromId = String(
                  typeof expandFromRaw === 'object' && expandFromRaw !== null
                    ? expandFromRaw.id
                    : expandFromRaw
                ).replace('gid://shopify/Product/', '');
                const extra = variantIdsByProduct.get(expandFromId) || [];
                if (extra.length) {
                  const merged = new Set(effectiveVariantIds);
                  extra.forEach(function (id) {
                    merged.add(id);
                  });
                  effectiveVariantIds = Array.from(merged);
                } else {
                  isDev() &&
                    console.warn(
                      '⚠️ DATA PIPELINE: condition group "' +
                        (group.name || group.id) +
                        '" sets expand_from_product=' +
                        expandFromId +
                        ', but that product has no variants in this PPD — list left as-is (' +
                        effectiveVariantIds.length +
                        ' variant(s)).'
                    );
                }
              }

              return [
                group.id,
                {
                  id: group.id,
                  name: group.name,
                  description: group.description || '',
                  matchType: group.match_type,
                  productId: productId,
                  variantIds: effectiveVariantIds,
                  minRange: group.min_range || null,
                  maxRange: group.max_range || null,
                  enabled: group.enabled !== false,
                },
              ];
            })
        );

        isDev() &&
          console.log('✅ DATA PIPELINE: Condition groups processed', {
            totalGroups: conditionGroupMap.size,
            sampleGroup:
              conditionGroupMap.size > 0
                ? conditionGroupMap.values().next().value
                : null,
          });

        // ═══════════════════════════════════════════════════════
        // Step 2: Load Unified Rules
        // Read from extractedData parameter (direct data flow, no DOM)
        // ═══════════════════════════════════════════════════════
        const rulesData = extractedData.forbiddenRulesUnified || [];

        if (!Array.isArray(rulesData)) {
          isDev() &&
            console.warn(
              '⚠️ DATA PIPELINE: Unified rules data is not an array',
              typeof rulesData
            );
          return { conditionGroups: conditionGroupMap, rules: new Map() };
        }

        if (rulesData.length === 0) {
          isDev() &&
            console.warn(
              '⚠️ DATA PIPELINE: No unified rules found in extractedData'
            );
          return { conditionGroups: conditionGroupMap, rules: new Map() };
        }

        isDev() &&
          console.log(
            `✅ DATA PIPELINE: Loaded ${rulesData.length} unified rules`
          );

        // Process rules and hydrate condition groups
        const unifiedRules = rulesData
          .filter(function (rule) {
            return rule.enabled !== false;
          }) // Only enabled rules
          .map(function (rule) {
            // Hydrate condition groups (resolve references)
            const conditionGroups = (rule.condition_groups || [])
              .map(function (ref) {
                const groupId = ref.id || ref;
                const group = conditionGroupMap.get(groupId);

                if (!group) {
                  isDev() &&
                    console.warn(
                      '⚠️ DATA PIPELINE: Condition group ' +
                        groupId +
                        ' not found for rule "' +
                        rule.rule_name +
                        '"'
                    );
                  return null;
                }

                return group;
              })
              .filter(function (g) {
                return g !== null && g.enabled;
              }); // Only enabled groups

            // Extract target variant IDs
            // CRITICAL FIX: rule.target_variants is an ARRAY OF OBJECTS, not strings!
            // Each element has {id, title, price, ...} - extract the .id property
            const targetVariants = (rule.target_variants || []).map(
              function (v) {
                // v can be either a full variant object {id, title, ...} or a GID string
                const variantId =
                  typeof v === 'object' && v !== null ? v.id : v;
                return String(variantId).replace(
                  'gid://shopify/ProductVariant/',
                  ''
                );
              }
            );

            // Hydrate target condition groups (resolve references, same pattern as condition_groups)
            // target_condition_groups allow rules to target variants defined by condition groups
            // instead of (or in addition to) explicit target_variants
            const targetConditionGroups = (rule.target_condition_groups || [])
              .map(function (ref) {
                var groupId = ref.id || ref;
                var group = conditionGroupMap.get(groupId);

                if (!group) {
                  isDev() &&
                    console.warn(
                      '⚠️ DATA PIPELINE: Target condition group ' +
                        groupId +
                        ' not found for rule "' +
                        rule.rule_name +
                        '"'
                    );
                  return null;
                }

                return group;
              })
              .filter(function (g) {
                return g !== null && g.enabled;
              }); // Only enabled groups

            return {
              id: rule.id,
              name: rule.rule_name,
              description: rule.rule_description,
              category: rule.rule_category || 'Uncategorized',
              type: 'unified',
              logicOperator: rule.logic_operator,
              conditionGroups: conditionGroups,
              targetVariants: new Set(targetVariants),
              targetConditionGroups: targetConditionGroups,
              targetAction: rule.target_action || 'DISABLE_VARIANTS',
              // conflict_direction: BIDIRECTIONAL (default), ONE_WAY, or MUTUAL_EXCLUSION
              // Controls whether reverse-direction checking is enabled for preemptive validation
              conflict_direction: rule.conflict_direction || null,
              priority: rule.priority || 100,
              enabled: rule.enabled !== false,
              notes: rule.notes || '',
            };
          })
          .sort(function (a, b) {
            return a.priority - b.priority;
          }); // Sort by priority (lower = first)

        // Convert to Map
        const rulesMap = new Map(
          unifiedRules.map(function (rule) {
            return [rule.id, rule];
          })
        );

        isDev() &&
          console.log('✅ DATA PIPELINE: Unified rules processed', {
            totalRules: rulesMap.size,
            totalConditionGroups: conditionGroupMap.size,
            categoriesFound: [
              ...new Set(
                unifiedRules.map(function (r) {
                  return r.category;
                })
              ),
            ],
            sampleRule: unifiedRules[0] || null,
          });

        return {
          conditionGroups: conditionGroupMap,
          rules: rulesMap,
        };
      } catch (error) {
        isDev() &&
          console.error(
            '❌ DATA PIPELINE: Unified rules loading failed',
            error
          );
        return { conditionGroups: new Map(), rules: new Map() };
      }
    },

    /* Helper Methods */

    updateApiLogs: function (
      configData,
      productData,
      woodMetaobjects,
      finishMetaobjects,
      forbiddenConditionGroups,
      forbiddenRulesUnified
    ) {
      isDev() &&
        console.log('🔍 DEBUG updateApiLogs received:', {
          forbiddenConditionGroups: Array.isArray(forbiddenConditionGroups)
            ? forbiddenConditionGroups.length + ' items'
            : typeof forbiddenConditionGroups,
          forbiddenRulesUnified: Array.isArray(forbiddenRulesUnified)
            ? forbiddenRulesUnified.length + ' items'
            : typeof forbiddenRulesUnified,
        });

      if (typeof window === 'undefined' || !window.location) {
        return;
      }

      const hostname = window.location.hostname;
      const isLocal =
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.local') ||
        hostname.includes('ngrok');

      if (!isLocal) {
        return;
      }

      isDev() &&
        console.log(
          '🔄 LOG UPLOAD: Starting log file uploads to log server...'
        );

      const logData = {
        configData: {
          filename: 'config-data.json',
          data: configData,
        },
        productData: {
          filename: 'product-data.json',
          data: productData,
        },
        woodMetaobjects: {
          filename: 'wood-metaobjects.json',
          data: woodMetaobjects,
        },
        finishMetaobjects: {
          filename: 'finish-metaobjects.json',
          data: finishMetaobjects,
        },
        // Unified forbidden combinations system
        forbiddenConditionGroups: {
          filename: 'forbidden-condition-groups.json',
          data: forbiddenConditionGroups || [],
        },
        forbiddenRulesUnified: {
          filename: 'forbidden-rules-unified.json',
          data: forbiddenRulesUnified || [],
        },
      };

      Object.entries(logData).forEach(([, { filename, data }]) => {
        try {
          const formattedData = JSON.stringify(data, null, 2);

          isDev() &&
            console.log(`📤 LOG UPLOAD: Attempting upload of ${filename}...`);

          // Try local server endpoint - if not running, silently skip (no browser download fallback)
          this.tryLocalServerUpload(formattedData, filename)
            .then((success) => {
              if (success) {
                isDev() &&
                  console.log(
                    `✅ LOG UPLOAD: Successfully uploaded ${filename}`
                  );
              } else {
                isDev() &&
                  console.warn(
                    `⚠️ LOG UPLOAD: Log server not running for ${filename} - run 'npm run dev:logs' to enable file logging`
                  );
              }
            })
            .catch((err) => {
              isDev() &&
                console.error(
                  `❌ LOG UPLOAD: Error uploading ${filename}:`,
                  err
                );
            });
        } catch (error) {
          isDev() &&
            console.error(
              `❌ LOG UPLOAD: Error processing ${filename}:`,
              error
            );
        }
      });
    },

    tryLocalServerUpload: async function (data, filename) {
      // 🚫 PRODUCTION SAFETY: Only upload on local development
      const isLocalDevelopment =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.port === '9292';

      if (!isLocalDevelopment) {
        return false; // Skip upload in staging/production
      }

      try {
        const response = await fetch('http://127.0.0.1:3001/save-log', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename: filename,
            data: data,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          isDev() &&
            console.error(
              `❌ LOG UPLOAD: Server returned ${response.status} for ${filename}:`,
              errorText
            );
          return false;
        }

        return true;
      } catch (error) {
        isDev() &&
          console.warn(
            `⚠️ LOG UPLOAD: Cannot reach log server for ${filename}:`,
            error.message
          );
        // Local server not running, that's fine
        return false;
      }
    },

    /**
     * 🔧 UTILITY: Recursive serializer for complex objects containing Maps and Sets
     *
     * This utility function makes PPD logging future-proof by automatically handling
     * any nested Maps, Sets, Arrays, and Objects without manual serialization code.
     *
     * Zen Precommit Recommendation: Replace brittle manual serialization with reusable utility
     * Circular Reference Protection: Tracks visited objects to prevent stack overflow
     *
     * @param {*} obj - Any value (Map, Set, Array, Object, or primitive)
     * @param {Set} seen - Internal parameter tracking visited objects (prevents circular refs)
     * @returns {*} Serialized value ready for JSON.stringify()
     */
    serializeComplexObject: function (obj, seen = new Set()) {
      // Primitives and null don't need circular reference checks
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }

      // Avoid serializing DOM elements
      if (obj.nodeType) {
        return '[DOM Element]';
      }

      // 🛡️ CIRCULAR REFERENCE PROTECTION: If we've seen this object before, mark it
      if (seen.has(obj)) {
        return '[Circular Reference]';
      }
      seen.add(obj);

      // Handle Map: convert to object with serialized values
      if (obj instanceof Map) {
        return Object.fromEntries(
          Array.from(obj.entries(), ([k, v]) => [
            k,
            this.serializeComplexObject(v, seen),
          ])
        );
      }

      // Handle Set: convert to array with serialized values
      if (obj instanceof Set) {
        return Array.from(obj.values()).map((v) =>
          this.serializeComplexObject(v, seen)
        );
      }

      // Handle Array: serialize each element
      if (Array.isArray(obj)) {
        return obj.map((v) => this.serializeComplexObject(v, seen));
      }

      // Handle plain Object
      const newObj = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          newObj[key] = this.serializeComplexObject(obj[key], seen);
        }
      }
      return newObj;
    },

    /**
     * 📊 DEV LOGGING: Log perfectProductDictionary snapshot for analysis
     *
     * Uses reusable serializeComplexObject() utility for future-proof PPD serialization.
     * Automatically handles any PPD structure changes without manual code updates.
     *
     * Zen Precommit Fix: Replaced 400+ lines of brittle manual serialization with utility function
     * @param {Object} ppd - The perfectProductDictionary to log
     */
    logPerfectProductDictionary: function (ppd) {
      // Check if we're in local development
      if (typeof window === 'undefined' || !window.location) {
        return;
      }

      const hostname = window.location.hostname;
      const isLocal =
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.local') ||
        hostname.includes('ngrok');

      if (!isLocal) {
        return;
      }

      try {
        // 🚀 FUTURE-PROOF SERIALIZATION: Use utility function (Zen Precommit Fix)
        const serializedPPD = this.serializeComplexObject(ppd);

        // Add analysis summary
        serializedPPD.analysis = {
          totalProducts: Object.keys(serializedPPD.products).length,
          totalCategories: Object.keys(serializedPPD.categories).length,
          totalMaterialRelationships: Object.keys(
            serializedPPD.materialRelationships
          ).length,
          totalTabs: Object.keys(serializedPPD.tabStructure).length,
          timestamp: new Date().toISOString(),
          pipelineProcessingTime: ppd.metadata?.processingTime || 0,
        };

        const formattedData = JSON.stringify(serializedPPD, null, 2);

        // Upload to log server
        this.tryLocalServerUpload(
          formattedData,
          'perfect-product-dictionary.json'
        )
          .then((success) => {
            if (success) {
              isDev() &&
                console.log(
                  `📊 PPD SNAPSHOT: Saved perfectProductDictionary (${formattedData.length} bytes) to logs/perfect-product-dictionary.json`
                );
            }
          })
          .catch((err) => {
            isDev() && console.error('❌ PPD SNAPSHOT: Error uploading:', err);
          });
      } catch (error) {
        isDev() &&
          console.warn('⚠️ Failed to log perfectProductDictionary:', error);
      }
    },

    // New method to group products by display_name
    groupProductsByDisplayName: function (products) {
      const grouped = new Map();
      if (!products) return grouped;

      products.forEach((product) => {
        if (product.display_name) {
          const groupName = product.display_name;
          if (!grouped.has(groupName)) {
            grouped.set(groupName, []);
          }
          grouped.get(groupName).push(product);
        }
      });

      return grouped;
    },

    /**
     * 🔧 CCP COMPLIANT: Get enhanced variants for conductor delegation
     * This method provides CCP-compliant access to enhanced variants via conductor delegation
     * Performance optimized: Reduces excessive logging through call deduplication
     *
     * @returns {Array} Array of enhanced variants or empty array if not available
     */
    getEnhancedVariants: function () {
      if (this.enhancedVariants && Array.isArray(this.enhancedVariants)) {
        // 🔥 PERFORMANCE FIX: Throttle excessive CCP delegation logging
        const now = performance.now();
        if (!this._lastLogTime || now - this._lastLogTime > 100) {
          isDev() &&
            console.log(
              `🎯 CCP DELEGATION: Providing ${this.enhancedVariants.length} enhanced variants via conductor delegation`
            );
          this._lastLogTime = now;
        }
        return this.enhancedVariants;
      } else {
        isDev() &&
          console.warn(
            '⚠️ CCP DELEGATION: No enhanced variants available in config module'
          );
        return [];
      }
    },

    /**
     * 🔧 CCP COMPLIANT: Get material relationships for conductor delegation
     * This method provides CCP-compliant access to material relationships via conductor delegation
     *
     * @returns {Map} Map of material relationships or empty Map if not available
     */
    getMaterialRelationships: function () {
      if (
        this.materialRelationships &&
        this.materialRelationships instanceof Map
      ) {
        isDev() &&
          console.log(
            `🎯 CCP DELEGATION: Providing ${this.materialRelationships.size} material relationships via conductor delegation`
          );
        return this.materialRelationships;
      } else {
        isDev() &&
          console.warn(
            '⚠️ CCP DELEGATION: No material relationships available in config module'
          );
        return new Map();
      }
    },

    /**
     * 🔧 CCP COMPLIANT: Get processed data for conductor delegation
     * This method provides limited access to processed data via conductor delegation
     * Note: Full processedData access is restricted to data processing pipeline modules only
     *
     * @returns {Object} Limited processed data object with summary information
     */
    getProcessedDataSummary: function () {
      if (this.processedData) {
        return {
          tabCount: this.processedData.tabStructure?.size || 0,
          categoryCount: this.processedData.categoryStructure?.size || 0,
          productCount:
            this.processedData.productStructure?.addOnProducts?.size || 0,
          materialRelationshipsCount:
            this.processedData.productStructure?.materialRelationships?.size ||
            0,
          enhancedVariantsCount:
            this.processedData.enhancedVariants?.length || 0,
          hasValidData: !!(
            this.processedData.tabStructure &&
            this.processedData.categoryStructure
          ),
        };
      } else {
        return {
          tabCount: 0,
          categoryCount: 0,
          productCount: 0,
          materialRelationshipsCount: 0,
          enhancedVariantsCount: 0,
          hasValidData: false,
        };
      }
    },

    // ============================================================================
    // DIMENSION EXTRACTION HELPERS (for Layer 1 pre-parsing)
    // ============================================================================

    /**
     * Parse dimension value to numeric (handles fractions like "8-1/2")
     * @param {string} value - Dimension value like "8-1/2", "32", "10.5"
     * @returns {number|null} Numeric value or null
     */
    _parseDimensionValue: function (value) {
      if (!value) return null;

      // Normalize space-separated fractions to dash-separated
      const normalizedValue =
        value.includes(' ') && value.includes('/')
          ? value.replace(' ', '-')
          : value;

      // Handle fractions like "8-1/2"
      if (normalizedValue.includes('-') && normalizedValue.includes('/')) {
        const parts = normalizedValue.split('-');
        const integerPart = parseInt(parts[0], 10);
        const fractionParts = parts[1].split('/');
        if (fractionParts.length === 2) {
          const numerator = parseInt(fractionParts[0], 10);
          const denominator = parseInt(fractionParts[1], 10);
          if (
            !isNaN(integerPart) &&
            !isNaN(numerator) &&
            !isNaN(denominator) &&
            denominator !== 0
          ) {
            return integerPart + numerator / denominator;
          }
        }
      }

      // Handle regular numbers and decimals
      const numericValue = parseFloat(value);
      return isNaN(numericValue) ? null : numericValue;
    },

    /**
     * Extract a standalone dimension from "X in." format (no dimension-type suffix).
     * Handles variants like "30 in. (Rough Opening 32 in.)" and "6-1/2 in. (Shelf Depth = 4-3/4 in.)"
     * where the dimension type is implied by the parent product's category, not the variant title.
     * @param {string} title - Variant title
     * @returns {number|null} Numeric dimension value or null
     */
    _extractStandaloneDimension: function (title) {
      if (!title) return null;
      // Match "X in." or "X in" at the start, where X can be "30", "6-1/2", "8.25" etc.
      const match = title.match(
        /^(\d+(?:[\s-]\d+\/\d+)?(?:\.\d+)?)\s*(?:in\.?|")\b/i
      );
      return match ? this._parseDimensionValue(match[1]) : null;
    },

    /**
     * Extract width from variant title (returns numeric value)
     * @param {string} title - Variant title
     * @returns {number|null} Numeric width value or null
     */
    _extractWidth: function (title) {
      if (!title) return null;
      const match = title.match(
        /(\d+(?:[\s-]\d+\/\d+)?(?:\.\d+)?)\"?\s*(?:w|wide|width)/i
      );
      return match ? this._parseDimensionValue(match[1]) : null;
    },

    /**
     * Extract height from variant title (returns numeric value)
     * @param {string} title - Variant title
     * @returns {number|null} Numeric height value or null
     */
    _extractHeight: function (title) {
      if (!title) return null;
      const match = title.match(
        /(\d+(?:[\s-]\d+\/\d+)?(?:\.\d+)?)\"?\s*(?:h|high|height|tall)/i
      );
      return match ? this._parseDimensionValue(match[1]) : null;
    },

    /**
     * Extract depth from variant title (returns numeric value)
     * @param {string} title - Variant title
     * @returns {number|null} Numeric depth value or null
     */
    _extractDepth: function (title) {
      if (!title) return null;
      const match = title.match(
        /(\d+(?:[\s-]\d+\/\d+)?(?:\.\d+)?)\"?\s*(?:d|deep|depth)/i
      );
      return match ? this._parseDimensionValue(match[1]) : null;
    },

    /**
     * Extract handling from variant title (lowercase input expected)
     * @param {string} titleLower - Lowercase variant title
     * @returns {string|null} 'in-swing', 'out-swing', 'left', 'right', or null
     */
    _extractHandling: function (titleLower) {
      if (!titleLower) return null;

      if (titleLower.includes('in-swing') || titleLower.includes('inswing')) {
        return 'in-swing';
      }
      if (titleLower.includes('out-swing') || titleLower.includes('outswing')) {
        return 'out-swing';
      }
      if (titleLower.includes('left')) {
        return 'left';
      }
      if (titleLower.includes('right')) {
        return 'right';
      }

      return null;
    },

    /**
     * Generate variant thumbnail HTML for a single variant.
     * Shared helper used by both Layer 5 (createPerfectProductDictionary) and rebuildHtmlCaches.
     *
     * @param {Object} variant - Variant data
     * @param {boolean} isComposite - Whether this is a composite product variant
     * @param {number|string} productId - Parent product ID
     * @param {Map} variantIdToVariantMap - PPD's variant lookup map
     * @param {string} statusIconHTML - Pre-built status icon HTML from DOM SVGs
     * @param {string|null} groupId - Composite group ID (optional)
     * @param {string|null} groupName - Composite display name (optional)
     * @returns {string} Rendered HTML string
     */
    _generateVariantThumbnailHTML(
      variant,
      isComposite,
      productId,
      variantIdToVariantMap,
      statusIconHTML,
      groupId = null,
      groupName = null
    ) {
      // NOTE: The CSS classes below (murphy-door-configurator__variant-item, __thumbnail-item) are
      // targeted by md-config-ui-updater.js for active state management. Changes require sync.
      const variantId =
        typeof variant.id === 'string' ? parseInt(variant.id) : variant.id;
      const enhancedVariant = variantIdToVariantMap.get(variantId);

      const isCompositeVariant =
        variant.isCompositeProductVariant || isComposite;
      const normalizedTitle = isCompositeVariant
        ? enhancedVariant?.displayFormatting?.compositeVariantDisplay ||
          enhancedVariant?.displayFormatting?.cleanVariantName ||
          enhancedVariant?.title ||
          variant.title
        : enhancedVariant?.displayFormatting?.variantBrowsingDisplay ||
          enhancedVariant?.displayFormatting?.cleanVariantName ||
          enhancedVariant?.title ||
          variant.title;

      // Phase 0 helper. Guarded because a tile builder must degrade to a plain
      // <img> rather than throw -- the catch around these builders swaps the whole
      // tile for an error thumbnail, and losing a tile over a missing srcset is a
      // far worse outcome than shipping one unresponsive image.
      const _thumbImgHtml =
        window.MurphyDoorConfigurator?.utils?.thumbnailImgHtml?.bind(
          window.MurphyDoorConfigurator.utils
        ) || null;

      const imageSrc =
        enhancedVariant?.normalizedImage?.src ||
        variant.normalizedImage?.src ||
        variant.image?.src ||
        variant.image ||
        variant.featured_image?.src ||
        variant.featured_image ||
        '/assets/placeholder.jpg';

      const imageAlt =
        enhancedVariant?.normalizedImage?.alt ||
        variant.normalizedImage?.alt ||
        variant.featured_image?.alt ||
        normalizedTitle;

      // PPD variant.price is a pre-formatted dollar string ("1,190.00"), not
      // cents. Whole dollars render as "$1,190"; real cents (the catalog has
      // add-on variants at "20.90", "92.50" ...) pass through untouched, so
      // the caption can never advertise a total the cart will not charge.
      // Guarded: a missing Phase 0 utility must not take the tile down with it.
      const _utils = window.MurphyDoorConfigurator.utils;
      const _rawVariantPrice = variant.price || '0.00';
      const variantPrice =
        _utils && _utils.dropWholeDollarCents
          ? _utils.dropWholeDollarCents(_rawVariantPrice)
          : _rawVariantPrice;
      // A literal "$0" next to sibling tiles carrying real dollar amounts
      // reads as "is this broken?" -- Door Handing (in/out-swing, left/right
      // hand) has zero price variance across ANY of its options, so every
      // tile hit this. Matches the "Included" convention already used by the
      // Summary sidebar and category-button caption (formatConfiguratorPrice's
      // zeroLabel) -- same rule, applied here because this builder makes its
      // own caption HTML rather than routing through that helper.
      const _numericVariantPrice =
        parseFloat(String(_rawVariantPrice).replace(/,/g, '')) || 0;
      // Wrapped in parens, not bare -- several live option names already
      // start with "No" (Knobs & Pulls, Cabinet Doors, Back Casing, Smart
      // Home Automations, Appearance Upgrades). A bare "No Knobs or Pulls
      // Included" reads as a run-on sentence, not a price label; the parens
      // do the same delimiting job "$" does for a real price.
      const priceDisplay =
        _numericVariantPrice === 0 ? '(Included)' : `$${variantPrice}`;

      const compositeDataAttrs =
        groupId && groupName
          ? `data-group-id="${groupId}" data-display-name="${groupName}"`
          : '';

      // P6 A11Y: option tiles are clickable <div>s; expose them as focusable
      // role="button" controls with a descriptive label (title + price) so
      // keyboard + screen-reader users can select an option. Keyboard
      // activation is handled by the delegated keydown funnel in the
      // event-handler (Enter/Space → the same click path a tap uses).
      // Escaped: door titles routinely contain `"` (e.g. 24"), which would
      // otherwise break out of the aria-label attribute.
      const tileAriaLabel = escapeHTML(`${normalizedTitle} ${priceDisplay}`);

      return `
        <div class="murphy-door-configurator__variant-item murphy-door-configurator__thumbnail-item"
             data-variant-id="${variant.id}"
             data-product-id="${productId || variant.productId || ''}"
             data-is-composite="${isComposite}"
             role="button"
             tabindex="0"
             aria-pressed="false"
             aria-label="${tileAriaLabel}"
             ${compositeDataAttrs}>
          <div class="murphy-door-configurator__thumbnail-image">
            ${_thumbImgHtml
              ? _thumbImgHtml({
                  src: imageSrc,
                  srcsetFrom: imageSrc,
                  alt: imageAlt,
                })
              : `<img src="${escapeHTML(imageSrc)}" alt="${escapeHTML(
                  imageAlt
                )}" width="100" height="100" loading="lazy">`}
            ${statusIconHTML}
          </div>
          <span class="murphy-door-configurator__thumbnail-caption">
            ${normalizedTitle} ${priceDisplay}
          </span>
        </div>
      `.trim();
    },

    /**
     * Parse a numeric-input product's real live variants into a sorted
     * decimalInches -> variant lookup, using the Phase 0 catalog-title parser
     * (Utils.parseFractionalInches) — never a synthesized/assumed arithmetic
     * range. Entries whose title doesn't parse (there is no such case in the
     * live Custom Jamb Depth catalog today, but a future non-numeric variant
     * added to an opted-in product must not crash PPD construction) are
     * silently excluded rather than corrupting the lookup with a null key.
     *
     * @param {Object} product - product with a `variants` array
     * @returns {Array<{decimalInches: number, variantId: (string|number), displayTitle: string, price: string}>}
     *   sorted ascending by decimalInches
     */
    _buildNumericInputLookup(product) {
      const utils = window.MurphyDoorConfigurator.utils;
      if (!utils || !utils.parseFractionalInches || !product.variants) {
        return [];
      }
      const entries = [];
      product.variants.forEach((variant) => {
        const displayTitle = variant.title || '';
        const decimalInches = utils.parseFractionalInches(displayTitle);
        if (decimalInches === null) return;
        const rawPrice = variant.price || '0.00';
        const price = utils.dropWholeDollarCents
          ? utils.dropWholeDollarCents(rawPrice)
          : rawPrice;
        entries.push({
          decimalInches,
          variantId: variant.id,
          displayTitle,
          price,
        });
      });
      entries.sort((a, b) => a.decimalInches - b.decimalInches);
      return entries;
    },

    /**
     * Build the inches/fraction stepper card that REPLACES a variant-tile
     * grid for a NUMERIC_INPUT_PRODUCTS-listed product. Stored in the exact
     * same productToVariantLayoutHTMLMap the tile grid would have occupied,
     * under the same product id, so md-config-ui-updater.js's
     * generateVariantLayout() needs no changes to render either shape.
     *
     * Two independent steppers (Inches / Fraction) replace the old freeform
     * text field -- md-config-ui-numeric-input-selector.js drives both
     * entirely from `product.numericInputLookup`, so every value the arrows
     * can land on is guaranteed to already be a real catalog variant. Both
     * `<button>`s start `disabled` here (the pre-JS/pre-forbidden-state
     * default); the Phase 2 module enables the correct pair the moment it
     * wires the card.
     *
     * 🚨 CRITICAL — includes ONE inert placeholder element carrying the exact
     * classes `murphy-door-configurator__variant-item
     * murphy-door-configurator__thumbnail-item` from FIRST PAINT.
     * md-config-ui-updater.js's showVariantLayout() synchronously requires a
     * `.murphy-door-configurator__thumbnail-item` to exist immediately after
     * this HTML is inserted, or it rolls back the whole category as broken
     * for the rest of the session (apo_style). The Phase 2 numeric-input
     * selector module POPULATES this SAME element in place once a stepper
     * value resolves (sets data-variant-id/data-product-id, removes the
     * `--placeholder` hiding class, fills in image/caption) and calls
     * Utils.simulateClick() on it — it does not create a second node.
     *
     * @param {Object} product
     * @param {string|number} productId
     * @param {Array} lookup - result of _buildNumericInputLookup(product)
     * @param {string} statusIconHTML - shared save/check icon markup
     * @returns {string} HTML fragment (same shape contract as a variant grid)
     */
    _generateNumericInputCardHTML(product, productId, lookup, statusIconHTML) {
      const utils = window.MurphyDoorConfigurator.utils;
      // 🚨 MUST BIND TO utils. formatInches reads `this.decimalInchesToFraction`
      // internally, so extracting it as a bare reference and calling fmt(...)
      // detaches it from that receiver -- `this` is undefined in strict mode,
      // and every call throws "Cannot read properties of undefined (reading
      // 'decimalInchesToFraction')". That crash lands here, inside PPD
      // construction, so it doesn't just break this card -- it fails the whole
      // perfectProductDictionary build for every product with a numeric-input
      // category (Jamb Depth's Custom option), taking the entire configurator
      // bootstrap down with it.
      const fmt =
        utils && utils.formatInches ? utils.formatInches.bind(utils) : () => null;
      const minLabel =
        lookup.length > 0 ? fmt(lookup[0].decimalInches) : null;
      const maxLabel =
        lookup.length > 0 ? fmt(lookup[lookup.length - 1].decimalInches) : null;
      const rangeText =
        minLabel && maxLabel
          ? `Accepts measurements from ${minLabel} to ${maxLabel}`
          : 'Adjust the arrows to set your measurement';
      const baseId = `md-numeric-input-${escapeHTML(String(productId))}`;
      const hintId = `${baseId}-hint`;
      const messageId = `${baseId}-message`;
      // Same parenthetical-stripping as Layer 4's selectionDisplayDataMap
      // (line ~4803) -- raw product.title carries suffixes like
      // "(Single/French Door)" that duplicate context already shown by the
      // category heading and don't belong in the card's own label.
      const productTitle = escapeHTML(
        (product.display_name || product.title || 'this option')
          .replace(/\s*\([^)]*\)\s*/g, '')
          .trim()
      );

      // Every variant of a numeric-input product shares one representative
      // image (confirmed live for Custom Jamb Depth: the product AND every
      // variant carry the identical thumbnail) -- baking the first variant's
      // image into the placeholder now means the Phase 2 module only has to
      // populate data-variant-id/caption/a11y state at commit time, not fetch
      // image data at runtime.
      const firstVariant = (product.variants && product.variants[0]) || null;
      const sharedImageSrc = escapeHTML(
        (firstVariant &&
          (firstVariant.normalizedImage?.src ||
            firstVariant.image?.src ||
            firstVariant.image ||
            firstVariant.featured_image?.src ||
            firstVariant.featured_image)) ||
          '/assets/placeholder.jpg'
      );

      return `
        <div class="murphy-door-configurator__numeric-input-card" data-product-id="${escapeHTML(
          String(productId)
        )}">
          <p class="murphy-door-configurator__numeric-input-label">
            Enter your ${productTitle}
          </p>
          <p class="murphy-door-configurator__numeric-input-hint" id="${hintId}">
            ${escapeHTML(rangeText)}
          </p>
          <div class="murphy-door-configurator__numeric-input-steppers">
            <div class="murphy-door-configurator__numeric-stepper">
              <span class="murphy-door-configurator__numeric-stepper-label">Inches</span>
              <div class="murphy-door-configurator__numeric-stepper-control">
                <button type="button" class="murphy-door-configurator__numeric-stepper-btn murphy-door-configurator__numeric-stepper-btn--down" data-stepper-action="inches-down" aria-label="Decrease inches" disabled>&minus;</button>
                <span
                  class="murphy-door-configurator__numeric-stepper-value"
                  data-stepper-value="inches"
                  role="spinbutton"
                  tabindex="0"
                  aria-label="Inches"
                  aria-describedby="${hintId} ${messageId}"
                >&mdash;</span>
                <button type="button" class="murphy-door-configurator__numeric-stepper-btn murphy-door-configurator__numeric-stepper-btn--up" data-stepper-action="inches-up" aria-label="Increase inches" disabled>&plus;</button>
              </div>
            </div>
            <div class="murphy-door-configurator__numeric-stepper">
              <span class="murphy-door-configurator__numeric-stepper-label">Fraction</span>
              <div class="murphy-door-configurator__numeric-stepper-control">
                <button type="button" class="murphy-door-configurator__numeric-stepper-btn murphy-door-configurator__numeric-stepper-btn--down" data-stepper-action="fraction-down" aria-label="Decrease fraction" disabled>&minus;</button>
                <span
                  class="murphy-door-configurator__numeric-stepper-value"
                  data-stepper-value="fraction"
                  role="spinbutton"
                  tabindex="0"
                  aria-label="Fraction"
                  aria-describedby="${hintId} ${messageId}"
                >&mdash;</span>
                <button type="button" class="murphy-door-configurator__numeric-stepper-btn murphy-door-configurator__numeric-stepper-btn--up" data-stepper-action="fraction-up" aria-label="Increase fraction" disabled>&plus;</button>
              </div>
            </div>
          </div>
          <p class="murphy-door-configurator__numeric-input-message" id="${messageId}" aria-live="polite"></p>
          <div class="murphy-door-configurator__variant-item murphy-door-configurator__thumbnail-item murphy-door-configurator__numeric-input-placeholder-tile"
               data-variant-id=""
               data-product-id=""
               data-is-composite="false"
               aria-hidden="true"
               tabindex="-1">
            <div class="murphy-door-configurator__thumbnail-image">
              <img src="${sharedImageSrc}" alt="" width="100" height="100" loading="lazy">
              ${statusIconHTML || ''}
            </div>
            <span class="murphy-door-configurator__thumbnail-caption"></span>
          </div>
        </div>
      `.trim();
    },

    /**
     * Rebuild HTML cache maps stripped from a lean CDN PPD.
     * Called when __ppdMeta.htmlMapsStripped is true after CDN fetch.
     * Rebuilds: productToDirectVariantLayoutHTMLMap, productToVariantLayoutHTMLMap,
     * compositeProductToVariantGridHTMLMap using data already in the PPD.
     *
     * @param {Object} ppd - Lean PPD with empty HTML cache Maps
     * @returns {Object} PPD with rebuilt HTML cache Maps (mutated in place)
     */
    rebuildHtmlCaches(ppd) {
      // NOTE: This function's logic for building the three HTML cache maps mirrors the
      // original implementation in Layer 5 of createPerfectProductDictionary (around line 3506).
      // Any changes to map structures or variant data must be synchronized between both locations.
      const startTime = performance.now();
      isDev() &&
        console.log('🔧 PHASE 2B: Rebuilding HTML caches from lean CDN PPD...');

      // Validate all required data structures exist before rebuilding
      const {
        variantIdToVariantMap,
        products,
        categoryDisplayModeMap,
        compositeGroupIdToProductMap,
      } = ppd;
      if (!variantIdToVariantMap || !products) {
        isDev() &&
          console.error(
            '❌ PHASE 2B: Lean PPD is missing required data maps for HTML cache rebuild'
          );
        throw new Error(
          'Lean PPD is malformed — missing variantIdToVariantMap or products'
        );
      }

      // Read SVG templates from DOM (available client-side, NOT available server-side)
      const saveIconSvgHTML =
        document
          .getElementById('murphy-door-save-icon-svg')
          ?.querySelector('svg')?.outerHTML || '';
      const circleCheckSvgHTML =
        document
          .getElementById('murphy-door-circle-check-svg')
          ?.querySelector('svg')?.outerHTML || '';

      const statusIconHTML =
        saveIconSvgHTML && circleCheckSvgHTML
          ? `<div class="murphy-door-configurator__thumbnail-status-icon" aria-hidden="true">
            <span class="thumbnail-save-button">${saveIconSvgHTML}</span>
            <span class="thumbnail-circle-check">${circleCheckSvgHTML}</span>
          </div>`
          : '';

      // 1. Rebuild productToDirectVariantLayoutHTMLMap
      const productToDirectVariantLayoutHTMLMap = new Map();
      if (ppd.categoryDisplayModeMap) {
        ppd.categoryDisplayModeMap.forEach((displayMode) => {
          if (
            displayMode.hasDirectVariant &&
            displayMode.directVariantProductId
          ) {
            const product = ppd.products.get(
              displayMode.directVariantProductId
            );
            if (product && product.variants && product.variants.length > 0) {
              const isComposite = product.type === 'CompositeProduct';
              const variantGridHTML = product.variants
                .map((v) =>
                  this._generateVariantThumbnailHTML(
                    v,
                    isComposite,
                    product.id,
                    variantIdToVariantMap,
                    statusIconHTML
                  )
                )
                .join('');

              const layoutHTML = `
                <div class="murphy-door-configurator__variant-view murphy-door-configurator__variant-view--direct"
                     data-variant-selection="true"
                     data-product-type="${isComposite ? 'composite' : 'regular'}"
                     style="opacity: 1;">
                  <div class="murphy-door-configurator__variants-grid">
                    ${variantGridHTML}
                  </div>
                </div>
              `.trim();

              productToDirectVariantLayoutHTMLMap.set(product.id, layoutHTML);
            }
          }
        });
      }
      Object.freeze(productToDirectVariantLayoutHTMLMap);
      ppd.productToDirectVariantLayoutHTMLMap =
        productToDirectVariantLayoutHTMLMap;

      // 2. Rebuild productToVariantLayoutHTMLMap
      // 🚨 This is the SECOND live builder of this map (the first is Layer 5
      // in createPerfectProductDictionary, above) — a customer served a lean
      // CDN PPD (__ppdMeta.htmlMapsStripped) rebuilds through THIS path
      // instead. The NUMERIC_INPUT_PRODUCTS branch below must stay identical
      // to Layer 5's, or such a customer silently gets the old tile-grid HTML
      // back for an opted-in product.
      const productToVariantLayoutHTMLMap = new Map();
      ppd.products.forEach((product, productId) => {
        if (
          product.variants &&
          product.variants.length > 1 &&
          product.type !== 'single-variant'
        ) {
          const isComposite = product.type === 'CompositeProduct';
          const usesNumericInputUI =
            window.MurphyDoorConfigurator.CONSTANTS.NUMERIC_INPUT_PRODUCTS.has(
              product.title
            );

          let variantGridHTML;
          if (usesNumericInputUI) {
            product.numericInputLookup = this._buildNumericInputLookup(
              product,
              variantIdToVariantMap
            );
            variantGridHTML = this._generateNumericInputCardHTML(
              product,
              productId,
              product.numericInputLookup,
              statusIconHTML
            );
          } else {
            variantGridHTML = product.variants
              .map((variant) =>
                this._generateVariantThumbnailHTML(
                  variant,
                  isComposite,
                  productId,
                  variantIdToVariantMap,
                  statusIconHTML
                )
              )
              .join('');
          }
          productToVariantLayoutHTMLMap.set(productId, variantGridHTML);
        }
      });
      Object.freeze(productToVariantLayoutHTMLMap);
      ppd.productToVariantLayoutHTMLMap = productToVariantLayoutHTMLMap;

      // 3. Rebuild compositeProductToVariantGridHTMLMap
      const compositeProductToVariantGridHTMLMap = new Map();
      if (ppd.compositeGroupIdToProductMap) {
        ppd.compositeGroupIdToProductMap.forEach(
          (compositeProduct, groupId) => {
            if (
              compositeProduct.variants &&
              compositeProduct.variants.length > 0
            ) {
              const groupName = compositeProduct.display_name;
              const variantGridHTML = compositeProduct.variants
                .map((variant) =>
                  this._generateVariantThumbnailHTML(
                    variant,
                    false,
                    compositeProduct.id,
                    variantIdToVariantMap,
                    statusIconHTML,
                    groupId,
                    groupName
                  )
                )
                .join('');
              compositeProductToVariantGridHTMLMap.set(
                groupId,
                variantGridHTML
              );
            }
          }
        );
      }
      Object.freeze(compositeProductToVariantGridHTMLMap);
      ppd.compositeProductToVariantGridHTMLMap =
        compositeProductToVariantGridHTMLMap;

      // Remove lean PPD metadata flag
      delete ppd.__ppdMeta;

      const elapsed = (performance.now() - startTime).toFixed(1);
      isDev() &&
        console.log(
          `✅ PHASE 2B: HTML caches rebuilt in ${elapsed}ms - ` +
            `${productToDirectVariantLayoutHTMLMap.size} direct layouts, ` +
            `${productToVariantLayoutHTMLMap.size} variant grids, ` +
            `${compositeProductToVariantGridHTMLMap.size} composite grids`
        );

      return ppd;
    },
  };

  // Add the dataPipeline object to the global namespace
  window.MurphyDoorConfigurator.dataPipeline = config;

  // Also register as pipelineProcessor for conductor compatibility
  window.MurphyDoorConfigurator.pipelineProcessor = config;

  // Console mirroring to logs/console.log is md-config-support-console-capture.js's job alone.
  // A second capture here appended untagged, uncapped JSON every second, so the file never
  // reset on page load and reached 50 GB (2026-10-02).

  // Register the module with the central conductor if available
  if (window.MurphyDoorConfigurator.Main) {
    window.MurphyDoorConfigurator.Main.registerModule('DataPipeline', config);
  }

  // Initialize sectionId validation with retry logic
  // 🏗️ BOOTSTRAP FIX: The DOM element may not be parsed yet when this script loads.
  // Use lazy getter pattern + retry logic to handle race conditions gracefully.
  (function validateSectionId() {
    let retryCount = 0;
    const maxRetries = 5;
    const retryDelay = 100; // Start with 100ms

    function checkSectionId() {
      try {
        const sectionId = window.MurphyDoorConfigurator.dataPipeline.sectionId;

        if (sectionId) {
          // Success! SectionId is available
          isDev() &&
            console.log(
              `[DataPipeline] ✅ SectionId initialized successfully: ${sectionId}`
            );
          return true;
        }

        // SectionId not available yet
        if (retryCount < maxRetries) {
          retryCount++;
          const backoffDelay = retryDelay * Math.pow(2, retryCount - 1); // Exponential backoff
          isDev() &&
            console.warn(
              `[DataPipeline] ⏳ SectionId not ready yet, retry ${retryCount}/${maxRetries} in ${backoffDelay}ms...`
            );

          setTimeout(checkSectionId, backoffDelay);
          return false;
        }

        // Max retries exceeded - log warning but don't fail initialization
        isDev() &&
          console.warn(
            '[DataPipeline] ⚠️ SectionId not available after ' +
              maxRetries +
              ' retries. ' +
              'This may cause issues with media gallery. The configurator will attempt to continue.'
          );
        return false;
      } catch (error) {
        isDev() &&
          console.error(
            '[DataPipeline] ❌ Error during sectionId validation:',
            error
          );
        // Don't retry on error, but don't crash either
        return false;
      }
    }

    // Start the validation check
    checkSectionId();
  })();

  // 🔄 Subscribe to material preference changes for score invalidation
  if (window.MurphyDoorConfigurator?.coreEventBus) {
    // 🏗️ BOOTSTRAP TIMING FIX: Rebuild scores after state manager initializes
    // During Phase 1 bootstrap, stateManager doesn't exist yet, so Layer 4 scores are all 0
    // Once state initializes, rebuild scores with actual material preferences
    window.MurphyDoorConfigurator.CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_MAPS_INITIALIZED,
      () => {
        isDev() &&
          console.log(
            '🏗️ State manager initialized - rebuilding composite product scores with material preferences'
          );

        const stateManager = window.MurphyDoorConfigurator.stateManager;
        if (!stateManager || typeof stateManager.getState !== 'function') {
          isDev() &&
            console.error(
              'CRITICAL BOOTSTRAP ERROR: system:maps_initialized fired but stateManager is not ready. Cannot rebuild scores.'
            );
          return;
        }

        const currentState = stateManager.getState();
        const materialPreferences = currentState?.materialPreferences;

        if (materialPreferences) {
          config.rebuildCompositeProductScores(materialPreferences);
        } else {
          // No preferences yet - use default empty preferences
          config.rebuildCompositeProductScores({
            woodApplied: false,
            finishApplied: false,
            appliedWoodGid: null,
            appliedFinishGid: null,
          });
        }
      }
    );

    window.MurphyDoorConfigurator.CoreEventBus.subscribe(
      CONSTANTS.EVENTS.STATE_MATERIAL_PREFERENCES_UPDATED,
      (event) => {
        isDev() &&
          console.log(
            '🔄 Material preferences updated - triggering composite score rebuild'
          );
        config.rebuildCompositeProductScores(event.data.materialPreferences);
      }
    );

    window.MurphyDoorConfigurator.CoreEventBus.subscribe(
      CONSTANTS.EVENTS.STATE_MATERIAL_PREFERENCES_CLEARED,
      () => {
        isDev() &&
          console.log(
            '🔄 Material preferences cleared - triggering composite score rebuild'
          );
        config.rebuildCompositeProductScores({
          woodApplied: false,
          finishApplied: false,
          appliedWoodGid: null,
          appliedFinishGid: null,
        });
      }
    );
  }
})(window);
